import { STORAGE_KEY, store } from './lib/store.js';
import { localDateKey, addDays, diasEntre, listaDias } from './lib/dates.js';
import { uid } from './lib/uid.js';
import {
  fmtBRL, fmtNum, fmtPct, fmtDate,
  fmtCompact, fmtCompactBRL, fmtUSD, fmtCompactUSD,
  deltaPct, deltaClass, fmtDelta, deltaMini
} from './lib/format.js';
import { campaignMetrics } from './lib/metrics.js';
import { statusPillClass } from './lib/status.js';
import { escapeHtml } from './lib/html.js';
import { parseCSV } from './lib/csv.js';
import { parseMoney } from './lib/money.js';
import { tempoRelativo, dataHora } from './lib/time.js';
import { niceStep } from './lib/charts-math.js';
import { loadXLSX, parseWorkbookToTable } from './lib/xlsx.js';
import { paginate, pageNumbers, LIST_PAGE_SIZE, REPORT_PAGE_SIZE } from './lib/pagination.js';
import { buildHref, parseRoute, hrefMatches, isPlainClick, loginHref, AUTH_SCREENS } from './lib/router.js';
import { clearSession, getSession, initialsOf, isAdmin, restoreSession, signOut } from './lib/auth.js';
import { esquecerAparelho, prepararApp } from './lib/app-bridge.js';
import {
  aplicarModoApp, bindAppMode, cabecalhoApp, marcarAbaApp, modoApp,
  mostrarOnboarding, onboardingPendente, renderAppHome, renderAppPerfil,
} from './ui/app-mode.js';
import { deleteAdmin, inviteAdmin, sendAdminPassword, updateAdmin } from './lib/admins.js';
import { deleteShopifyLoja, normalizeShopDomain, saveShopifyLoja, shopifyInstallStart, syncShopifyLoja } from './lib/shopify.js';
import { completeFacebookOAuth, syncFacebookBm, syncFacebookCampaigns, updateFacebookCampaign } from './lib/facebook.js';
import { cotacaoLabel, fmtValor, moedaBase, paraBase, paraBrl, paraOrigem, setMoedaBase, setTaxaUsdBrl, simboloMoeda, taxaUsdBrl } from './lib/moeda.js';
import { apiRequest } from './lib/api.js';
import { showToast } from './ui/toast.js';
import { bindConfirm, cancelarConfirmacao, confirmarApagar, isConfirmOpen } from './ui/confirm.js';
import { getCachedAdmin, refreshAdmins, renderAdmins } from './ui/admin.js';
import { clearLojaSyncPending, getCachedLoja, isLojaSyncBusy, markLojaSyncPending, refreshIntegracoes, renderIntegracoes } from './ui/integracoes.js';
import { invalidateVendas, refreshVendas, renderVendas, resumoVendasPeriodo, setVendasFilter } from './ui/vendas.js';
import { invalidateProdutos, renderProdutos, setProdutosFilter } from './ui/produtos.js';
import { aplicarRotaTracker, bindTracker, renderTracker, trackerRouteId } from './ui/tracker.js';
import { bindNotificacoes, renderNotificacoes } from './ui/notificacoes.js';
import { invalidateFunil, lerFunil, renderFunil, setFunilFilter } from './ui/funil.js';
import { bindLojaWizard, closeLojaWizard, isLojaWizardOpen, openLojaWizard } from './ui/loja-wizard.js';
import { bindBmWizard, closeBmWizard, isBmWizardOpen, openBmPicker, openBmWizard } from './ui/bm-wizard.js';
import { clearBmSyncPending, isBmSyncBusy, markBmSyncPending, renderBms as renderBmsView } from './ui/bms.js';
import { bindTheme } from './ui/theme.js';
import { renderPager } from './ui/pager.js';
import { bindLogin, showLoginScreen, hideLoginScreen, showBootScreen, consumeLoginNext, loadResetToken } from './ui/login.js';
import { SECTIONS, NAV_GROUPS, PERIODOS } from './data/sections.js';
import { ICONS, HERO_ICONS, G2_ICONS } from './data/icons.js';
import { FIELD_SCHEMAS, TYPE_LABELS } from './data/schemas.js';
import { INFO_PAGES } from './data/info-pages.js';
import N8N_FLUXO from '../../n8n/n8n-torre-meta.json';

/* =========================================================
   ESTADO E PERSISTÊNCIA
   ========================================================= */

let state = { bms: [], contas: [], campanhas: [], rejeitados: [], importLog: [], history: [], hourly: {}, meta: { valorObjetivo: 0 }, saude: { metaLojas: 5, metaContas: 8 }, faturamento: { deducaoPct: 0 }, conjuntos: [], anuncios: [], sincronizadoEm: null, integracao: { ativo:false, url:'', chave:'' }, alteracoes: [] };
let currentSection = 'overview';
let appPerfil = false;
const listPage = { lojas: 1, vendas: 1, produtos: 1, bms: 1, contas: 1, rejeitados: 1, relatorios: 1, admin: 1 };
const listFilter = { contas: 'todas', rejeitados: 'todas' };
let faturamentoView = 'liquido'; // 'liquido' | 'bruto' — alterna o card de faturamento
// período da Visão geral: { tipo:'hoje'|'ontem'|'7d'|'30d'|'90d'|'custom', de, ate }
let periodo = { tipo:'hoje' };
try{
  const salvo = JSON.parse(localStorage.getItem('torre:periodo') || 'null');
  if(salvo && ['hoje','ontem','7d','30d','90d','custom'].includes(salvo.tipo)) periodo = salvo;
}catch(e){}
function salvarPeriodo(){ try{ localStorage.setItem('torre:periodo', JSON.stringify(periodo)); }catch(e){} }
let ocultarValores = false; // botão do olho: esconde os valores em R$
try{ ocultarValores = localStorage.getItem('torre:ocultar') === '1'; }catch(e){}
let fatChart = null; // dados do gráfico por hora, usados no desenho e no tooltip

let editing = null; // { type, id } | null
let pendingImport = null; // { tipo, headers, rows, fileName } | null

async function loadState(){
  try{
    const raw = await store.get(STORAGE_KEY);
    if(raw){
      state = JSON.parse(raw);
      if(!state.importLog) state.importLog = [];
      if(!state.history) state.history = [];
      if(!state.meta) state.meta = { valorObjetivo: 100000 };
      if(!state.hourly) state.hourly = {};
      // versão anterior guardava chargeback/reembolso/taxas na saúde
      const antiga = state.saude || {};
      if(!state.faturamento){
        state.faturamento = { deducaoPct: (Number(antiga.reembolsoPct)||0) + (Number(antiga.taxaPct)||0) };
      }
      state.saude = { metaLojas: Number(antiga.metaLojas) || 5, metaContas: Number(antiga.metaContas) || 8 };
      if(!Array.isArray(state.conjuntos)) state.conjuntos = [];
      if(!Array.isArray(state.anuncios)) state.anuncios = [];
      if(!state.integracao) state.integracao = { ativo:false, url:'', chave:'' };
      if(!state.moeda || (state.moeda.base !== 'USD' && state.moeda.base !== 'BRL')) state.moeda = { base:'BRL' };
      setMoedaBase(state.moeda.base);
      if(!Array.isArray(state.alteracoes)) state.alteracoes = [];
      [...state.campanhas, ...state.conjuntos, ...state.anuncios].forEach(it=>{ if(it.sync && it.sync.estado === 'enviando') delete it.sync; });
      // campanhas de exemplo antigas ganham produto, orçamento diário e histórico
      const DEMO_CAMP = {
        'Aurora — Coleção Inverno':['Casacos',300], 'Aurora — Retargeting':['Casacos',100],
        'Vitalis — Leads Odontologia':['Implantes',150], 'Vitalis — Google Pesquisa':['Implantes',80],
        'Nórdica — Sala de estar':['Sofás',250], 'Nórdica — Quarto (teste)':['Camas',120]
      };
      state.campanhas.forEach(c=>{
        const d = DEMO_CAMP[c.nome];
        if(d && !c.orcamentoTipo){ c.produto = c.produto || d[0]; c.orcamento = d[1]; c.orcamentoTipo = 'Diário'; }
        if(d && !Array.isArray(c.hist)) gerarHistItem(c, 95);
        if(d && !c.ult) c.ult = { em: new Date(Date.now() - 1000*60*(20 + Math.floor(Math.random()*400))).toISOString(), gasto:Number(c.gasto)||0, receita:Number(c.receita)||0, vendas:Number(c.conversoes)||0 };
      });
      if(!state.sincronizadoEm) state.sincronizadoEm = new Date(Date.now() - 1000*60*12).toISOString();
      // remove números de funil que eram só exemplo em versões anteriores
      if(!state.migFunilDemo){
        const DEMO = new Set(["3181-2151-1223", "9840-6710-905", "861-582-331", "2140-1620-236", "608-411-234", "3120-1870-120", "1427-965-549", "5200-3380-452", "549-371-211", "1900-1190-170", "491-332-189", "1870-1310-118"]);
        state.campanhas.forEach(c=>{
          if(DEMO.has(`${c.cliques}-${c.visualizacoes}-${c.checkouts}`)){ c.cliques = 0; c.visualizacoes = 0; c.checkouts = 0; }
        });
        state.migFunilDemo = true;
      }
      if(!state.migBmsFake){
        const DEMO_BMS = new Set(['BM Principal — Agência', 'BM Google Ads Clientes']);
        state.bms = (state.bms || []).filter((b) => b.facebookId || !DEMO_BMS.has(b.nome));
        state.migBmsFake = true;
        await saveState({ snapshot:false });
      }
      if(!state.migDemoPainel && (state.bms || []).some((b) => b.facebookId)){
        const DEMO_CONTAS = new Set([
          'Loja Aurora — Conta 01',
          'Clínica Vitalis',
          'Vitalis — Google',
          'Casa Nórdica — Conta 01',
          'Casa Nórdica — Conta 02',
        ]);
        const DEMO_CAMPANHAS = new Set([
          'Aurora — Coleção Inverno',
          'Aurora — Retargeting',
          'Vitalis — Leads Odontologia',
          'Nórdica — Sala de estar',
          'Nórdica — Quarto (teste)',
          'Vitalis — Google Pesquisa',
        ]);
        const DEMO_REJEITADOS = new Set([
          'Aurora — Vídeo promo 40% OFF',
          'Vitalis — Carrossel implantes',
        ]);
        const contasDemo = new Set(
          (state.contas || [])
            .filter((c) => DEMO_CONTAS.has(c.nome) && !c.facebookAccountId && !c.idMeta)
            .map((c) => c.id),
        );
        const tinhaDemo = contasDemo.size
          || (state.campanhas || []).some((c) => DEMO_CAMPANHAS.has(c.nome))
          || (state.rejeitados || []).some((r) => DEMO_REJEITADOS.has(r.nomeAnuncio));
        state.contas = (state.contas || []).filter((c) => !contasDemo.has(c.id));
        state.campanhas = (state.campanhas || []).filter((c) => !DEMO_CAMPANHAS.has(c.nome) && !contasDemo.has(c.contaId));
        state.rejeitados = (state.rejeitados || []).filter((r) => !DEMO_REJEITADOS.has(r.nomeAnuncio) && !contasDemo.has(r.contaId));
        if(tinhaDemo){
          state.history = [];
          state.hourly = {};
          state.sincronizadoEm = null;
        }
        state.migDemoPainel = true;
        await saveState({ snapshot:false });
      }
      if(limparOrfaosBm()) await saveState({ snapshot:false });
      if(!state.migOverviewSeries){
        if(repairPanelSeries()) await saveState({ snapshot:false });
        state.migOverviewSeries = true;
        await saveState({ snapshot:false });
      }
      return;
    }
    throw new Error('vazio');
  }catch(e){
    if (modoApp()) {
      state = {
        bms: [], contas: [], campanhas: [], rejeitados: [], importLog: [], history: [],
        meta: { valorObjetivo: 0 }, saude: { metaLojas: 5, metaContas: 8 },
        faturamento: { deducaoPct: 0 }, hourly: {}, conjuntos: [], anuncios: [],
        integracao: { ativo: false, url: '', chave: '' }, alteracoes: [], moeda: { base: 'BRL' },
        _offline: Boolean(e?.offline) || (typeof navigator !== 'undefined' && !navigator.onLine),
      };
      setMoedaBase('BRL');
      return;
    }
    state = seedData();
    await saveState({ snapshot:false });
  }
}

function limparOrfaosBm(){
  const bmIds = new Set((state.bms || []).map((b) => String(b.id)));
  const contasVivas = new Set((state.contas || []).map((c) => String(c.id)));
  const contas = new Set(
    (state.contas || []).filter((c) => c.bmId && !bmIds.has(String(c.bmId))).map((c) => String(c.id)),
  );
  const camps = new Set(
    (state.campanhas || []).filter((c) => {
      if(contas.has(String(c.contaId))) return true;
      const facebook = c.facebookCampaignId || c.idMeta;
      return Boolean(facebook) && c.contaId && !contasVivas.has(String(c.contaId));
    }).map((c) => String(c.id)),
  );
  if(!contas.size && !camps.size) return false;
  state.contas = state.contas.filter((c) => !contas.has(String(c.id)));
  state.campanhas = (state.campanhas || []).filter((c) => !camps.has(String(c.id)));
  state.conjuntos = (state.conjuntos || []).filter((c) => !camps.has(String(c.campanhaId)));
  state.anuncios = (state.anuncios || []).filter((a) => !camps.has(String(a.campanhaId)));
  const contasFinais = new Set([...contasVivas].filter((id) => !contas.has(id)));
  state.rejeitados = (state.rejeitados || []).filter((r) => !r.contaId || contasFinais.has(String(r.contaId)));
  return true;
}

function removerBm(id){
  const bmIds = new Set([String(id)]);
  const contas = new Set(
    (state.contas || []).filter((c) => bmIds.has(String(c.bmId))).map((c) => String(c.id)),
  );
  const camps = new Set(
    (state.campanhas || []).filter((c) => contas.has(String(c.contaId))).map((c) => String(c.id)),
  );
  state.bms = (state.bms || []).filter((b) => !bmIds.has(String(b.id)));
  state.contas = (state.contas || []).filter((c) => !contas.has(String(c.id)));
  state.campanhas = (state.campanhas || []).filter((c) => !camps.has(String(c.id)));
  state.conjuntos = (state.conjuntos || []).filter((c) => !camps.has(String(c.campanhaId)));
  state.anuncios = (state.anuncios || []).filter((a) => !camps.has(String(a.campanhaId)));
  state.rejeitados = (state.rejeitados || []).filter((r) => !contas.has(String(r.contaId)));
}

function moedaCampanha(c){
  const conta = state.contas.find((item)=> item.id === c?.contaId);
  return String(c?.currency || conta?.currency || 'BRL').toUpperCase();
}
function moedaConta(c){
  return String(c?.currency || 'BRL').toUpperCase();
}
function currentTotals(){
  return {
    gasto: state.campanhas.reduce((s,c)=>s+paraBrl(c.gasto, moedaCampanha(c)),0),
    receita: state.campanhas.reduce((s,c)=>s+paraBrl(c.receita, moedaCampanha(c)),0),
    conversoes: state.campanhas.reduce((s,c)=>s+(Number(c.conversoes)||0),0)
  };
}

// Um registro por dia (acumulado). Entradas repetidas inflam os deltas na Visão geral.
function compactHistoryInPlace(){
  if(!Array.isArray(state.history)) { state.history = []; return false; }
  const sorted = [...state.history].filter(h=>h && h.date).sort((a,b)=> String(a.date).localeCompare(String(b.date)));
  const byDate = new Map();
  for (const h of sorted) {
    byDate.set(h.date, {
      date: h.date,
      gasto: Math.max(0, Number(h.gasto) || 0),
      receita: Math.max(0, Number(h.receita) || 0),
      conversoes: Math.max(0, Number(h.conversoes) || 0),
    });
  }
  const next = [...byDate.values()];
  const changed = next.length !== state.history.length
    || next.some((h, i) => {
      const o = state.history[i];
      return !o || o.date !== h.date || o.gasto !== h.gasto || o.receita !== h.receita;
    });
  state.history = next;
  return changed;
}

function dedupeCampanhasPorMeta(){
  const list = state.campanhas || [];
  const semMeta = [];
  const porMeta = new Map();
  for (const c of list) {
    const meta = String(c.idMeta || '').trim();
    if (!meta) { semMeta.push(c); continue; }
    const prev = porMeta.get(meta);
    if (!prev) { porMeta.set(meta, c); continue; }
    const peso = (x) => (Number(x.gasto) || 0) + (Number(x.receita) || 0);
    if (peso(c) >= peso(prev)) porMeta.set(meta, c);
  }
  const next = [...semMeta, ...porMeta.values()];
  const changed = next.length !== list.length;
  if (changed) state.campanhas = next;
  return changed;
}

function somaHourlyDia(key){
  const b = (state.hourly || {})[key];
  if (!b) return null;
  return b.reduce((t, x) => ({
    gasto: t.gasto + (Number(x && x.gasto) || 0),
    receita: t.receita + (Number(x && x.receita) || 0),
    conversoes: t.conversoes + (Number(x && x.conversoes) || 0),
  }), { gasto: 0, receita: 0, conversoes: 0 });
}

// Se o detalhe por hora de um dia fechado passa do delta do histórico, descartamos o hourly daquele dia.
function sanitizeHourlyVsHistory(){
  if (!state.hourly) return false;
  const mapa = {};
  dailyDeltas(state.history || []).slice(1).forEach((d) => { mapa[d.date] = d; });
  let changed = false;
  for (const key of Object.keys(state.hourly)) {
    const delta = mapa[key];
    const sum = somaHourlyDia(key);
    if (!delta || !sum) continue;
    const movimento = (delta.gasto || 0) + (delta.receita || 0);
    const soma = sum.gasto + sum.receita;
    if (movimento <= 0 && soma > 0.01) {
      delete state.hourly[key];
      changed = true;
      continue;
    }
    if (soma > movimento * 1.02 + 0.5) {
      delete state.hourly[key];
      changed = true;
    }
  }
  return changed;
}

function repairPanelSeries(){
  let changed = compactHistoryInPlace();
  if (dedupeCampanhasPorMeta()) changed = true;
  if (sanitizeHourlyVsHistory()) changed = true;
  return changed;
}

// Tira uma "foto" dos totais de hoje sempre que os dados mudam. Com o tempo,
// isso vira o histórico usado no gráfico de evolução e na meta do mês.
function snapshotHistory(){
  if(!state.history) state.history = [];
  compactHistoryInPlace();
  const hojeKey = localDateKey();
  const totals = currentTotals();
  const ultimo = state.history[state.history.length-1];
  if(ultimo && ultimo.date === hojeKey){
    ultimo.gasto = totals.gasto;
    ultimo.receita = totals.receita;
    ultimo.conversoes = totals.conversoes;
  } else {
    state.history.push({ date: hojeKey, gasto: totals.gasto, receita: totals.receita, conversoes: totals.conversoes });
  }
  if(state.history.length > 400) state.history = state.history.slice(-400);
}

// Totais acumulados até o fim do dia anterior — usados pra isolar
// "o que aconteceu hoje" dentro dos totais (que são cumulativos desde o início).
function totalsAntesDeHoje(){
  const hojeKey = localDateKey();
  const anteriores = state.history.filter(h=>h.date < hojeKey);
  if(!anteriores.length) return { gasto:0, receita:0, conversoes:0 };
  const ultimo = anteriores[anteriores.length-1];
  return { gasto: ultimo.gasto||0, receita: ultimo.receita||0, conversoes: ultimo.conversoes||0 };
}

// Distribui o que mudou hoje no "balde" da hora atual. Guardamos os últimos
// 35 dias pra comparar "hoje x ontem no mesmo horário" e consultar dias passados.
function snapshotHourly(){
  if(!state.hourly) state.hourly = {};
  const hojeKey = localDateKey();
  if(!state.hourly[hojeKey]){
    state.hourly[hojeKey] = Array.from({length:24}, ()=>({gasto:0, receita:0, conversoes:0}));
  }
  const totals = currentTotals();
  const base = totalsAntesDeHoje();
  const hojeAcum = {
    gasto: Math.max(0, totals.gasto - base.gasto),
    receita: Math.max(0, totals.receita - base.receita),
    conversoes: Math.max(0, totals.conversoes - base.conversoes)
  };
  const buckets = state.hourly[hojeKey];
  const somaAtual = buckets.reduce((s,b)=>({gasto:s.gasto+b.gasto, receita:s.receita+b.receita, conversoes:s.conversoes+b.conversoes}), {gasto:0,receita:0,conversoes:0});
  const hora = new Date().getHours();
  buckets[hora].gasto += Math.max(0, hojeAcum.gasto - somaAtual.gasto);
  buckets[hora].receita += Math.max(0, hojeAcum.receita - somaAtual.receita);
  buckets[hora].conversoes += Math.max(0, hojeAcum.conversoes - somaAtual.conversoes);

  const datas = Object.keys(state.hourly).sort();
  while(datas.length > 120){ delete state.hourly[datas.shift()]; }
}

async function saveState(opts){
  if(!opts || opts.snapshot !== false){
    snapshotHistory();
    snapshotHourly();
    snapshotItens();
  }
  try{
    await store.set(STORAGE_KEY, JSON.stringify(state));
  }catch(e){
    console.error('Falha ao salvar dados', e);
    showToast('Não foi possível salvar os dados agora.');
  }
}

function seedData(){
  const bm1 = uid(), bm2 = uid();
  const c1 = uid(), c2 = uid(), c3 = uid(), c4 = uid(), c5 = uid();
  const seed = {
    bms: [],
    contas: [
      { id: c1, nome: 'Loja Aurora — Conta 01', cliente: 'Loja Aurora', bmId: bm1, plataforma: 'Meta', status: 'ativa', limiteGasto: 350 },
      { id: c2, nome: 'Clínica Vitalis', cliente: 'Clínica Vitalis', bmId: bm1, plataforma: 'Meta', status: 'em revisão', limiteGasto: 180 },
      { id: c3, nome: 'Vitalis — Google', cliente: 'Clínica Vitalis', bmId: bm2, plataforma: 'Google Ads', status: 'ativa', limiteGasto: 220 },
      { id: c4, nome: 'Casa Nórdica — Conta 01', cliente: 'Casa Nórdica', bmId: bm1, plataforma: 'Meta', status: 'ativa', limiteGasto: 400 },
      { id: c5, nome: 'Casa Nórdica — Conta 02', cliente: 'Casa Nórdica', bmId: bm1, plataforma: 'Meta', status: 'ativa', limiteGasto: 250 }
    ],
    campanhas: [
      { id: uid(), nome: 'Aurora — Coleção Inverno', contaId: c1, cliente: 'Loja Aurora', loja: 'aurora-loja.myshopify.com', objetivo: 'Vendas', status: 'ativa', dataInicio: '2026-08-10', orcamento: 300, orcamentoTipo: 'Diário', produto: 'Casacos', gasto: 4210.32, receita: 15870.00, impressoes: 512000, cliques: 0, visualizacoes: 0, checkouts: 0, conversoes: 214 },
      { id: uid(), nome: 'Aurora — Retargeting', contaId: c1, cliente: 'Loja Aurora', loja: 'aurora-loja.myshopify.com', objetivo: 'Conversão', status: 'ativa', dataInicio: '2026-08-20', orcamento: 100, orcamentoTipo: 'Diário', produto: 'Casacos', gasto: 1488.90, receita: 3760.00, impressoes: 98000, cliques: 0, visualizacoes: 0, checkouts: 0, conversoes: 58 },
      { id: uid(), nome: 'Vitalis — Leads Odontologia', contaId: c2, cliente: 'Clínica Vitalis', loja: 'vitalis-store.myshopify.com', objetivo: 'Leads', status: 'pausada', dataInicio: '2026-07-02', orcamento: 150, orcamentoTipo: 'Diário', produto: 'Implantes', gasto: 2200.00, receita: 1980.00, impressoes: 210000, cliques: 0, visualizacoes: 0, checkouts: 0, conversoes: 41 },
      { id: uid(), nome: 'Nórdica — Sala de estar', contaId: c4, cliente: 'Casa Nórdica', loja: 'casanordica.myshopify.com', objetivo: 'Vendas', status: 'ativa', dataInicio: addDays(localDateKey(), -22), orcamento: 250, orcamentoTipo: 'Diário', produto: 'Sofás', gasto: 3100, receita: 9800, impressoes: 260000, cliques: 0, visualizacoes: 0, checkouts: 0, conversoes: 96 },
      { id: uid(), nome: 'Nórdica — Quarto (teste)', contaId: c5, cliente: 'Casa Nórdica', loja: 'casanordica.myshopify.com', objetivo: 'Vendas', status: 'ativa', dataInicio: addDays(localDateKey(), -2), orcamento: 120, orcamentoTipo: 'Diário', produto: 'Camas', gasto: 1650, receita: 4200, impressoes: 88000, cliques: 0, visualizacoes: 0, checkouts: 0, conversoes: 37 },
      { id: uid(), nome: 'Vitalis — Google Pesquisa', contaId: c3, cliente: 'Clínica Vitalis', loja: 'vitalis-store.myshopify.com', objetivo: 'Conversão', status: 'ativa', dataInicio: '2026-09-01', orcamento: 80, orcamentoTipo: 'Diário', produto: 'Implantes', gasto: 940.10, receita: 3120.00, impressoes: 41000, cliques: 0, visualizacoes: 0, checkouts: 0, conversoes: 33 }
    ],
    rejeitados: [
      { id: uid(), nomeAnuncio: 'Aurora — Vídeo promo 40% OFF', contaId: c1, motivo: 'Alegação de saúde/antes-e-depois não permitida.', data: '2026-09-05', status: 'em recurso' },
      { id: uid(), nomeAnuncio: 'Vitalis — Carrossel implantes', contaId: c2, motivo: 'Conteúdo sensível de saúde sem certificação anexada.', data: '2026-08-29', status: 'perdido' }
    ],
    importLog: [],
    history: [],
    meta: { valorObjetivo: 100000 },
    saude: { metaLojas: 5, metaContas: 8 },
    faturamento: { deducaoPct: 10 },
    hourly: gerarHourlyDemo()
  };
  seed.history = gerarHistoricoDemo(seed.campanhas, seed.hourly);
  seed.conjuntos = []; seed.anuncios = [];
  seed.integracao = { ativo:false, url:'', chave:'' }; seed.alteracoes = [];
  const agora = Date.now();
  seed.campanhas.forEach((c,i)=>{
    gerarHistItem(c, 95);
    c.ult = { em: new Date(agora - 1000*60*(15 + i*47)).toISOString(), gasto:Number(c.gasto)||0, receita:Number(c.receita)||0, vendas:Number(c.conversoes)||0 };
  });
  seed.sincronizadoEm = new Date(agora - 1000*60*15).toISOString();
  return seed;
}

// Gera um histórico fictício dos últimos dias só pra ilustrar o gráfico
// de evolução na primeira vez que o painel abre.
// Gera dados fictícios de hoje e ontem, hora a hora, só pra ilustrar o
// gráfico de faturamento por hora na primeira vez que o painel abre.
function gerarHourlyDemo(){
  const out = {};
  const hojeKey = localDateKey();
  const horaAtual = new Date().getHours();
  // curva típica de loja online: manhã subindo, pico no almoço e à tarde, noite fraca
  const curva = h => {
    const g = (c,w,a)=> a*Math.exp(-Math.pow(h-c,2)/(2*w*w));
    return g(8.8,1.6,0.55) + g(11.6,1.1,0.85) + g(14,0.55,1) + g(17,0.7,0.7) + (h===0 ? 0.16 : 0) + (h>=5 && h<=6 ? 0.1 : 0);
  };
  const somaCurva = Array.from({length:24},(_,h)=>curva(h)).reduce((x,y)=>x+y,0);
  const ticket = 123.5;
  for(let d=0; d<95; d++){
    const key = addDays(hojeKey, -d);
    const dow = new Date(key + 'T00:00:00').getDay();
    const fimDeSemana = (dow === 0 || dow === 6) ? 0.82 : 1;
    const tendencia = 2400 + 1500*(1 - d/95);
    const alvoDia = d===0 ? 6800 : tendencia * fimDeSemana * (0.85 + Math.random()*0.3);
    const ateHora = d===0 ? horaAtual : 23;
    const fatorGasto = 0.27 + Math.random()*0.14;
    const buckets = Array.from({length:24}, ()=>({gasto:0,receita:0,conversoes:0}));
    for(let h=0; h<=ateHora; h++){
      const receita = Math.max(0, curva(h)/somaCurva*alvoDia*(0.9 + Math.random()*0.2));
      buckets[h] = {
        gasto: Math.round(receita*fatorGasto*100)/100,
        receita: Math.round(receita*100)/100,
        conversoes: Math.round(receita/ticket)
      };
    }
    out[key] = buckets;
  }
  return out;
}

// Histórico diário fictício (valores acumulados) que termina exatamente nos
// totais atuais das campanhas — assim o primeiro salvamento não gera um pico falso.
function gerarHistoricoDemo(campanhas, hourly){
  const soma = (k)=> campanhas.reduce((t,c)=>t+(Number(c[k])||0),0);
  const somaDia = (key)=> (hourly[key]||[]).reduce((t,b)=>({gasto:t.gasto+b.gasto, receita:t.receita+b.receita, conversoes:t.conversoes+b.conversoes}), {gasto:0,receita:0,conversoes:0});
  let cur = { gasto: soma('gasto'), receita: soma('receita'), conversoes: soma('conversoes') };
  const out = [];
  let key = localDateKey();
  const nDias = Object.keys(hourly).length + 1;
  for(let i=0;i<nDias;i++){
    out.unshift({ date: key, gasto: Math.round(cur.gasto*100)/100, receita: Math.round(cur.receita*100)/100, conversoes: Math.round(cur.conversoes) });
    const d = hourly[key] ? somaDia(key) : { gasto: 700+Math.random()*500, receita: 1400+Math.random()*1800, conversoes: 3+Math.floor(Math.random()*6) };
    // acumulado pode ficar negativo no começo do exemplo; só as diferenças diárias importam
    cur = { gasto: cur.gasto-d.gasto, receita: cur.receita-d.receita, conversoes: cur.conversoes-d.conversoes };
    key = addDays(key, -1);
  }
  return out;
}

/* =========================================================
   HELPERS DE FORMATAÇÃO E MÉTRICAS
   ========================================================= */

function contaNome(id){ const c = state.contas.find(x=>x.id===id); return c ? c.nome : '—'; }
function bmNome(id){ const b = state.bms.find(x=>x.id===id); return b ? b.nome : '—'; }

function navSections(){
  return SECTIONS.filter((s) => !s.adminOnly || isAdmin());
}

function navItemHtml(s){
  let count = '';
  if(s.id==='rejeitados'){
    const n = state.rejeitados.filter(r=>r.status==='em recurso').length;
    if(n>0) count = `<span class="nav-count alert">${n}</span>`;
  }
  if(s.id==='contas'){
    const n = state.contas.filter(c=>c.status!=='ativa').length;
    if(n>0) count = `<span class="nav-count">${n}</span>`;
  }
  const href = buildHref({ section: s.id, page: 1, g2Level: s.id === 'campanhas' ? 'campanhas' : null });
  return `<a href="${href}" class="nav-item ${currentSection===s.id?'active':''}" data-nav="${s.id}" aria-current="${currentSection===s.id?'page':'false'}">
    <span class="nav-left">
      <span class="nav-icon-badge">${s.icon}</span>
      <span class="nav-label">${s.label}</span>
      <span class="nav-short">${s.short}</span>
    </span>
    ${count}
  </a>`;
}

function renderNav(){
  const nav = document.getElementById('nav');
  const seen = new Set();
  const html = [];
  for (const s of navSections()) {
    if (s.group && NAV_GROUPS[s.group]) {
      if (seen.has(s.group)) continue;
      seen.add(s.group);
      const group = NAV_GROUPS[s.group];
      const children = group.children
        .map((id) => navSections().find((item) => item.id === id))
        .filter(Boolean);
      const home = group.nav || group.children[group.children.length - 1];
      const open = children.some((item) => item.id === currentSection);
      html.push(`<div class="nav-group${open ? ' open' : ''}">
        <button type="button" class="nav-item nav-group-toggle" data-nav="${home}" aria-expanded="${open}">
          <span class="nav-left">
            <span class="nav-icon-badge">${group.icon}</span>
            <span class="nav-label">${group.label}</span>
            <span class="nav-short">${group.short}</span>
          </span>
        </button>
        <div class="nav-sub">${children.map((item) => navItemHtml(item)).join('')}</div>
      </div>`);
      continue;
    }
    html.push(navItemHtml(s));
  }
  nav.innerHTML = html.join('');
}

function viewHref(overrides = {}) {
  const section = overrides.section ?? currentSection;
  const g2Level = overrides.g2Level ?? (section === 'campanhas' ? g2.nivel : null);
  const page = overrides.page ?? (
    section === 'campanhas' ? g2.pagina
      : (Object.prototype.hasOwnProperty.call(listPage, section) ? listPage[section] : 1)
  );
  return buildHref({
    section,
    g2Level,
    page,
    trackerId: section === 'tracker' ? (overrides.trackerId ?? trackerRouteId()) : '',
  });
}

function syncUrl({ replace = false } = {}) {
  const href = viewHref();
  if (hrefMatches(href)) return;
  history[replace ? 'replaceState' : 'pushState']({ section: currentSection }, '', href);
}

function goSection(id, { fromHistory = false, page, g2Level, trackerId = '' } = {}) {
  appPerfil = false;
  if (id === 'integracoes') id = 'lojas';
  if (id === 'admin' && !isAdmin()) {
    id = 'overview';
  }
  const meta = SECTIONS.find(s=>s.id===id);
  if(!meta) return;
  const mudou = currentSection !== id;
  currentSection = id;
  if (id === 'tracker') aplicarRotaTracker(fromHistory ? trackerId : '');
  if (id === 'campanhas') {
    const nextLevel = g2Level || (!fromHistory ? 'campanhas' : g2.nivel);
    if (g2.nivel !== nextLevel) {
      g2.nivel = nextLevel;
      g2.sel.clear();
      g2.soSugestao = false;
      g2.soNaoTrack = false;
      salvarG2();
    }
    if (page != null) g2.pagina = page;
    else if (!fromHistory) g2.pagina = 1;
  } else if (Object.prototype.hasOwnProperty.call(listPage, id)) {
    if (page != null) listPage[id] = page;
    else if (!fromHistory) listPage[id] = 1;
  }
  document.getElementById('section-title').textContent = meta.label;
  document.getElementById('section-sub').textContent = meta.sub;
  document.title = `${meta.label} — Torre de Controle`;
  document.getElementById('btn-add').classList.toggle('hidden', ['overview','funil','relatorios','importar','vendas','produtos','tracker','lojas','notificacoes'].includes(id));
  const addBtn = document.getElementById('btn-add');
  if (addBtn && !addBtn.classList.contains('hidden')) {
    addBtn.textContent = id === 'admin' ? '+ Administrador' : id === 'lojas' ? '+ Loja' : id === 'bms' ? 'Conectar Facebook' : '+ Novo';
  }
  if(id!=='importar') pendingImport = null;
  renderNav();
  renderContent();
  renderMetaMini();
  syncUrl({ replace: fromHistory });
  syncUrl({ replace: true });
  if (id === 'campanhas') baixarCampanhasFacebook(false);
  if(mudou && window.innerWidth <= 820){
    window.scrollTo(0, 0);
    document.querySelector('.nav-item.active')?.scrollIntoView({ block:'nearest', inline:'center' });
  }
}

function applyLocation() {
  const parsed = parseRoute();
  if (AUTH_SCREENS.has(parsed.screen)) {
    goSection('overview', { fromHistory: true });
    return;
  }
  goSection(parsed.section, {
    fromHistory: true,
    page: parsed.page,
    g2Level: parsed.g2Level,
    trackerId: parsed.trackerId || '',
  });
}

function renderAuthChrome() {
  const session = getSession();
  if (!session) return;
  const name = document.getElementById('sidebar-user-name');
  const email = document.getElementById('sidebar-user-email');
  const av = document.getElementById('sidebar-user-av');
  if (name) name.textContent = session.name || 'Operador';
  if (email) email.textContent = session.email || '';
  if (av) av.textContent = initialsOf(session);
}

let appReady = false;
let sessionChecked = false;

function consumeFacebookReturn() {
  const params = new URLSearchParams(window.location.search);
  const facebook = params.get('facebook');
  if (!facebook) return null;
  const result = {
    facebook,
    bms: params.get('bms') || '0',
    contas: params.get('contas') || '0',
    motivo: params.get('motivo') || '',
  };
  params.delete('facebook');
  params.delete('bms');
  params.delete('contas');
  params.delete('motivo');
  const search = params.toString();
  history.replaceState({}, '', `${window.location.pathname}${search ? `?${search}` : ''}`);
  return result;
}

async function finishFacebookOAuth() {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  const state = params.get('state');
  const error = params.get('error');
  if (!code && !error) return null;
  params.delete('code');
  params.delete('state');
  params.delete('error');
  params.delete('error_reason');
  params.delete('error_description');
  const search = params.toString();
  history.replaceState({}, '', `${window.location.pathname}${search ? `?${search}` : ''}`);
  if (error) return { facebook: error === 'access_denied' ? 'negado' : 'falha' };
  try {
    const result = await completeFacebookOAuth({ code, state });
    const businesses = result?.data?.businesses || [];
    if (!businesses.length) return { facebook: 'sem_bm' };
    return { facebook: 'escolher', businesses, user: result?.data?.user || null };
  } catch (err) {
    const message = err?.message || '';
    if (/nenhuma BM/i.test(message)) return { facebook: 'sem_bm' };
    if (/já estão conectadas/i.test(message)) return { facebook: 'duplicada', motivo: message };
    if (/já foi conectada por/i.test(message)) return { facebook: 'em_uso', motivo: message };
    return { facebook: 'falha', motivo: message };
  }
}

function consumeShopifyReturn() {
  const params = new URLSearchParams(window.location.search);
  const shopify = params.get('shopify');
  if (!shopify) return null;
  const result = {
    shopify,
    historico: params.get('historico') || '',
    motivo: params.get('motivo') || '',
  };
  params.delete('shopify');
  params.delete('historico');
  params.delete('motivo');
  const search = params.toString();
  history.replaceState({}, '', `${window.location.pathname}${search ? `?${search}` : ''}`);
  return result;
}

function shopifyReturnMessage(result) {
  if (result.shopify === 'ok' && result.historico === 'parcial') {
    return 'Loja conectada. Pedidos não pagos com mais de 60 dias podem ficar de fora até a Shopify liberar o histórico completo.';
  }
  if (result.shopify === 'ok') return 'Loja conectada. Importando produtos e vendas.';
  if (result.shopify === 'negado') return 'Você não autorizou a Torre na Shopify.';
  if (result.shopify === 'dominio') return 'Informe o domínio admin da loja (minhaloja.myshopify.com).';
  if (result.shopify === 'escopo') return 'A Shopify não liberou leitura de produtos e vendas.';
  if (result.shopify === 'app') return 'O login da Shopify ainda não está disponível.';
  if (result.motivo) return result.motivo;
  return 'Não foi possível concluir a conexão com a Shopify.';
}

function facebookReturnMessage(result) {
  if (result.facebook === 'ok') {
    return `Facebook conectado. ${result.bms} BM(s) e ${result.contas} conta(s) importada(s).`;
  }
  if (result.facebook === 'negado') return 'Você não autorizou a Torre no Facebook.';
  if (result.facebook === 'sem_bm') return 'Essa conta do Facebook não administra nenhuma BM.';
  if (result.facebook === 'duplicada') {
    return result.motivo || 'Todas as BMs desta conta já estão conectadas aqui.';
  }
  if (result.facebook === 'em_uso') {
    return result.motivo || 'Esta BM já foi conectada por outro usuário.';
  }
  if (result.facebook === 'app') return 'O login do Facebook ainda não está disponível.';
  if (result.motivo) return result.motivo;
  return 'Não foi possível concluir a conexão com o Facebook.';
}

function redirectShopifyAppInstallIfNeeded() {
  const params = new URLSearchParams(window.location.search);
  const shop = params.get('shop');
  const hmac = params.get('hmac');
  const timestamp = params.get('timestamp');
  if (!shop || !hmac || !timestamp || params.get('code')) return false;
  const install = new URL('/integrations/shopify/install', window.location.origin);
  install.search = params.toString();
  window.location.replace(install.toString());
  return true;
}

async function garantirDados() {
  if (appReady) return;
  showBootScreen('Carregando dados', 'Preparando o painel');
  await loadState();
  appReady = true;
  renderAuthChrome();
}

async function handleRoute() {
  aplicarModoApp();
  if (onboardingPendente()) {
    mostrarOnboarding(() => { handleRoute(); });
    return;
  }
  if (redirectShopifyAppInstallIfNeeded()) return;
  if (!sessionChecked) {
    await restoreSession();
    await prepararApp();
    sessionChecked = true;
  }
  const parsed = parseRoute();
  if (AUTH_SCREENS.has(parsed.screen)) {
    if (getSession() && parsed.screen === 'login') {
      await garantirDados();
      hideLoginScreen();
      history.replaceState({}, '', consumeLoginNext() || buildHref({ section: 'overview' }));
      applyLocation();
      return;
    }
    showLoginScreen();
    if (parsed.screen === 'set-password') await loadResetToken();
    return;
  }
  if (!getSession()) {
    history.replaceState({ screen: 'login' }, '', loginHref(`${window.location.pathname}${window.location.search}`));
    showLoginScreen();
    return;
  }
  const facebookReturn = (await finishFacebookOAuth()) || consumeFacebookReturn();
  const shopifyReturn = consumeShopifyReturn();
  if (!appReady) await garantirDados();
  else if (facebookReturn?.facebook === 'ok') await loadState();
  hideLoginScreen();
  if (shopifyReturn?.shopify === 'ok') {
    invalidateVendas();
    invalidateProdutos();
    invalidateFunil();
  }
  applyLocation();
  await carregarCotacao();
  pintarMoeda();
  renderContent();
  renderMetaMini();
  if (facebookReturn?.facebook === 'escolher') {
    openBmPicker(facebookReturn.businesses, {
      onDone: async (result) => {
        await loadState();
        renderNav();
        renderContent();
        renderMetaMini();
        const imported = result?.data?.imported || {};
        showToast(`Facebook conectado. ${imported.bms || 0} BM(s) e ${imported.contas || 0} conta(s) importada(s).`);
      },
    });
  } else if (facebookReturn) showToast(facebookReturnMessage(facebookReturn));
  if (shopifyReturn) showToast(shopifyReturnMessage(shopifyReturn));
}

async function carregarCotacao(){
  try{
    const res = await apiRequest('/integrations/fx/usd-brl');
    const data = res?.data || {};
    if(Number(data.rate) > 0) setTaxaUsdBrl(data.rate, data.quotedAt);
  }catch(err){
    console.warn('cotação', err);
  }
}

function pintarMoeda(){
  document.querySelectorAll('[data-moeda]').forEach((btn)=>{
    const on = btn.dataset.moeda === moedaBase();
    btn.classList.toggle('on', on);
    btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    btn.title = cotacaoLabel() || 'Moeda base dos gráficos e relatórios';
  });
}

async function trocarMoeda(next){
  const alvo = next === 'USD' ? 'USD' : 'BRL';
  if(alvo === moedaBase()) return;
  const rate = taxaUsdBrl();
  if(!(rate > 0)){
    showToast('A cotação do dia ainda não carregou.');
    return;
  }
  if(state.meta?.valorObjetivo){
    state.meta.valorObjetivo = alvo === 'USD' ? state.meta.valorObjetivo / rate : state.meta.valorObjetivo * rate;
  }
  state.moeda = { base: alvo };
  setMoedaBase(alvo);
  await saveState({ snapshot:false });
  pintarMoeda();
  renderContent();
  renderMetaMini();
  showToast(`Moeda base: ${alvo}. ${cotacaoLabel()}`);
}

function kickToLogin(nextPath) {
  signOut();
  sessionChecked = true;
  appReady = false;
  const next = nextPath || `${window.location.pathname}${window.location.search}`;
  history.replaceState({ screen: 'login' }, '', loginHref(next));
  showLoginScreen();
}

async function logout() {
  await esquecerAparelho();
  await signOut();
  sessionChecked = true;
  appReady = false;
  showToast('Sessão encerrada.');
  history.replaceState({ screen: 'login' }, '', loginHref());
  showLoginScreen();
}

window.addEventListener('torre:unauthorized', () => {
  if (modoApp()) showToast('Sua sessão expirou. Entre de novo.');
  kickToLogin();
});

function dailyDeltas(history){
  return (history||[]).map((h,i)=>{
    if(i===0) return { date:h.date, gasto:h.gasto, receita:h.receita, conversoes: h.conversoes||0 };
    const prev = history[i-1];
    return {
      date: h.date,
      gasto: Math.max(0, h.gasto - prev.gasto),
      receita: Math.max(0, h.receita - prev.receita),
      conversoes: Math.max(0, (h.conversoes||0) - (prev.conversoes||0))
    };
  });
}

// Barrinha compacta de meta de lucro, fixada no cabeçalho — visível em
// qualquer seção do painel, não só na Visão Geral.
function renderMetaMini(){
  const el = document.getElementById('meta-mini');
  if(!el) return;
  const meta = state.meta || { valorObjetivo: 0 };
  if(!meta.valorObjetivo){
    el.innerHTML = `<button class="meta-mini-btn" data-action="edit" data-type="meta" title="Clique pra definir uma meta de lucro mensal.">
      <span class="meta-mini-icon">${ICONS.trofeu}</span>
      <span class="meta-mini-label">Definir meta</span>
    </button>`;
    return;
  }
  const now = new Date();
  const mesAtual = localDateKey(now).slice(0,7);
  const deltasMes = dailyDeltas(state.history).filter(d=>d.date.startsWith(mesAtual));
  const receitaMes = deltasMes.reduce((s,d)=>s+d.receita,0);
  const gastoMes = deltasMes.reduce((s,d)=>s+d.gasto,0);
  const lucroMes = receitaMes - gastoMes;
  const lucroNaBase = paraBase(lucroMes, 'BRL');
  const pct = (lucroNaBase/meta.valorObjetivo)*100;
  const pctBarra = Math.max(0, Math.min(100, pct));
  const bateu = lucroNaBase >= meta.valorObjetivo;
  const cor = lucroMes<0 ? 'var(--red)' : bateu ? 'var(--green)' : 'var(--blue)';
  const tooltip = `Meta na moeda base (${moedaBase()}). ${cotacaoLabel() || 'Cotação do dia ainda não carregou.'} Lucro do mês: ${money(lucroMes)} de meta ${fmtValor(meta.valorObjetivo)} (${pct.toLocaleString('pt-BR',{maximumFractionDigits:0})}%). Clique pra editar.`;

  el.innerHTML = `
    <button class="meta-mini-btn" data-action="edit" data-type="meta" title="${escapeHtml(tooltip)}">
      <div class="meta-mini-row">
        <span class="meta-mini-icon">${ICONS.trofeu}</span>
        <span class="meta-mini-label">Lucro</span>
        <span class="meta-mini-info">i</span>
        <span class="meta-mini-values mono">${ocultarValores ? `${simboloMoeda()} •••` : `${money(lucroMes)} / ${fmtValor(meta.valorObjetivo)}`}</span>
      </div>
      <div class="meta-mini-bar"><div class="meta-mini-bar-fill" style="width:${pctBarra}%;background:linear-gradient(90deg,var(--purple),${cor});box-shadow:0 0 8px ${cor==='var(--blue)'?'var(--blue-glow)':cor==='var(--green)'?'var(--green-glow)':'var(--red-glow)'};"></div></div>
    </button>`;
}

// Estima o gasto médio diário de uma conta (com base nas campanhas ativas
// vinculadas a ela) e compara com o limite de gasto diário cadastrado.
function contaRitmoGasto(conta){
  const camps = state.campanhas.filter(c=>c.contaId===conta.id && c.status==='ativa');
  if(!camps.length || !conta.limiteGasto || Number(conta.limiteGasto)<=0) return null;
  const totalGasto = camps.reduce((s,c)=>s+(Number(c.gasto)||0),0);
  const datas = camps.map(c=>c.dataInicio).filter(Boolean).sort();
  if(!datas.length) return null;
  const inicio = new Date(datas[0]+'T00:00:00');
  const dias = Math.max(1, Math.round((new Date() - inicio)/86400000));
  const mediaDiaria = totalGasto/dias;
  const pctLimite = (mediaDiaria/Number(conta.limiteGasto))*100;
  return { mediaDiaria, pctLimite };
}

const MASK = '•••••';
function money(v){ return ocultarValores ? `${simboloMoeda()} ${MASK}` : fmtValor(paraBase(v, 'BRL')); }
function moneyConta(v, origem){ return ocultarValores ? `${simboloMoeda()} ${MASK}` : fmtValor(paraBase(v, origem)); }

function periodoInfo(per){
  per = per || periodo;
  const hoje = localDateKey();
  let de, ate;
  switch(per.tipo){
    case 'ontem': de = ate = addDays(hoje,-1); break;
    case '7d':  de = addDays(hoje,-6);  ate = hoje; break;
    case '30d': de = addDays(hoje,-29); ate = hoje; break;
    case '90d': de = addDays(hoje,-89); ate = hoje; break;
    case 'custom':
      de = per.de || hoje; ate = per.ate || de;
      if(ate > hoje) ate = hoje;
      if(de > ate) [de, ate] = [ate, de];
      break;
    default: de = ate = hoje;
  }
  const n = diasEntre(de, ate) + 1;
  const unico = n === 1;
  const ehHoje = unico && ate === hoje;
  const curto = k => fmtDate(k).slice(0,5);
  let nome = (PERIODOS.find(p=>p[0]===per.tipo) || PERIODOS[0])[1];
  if(per.tipo === 'custom') nome = unico ? fmtDate(de) : `${curto(de)} – ${curto(ate)}`;
  let vsLabel;
  if(ehHoje) vsLabel = 'vs. ontem no mesmo horário';
  else if(per.tipo === 'ontem') vsLabel = 'vs. anteontem';
  else if(unico) vsLabel = `vs. ${curto(addDays(de,-1))}`;
  else vsLabel = `vs. ${n} dias anteriores`;
  return {
    de, ate, n, unico, ehHoje, nome, vsLabel,
    dias: listaDias(de, n),
    antDias: listaDias(addDays(de, -n), n),
    ateHora: ehHoje ? new Date().getHours() : 23
  };
}

// Movimento de um dia: usa o detalhamento por hora quando existe,
// senão a diferença diária do histórico.
function mapaDiario(){
  const m = {};
  dailyDeltas(state.history).slice(1).forEach(d=>{ m[d.date] = d; });
  return m;
}
function resumoDia(key, ateHora, mapa){
  const d = mapa[key];
  const diaFechado = ateHora >= 23;
  const hojeKey = localDateKey();
  const parcial = key === hojeKey && ateHora < 23;
  // Dia fechado: delta diário do histórico (evita hourly duplicado ou desatualizado).
  if (diaFechado && d && !parcial) {
    return { gasto: d.gasto || 0, receita: d.receita || 0, pedidos: d.conversoes || 0 };
  }
  const b = (state.hourly || {})[key];
  if (b) {
    const r = { gasto: 0, receita: 0, pedidos: 0 };
    for (let h = 0; h <= ateHora; h++) {
      r.gasto += Number(b[h] && b[h].gasto) || 0;
      r.receita += Number(b[h] && b[h].receita) || 0;
      r.pedidos += Number(b[h] && b[h].conversoes) || 0;
    }
    return r;
  }
  if (d) return { gasto: d.gasto || 0, receita: d.receita || 0, pedidos: d.conversoes || 0 };
  return { gasto: 0, receita: 0, pedidos: 0 };
}
function cobertura(dias, mapa){
  if(!dias.length) return 0;
  const com = dias.filter(k=> (state.hourly||{})[k] || mapa[k]).length;
  return com / dias.length;
}
function somaDias(dias, ateHora, mapa){
  return dias.reduce((t,k)=>{
    const r = resumoDia(k, ateHora, mapa);
    t.gasto += r.gasto; t.receita += r.receita; t.pedidos += r.pedidos;
    return t;
  }, { gasto:0, receita:0, pedidos:0 });
}

// Líquido = bruto − taxas, descontos e reembolsos (% definido no ajuste do card).
function liquidoDe(bruto){
  const deducao = Number((state.faturamento || {}).deducaoPct) || 0;
  return bruto * Math.max(0, 1 - deducao/100);
}

function somaAteHora(buckets, ateHora){
  const r = { bruto:0, pedidos:0 };
  if(!buckets) return r;
  for(let h=0; h<=ateHora; h++){
    r.bruto += Number(buckets[h] && buckets[h].receita) || 0;
    r.pedidos += Number(buckets[h] && buckets[h].conversoes) || 0;
  }
  return r;
}

// null = não dá pra comparar (dia anterior zerado)

function renderPeriodBar(){
  const P = periodoInfo();
  const hoje = localDateKey();
  const opcoes = PERIODOS.map(([v,l])=>{
    const rotulo = (v === 'custom' && periodo.tipo === 'custom') ? P.nome : l;
    return `<option value="${v}" ${periodo.tipo===v?'selected':''}>${rotulo}</option>`;
  }).join('');
  return `
  <div class="period-bar o-period">
    <div class="period-box" id="period-box">
      <select class="period-select" id="periodo-select" aria-label="Período">${opcoes}</select>
      <button type="button" class="sq-btn" data-action="periodo-cal" title="Período personalizado" aria-label="Período personalizado" aria-expanded="false">${HERO_ICONS.calendar}</button>
      <div class="period-pop" id="periodo-pop" hidden>
        <div class="pp-title">Período personalizado</div>
        <div class="pp-quick">
          ${[['hoje','Hoje'],['ontem','Ontem'],['7d','7 dias'],['30d','30 dias'],['90d','90 dias']].map(([v,l])=>
            `<button type="button" class="pp-chip ${periodo.tipo===v?'on':''}" data-action="pp-quick" data-v="${v}">${l}</button>`).join('')}
        </div>
        <div class="pp-row">
          <div class="field"><label for="pp-de">De</label><input type="date" id="pp-de" max="${hoje}" value="${P.de}"></div>
          <div class="field"><label for="pp-ate">Até</label><input type="date" id="pp-ate" max="${hoje}" value="${P.ate}"></div>
        </div>
        <div class="pp-actions">
          <button type="button" class="btn btn-ghost" data-action="pp-cancel">Cancelar</button>
          <button type="button" class="btn btn-primary" data-action="pp-apply">Aplicar</button>
        </div>
      </div>
    </div>
    <button type="button" class="eye-btn ${ocultarValores?'on':''}" data-action="toggle-eye"
      title="${ocultarValores?'Mostrar valores':'Ocultar valores'}" aria-label="${ocultarValores?'Mostrar valores':'Ocultar valores'}">
      ${ocultarValores ? HERO_ICONS.eyeOff : HERO_ICONS.eye}
    </button>
  </div>`;
}

function renderFaturamentoCard(){
  const P = periodoInfo();
  const mapa = mapaDiario();
  const atual = somaDias(P.dias, P.ateHora, mapa);
  const anterior = somaDias(P.antDias, P.ateHora, mapa);
  const temBase = cobertura(P.antDias, mapa) >= 0.5;
  const vendoLiquido = faturamentoView === 'liquido';
  const conv = vendoLiquido ? liquidoDe : (v=>v);
  const principal = conv(atual.receita);
  const dPrincipal = temBase ? deltaPct(principal, conv(anterior.receita)) : null;
  const dPedidos = temBase ? deltaPct(atual.pedidos, anterior.pedidos) : null;
  const ticket = atual.pedidos > 0 ? principal / atual.pedidos : 0;
  const ticketAnt = anterior.pedidos > 0 ? conv(anterior.receita) / anterior.pedidos : 0;
  const cls = deltaClass(dPrincipal);
  const semBase = s => temBase ? s : '';

  // pontos do gráfico: por hora (1 dia) ou por dia (vários dias)
  let bruto, pedidos, brutoAnt, eixo, rotulo, n, ultimo;
  if(P.unico){
    const b = (state.hourly||{})[P.de], ba = (state.hourly||{})[P.antDias[0]];
    const pega = (bk, h, k) => Number(bk && bk[h] && bk[h][k]) || 0;
    n = 24; ultimo = P.ateHora;
    bruto = Array.from({length:24}, (_,h)=> pega(b,h,'receita'));
    pedidos = Array.from({length:24}, (_,h)=> pega(b,h,'conversoes'));
    brutoAnt = Array.from({length:24}, (_,h)=> pega(ba,h,'receita'));
    eixo = W => (W < 520 ? [0,6,12,18,23] : [0,4,8,12,16,20,23]).map(h=>({ i:h, txt:String(h).padStart(2,'0')+'h' }));
    rotulo = h => `${String(h).padStart(2,'0')}h – ${String(h).padStart(2,'0')}h59`;
  } else {
    const dia = P.dias.map(k=>resumoDia(k, 23, mapa));
    n = P.n; ultimo = n - 1;
    bruto = dia.map(d=>d.receita);
    pedidos = dia.map(d=>d.pedidos);
    brutoAnt = P.antDias.map(k=>resumoDia(k, 23, mapa).receita);
    eixo = W => {
      const alvo = W < 520 ? 4 : 7;
      const passo = Math.max(1, Math.ceil(n / alvo));
      const idx = [];
      for(let i=0; i<n; i+=passo) idx.push(i);
      if(n-1 - idx[idx.length-1] < passo/2 && idx.length > 1) idx.pop();
      if(idx[idx.length-1] !== n-1) idx.push(n-1);
      return idx.map(i=>({ i, txt: fmtDate(P.dias[i]).slice(0,5) }));
    };
    const SEM = ['dom','seg','ter','qua','qui','sex','sáb'];
    rotulo = i => `${fmtDate(P.dias[i])} · ${SEM[new Date(P.dias[i]+'T00:00:00').getDay()]}`;
  }
  const liq = bruto.map(liquidoDe);
  fatChart = {
    n, ultimo, eixo, rotulo, liq, bruto, pedidos,
    principal: vendoLiquido ? liq : bruto,
    comparativo: vendoLiquido ? bruto : liq,
    anterior: brutoAnt.map(conv),
    temDados: atual.receita > 0,
    labelPrincipal: vendoLiquido ? 'líquido' : 'bruto',
    labelAnterior: P.ehHoje ? 'Ontem' : P.unico ? 'Dia anterior' : 'Período anterior'
  };

  return `
  <div class="g-card fat-card o-fat">
    <div class="card-head">
      <div class="eyebrow">Faturamento <span class="eyebrow-per">· ${P.nome}</span></div>
      <div class="fat-tools">
        <div class="fat-toggle" role="group" aria-label="Tipo de faturamento">
          <button type="button" class="pill-btn ${vendoLiquido?'on':''}" data-action="fat-view" data-view="liquido" aria-pressed="${vendoLiquido}"><span class="sw"></span>Líquido</button>
          <button type="button" class="pill-btn ${!vendoLiquido?'on':''}" data-action="fat-view" data-view="bruto" aria-pressed="${!vendoLiquido}"><span class="sw"></span>Bruto</button>
        </div>
        <button type="button" class="gear-btn" data-action="edit" data-type="faturamento" title="Ajustar cálculo do líquido" aria-label="Ajustar cálculo do líquido">${HERO_ICONS.gear}</button>
      </div>
    </div>
    <div class="fat-main">
      <div class="fat-left">
        <div class="fat-kind">${vendoLiquido ? 'Líquido' : 'Bruto'}</div>
        <div class="fat-value">${money(principal)}</div>
        <div class="fat-underline"></div>
        <div class="fat-delta-row">
          ${temBase
            ? `<span class="delta-badge ${cls}">${HERO_ICONS[cls]}${fmtDelta(dPrincipal)}</span><span class="fat-vs">${P.vsLabel}</span>`
            : `<span class="delta-badge flat">${HERO_ICONS.flat}—</span><span class="fat-vs">sem dados do período anterior pra comparar</span>`}
        </div>
      </div>
      <div class="fat-stats">
        <div class="mini-stat">
          <div class="lbl">Pedidos pagos</div>
          <div class="val">${fmtNum(atual.pedidos)}</div>
          ${semBase(deltaMini(dPedidos))}
        </div>
        <div class="mini-stat">
          <div class="lbl">Ticket médio</div>
          <div class="val">${money(ticket)}</div>
          ${semBase(deltaMini(deltaPct(ticket, ticketAnt)))}
        </div>
      </div>
    </div>
    <div class="fat-chart-wrap" id="fat-chart">
      <div class="fat-tip" id="fat-tip"></div>
    </div>
  </div>`;
}

// Curva suave sem "passar do ponto" (monotone cubic)
function monotonePath(pts){
  const n = pts.length;
  const f = v => v.toFixed(1);
  if(!n) return '';
  if(n === 1) return `M${f(pts[0][0])},${f(pts[0][1])}`;
  const dx = [], m = [], t = [];
  for(let i=0;i<n-1;i++){
    dx[i] = pts[i+1][0] - pts[i][0];
    m[i] = (pts[i+1][1] - pts[i][1]) / dx[i];
  }
  t[0] = m[0]; t[n-1] = m[n-2];
  for(let i=1;i<n-1;i++){
    if(m[i-1]*m[i] <= 0) t[i] = 0;
    else {
      const w1 = 2*dx[i] + dx[i-1], w2 = dx[i] + 2*dx[i-1];
      t[i] = (w1 + w2) / (w1/m[i-1] + w2/m[i]);
    }
  }
  let d = `M${f(pts[0][0])},${f(pts[0][1])}`;
  for(let i=0;i<n-1;i++){
    const h = dx[i]/3;
    d += ` C${f(pts[i][0]+h)},${f(pts[i][1]+h*t[i])} ${f(pts[i+1][0]-h)},${f(pts[i+1][1]-h*t[i+1])} ${f(pts[i+1][0])},${f(pts[i+1][1])}`;
  }
  return d;
}

function axisMoney(v){
  const n = paraBase(Number(v) || 0, 'BRL');
  const sym = simboloMoeda();
  if(ocultarValores) return `${sym} •••`;
  const loc = moedaBase() === 'USD' ? 'en-US' : 'pt-BR';
  if(Math.abs(n) >= 1000) return `${sym} ${(n/1000).toLocaleString(loc,{maximumFractionDigits:1})}k`;
  return `${sym} ${n.toLocaleString(loc,{maximumFractionDigits:0})}`;
}

// Desenha grade + eixo Y (3 faixas) e devolve as escalas.
function chartFrame(W, H, maxVal, pad){
  const step = niceStep(Math.max(maxVal, 1) / 3);
  const topo = Math.max(step*3, Math.ceil((maxVal*1.08)/step)*step);
  const y = v => pad.t + (1 - v/topo)*(H-pad.t-pad.b);
  let svg = '';
  for(let v=0; v<=topo+0.001; v+=step){
    const yy = y(v).toFixed(1);
    svg += `<line class="ch-grid" x1="${pad.l}" y1="${yy}" x2="${W-pad.r}" y2="${yy}" stroke-width="1" stroke-dasharray="2 5"/>`;
    svg += `<text x="${pad.l-12}" y="${(y(v)+4).toFixed(1)}" text-anchor="end" font-size="11" class="ch-axis" font-family="Sora, sans-serif">${axisMoney(v)}</text>`;
  }
  return { y, svg };
}

function drawFatChart(){
  const wrap = document.getElementById('fat-chart');
  if(!wrap || !fatChart) return;
  const W = Math.max(260, wrap.clientWidth);
  const H = wrap.clientHeight || 260;
  const pad = { l:58, r:12, t:10, b:28 };
  const { principal, comparativo, ultimo, n, temDados } = fatChart;
  const maxVal = Math.max(300, ...principal.slice(0, ultimo+1), ...comparativo.slice(0, ultimo+1));
  const { y, svg:grid } = chartFrame(W, H, maxVal, pad);
  const x = i => pad.l + i*(W-pad.l-pad.r)/Math.max(1, n-1);
  const base = y(0);

  const xLabels = fatChart.eixo(W).map(({i, txt})=>
    `<text x="${x(i).toFixed(1)}" y="${H-6}" text-anchor="${i===0?'start':i===n-1?'end':'middle'}" font-size="11" class="ch-axis" font-family="Sora, sans-serif">${txt}</text>`).join('');

  const ptsA = principal.slice(0, ultimo+1).map((v,i)=>[x(i), y(v)]);
  const ptsB = comparativo.slice(0, ultimo+1).map((v,i)=>[x(i), y(v)]);
  const lineA = monotonePath(ptsA);
  const last = ptsA[ptsA.length-1];
  const area = ptsA.length > 1 ? `${lineA} L${last[0].toFixed(1)},${base.toFixed(1)} L${ptsA[0][0].toFixed(1)},${base.toFixed(1)} Z` : '';

  wrap.querySelector('svg')?.remove();
  wrap.querySelector('.chart-empty')?.remove();
  wrap.insertAdjacentHTML('afterbegin', `
    <svg viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" role="img" aria-label="Faturamento no período">
      <defs>
        <linearGradient id="fatArea" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="#2f6bed" stop-opacity="0.3"/>
          <stop offset="100%" stop-color="#2f6bed" stop-opacity="0"/>
        </linearGradient>
      </defs>
      ${grid}
      ${area ? `<path d="${area}" fill="url(#fatArea)"/>` : ''}
      ${ptsB.length > 1 ? `<path d="${monotonePath(ptsB)}" fill="none" class="ch-compare" stroke-width="1.4" stroke-dasharray="5 5"/>` : ''}
      ${ptsA.length > 1 ? `<path d="${lineA}" fill="none" stroke="rgba(47,107,237,0.22)" stroke-width="9" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="${lineA}" fill="none" stroke="#2f6bed" stroke-width="2.75" stroke-linecap="round" stroke-linejoin="round"/>` : ''}
      <line id="fat-guide" x1="0" x2="0" y1="${pad.t}" y2="${base.toFixed(1)}" class="ch-guide" stroke-width="1" opacity="0"/>
      <circle id="fat-hover-dot" class="ch-dot" r="4.5" cx="0" cy="0" stroke="#2f6bed" stroke-width="2.5" opacity="0"/>
      ${last ? `<circle cx="${last[0].toFixed(1)}" cy="${last[1].toFixed(1)}" r="5" class="ch-dot" stroke="#2f6bed" stroke-width="2.5"/>` : ''}
      ${xLabels}
    </svg>`);
  if(!temDados){
    wrap.insertAdjacentHTML('beforeend', `<div class="chart-empty">Sem vendas registradas nesse período.</div>`);
  }
  fatChart.geo = { x, y, pad, W };
}

function onFatChartMove(e){
  const wrap = document.getElementById('fat-chart');
  if(!wrap || !fatChart || !fatChart.geo) return;
  const rect = wrap.getBoundingClientRect();
  const { x, y, pad, W } = fatChart.geo;
  const passo = (W - pad.l - pad.r) / Math.max(1, fatChart.n - 1);
  const i = Math.max(0, Math.min(fatChart.ultimo, Math.round((e.clientX - rect.left - pad.l) / passo)));
  const cx = x(i), cy = y(fatChart.principal[i]);
  const guide = wrap.querySelector('#fat-guide');
  const dot = wrap.querySelector('#fat-hover-dot');
  const tip = wrap.querySelector('#fat-tip');
  if(!guide || !dot || !tip) return;
  guide.setAttribute('x1', cx); guide.setAttribute('x2', cx); guide.setAttribute('opacity', 1);
  dot.setAttribute('cx', cx); dot.setAttribute('cy', cy); dot.setAttribute('opacity', 1);
  tip.innerHTML = `
    <div class="t-h">${fatChart.rotulo(i)}</div>
    <div class="t-r"><span>Líquido</span><b>${money(fatChart.liq[i])}</b></div>
    <div class="t-r"><span>Bruto</span><b>${money(fatChart.bruto[i])}</b></div>
    <div class="t-r"><span>Pedidos</span><b>${fmtNum(fatChart.pedidos[i])}</b></div>
    <div class="t-r"><span>${fatChart.labelAnterior} (${fatChart.labelPrincipal})</span><b>${money(fatChart.anterior[i])}</b></div>`;
  const tw = tip.offsetWidth || 170;
  let left = cx + 14;
  if(left + tw > rect.width) left = cx - tw - 14;
  tip.style.left = Math.max(0, left) + 'px';
  tip.style.top = Math.max(0, Math.min(cy - 40, rect.height - (tip.offsetHeight||110))) + 'px';
  tip.classList.add('show');
}
function onFatChartLeave(){
  const wrap = document.getElementById('fat-chart');
  if(!wrap) return;
  wrap.querySelector('#fat-guide')?.setAttribute('opacity', 0);
  wrap.querySelector('#fat-hover-dot')?.setAttribute('opacity', 0);
  wrap.querySelector('#fat-tip')?.classList.remove('show');
}

/* ---------- Saúde da conta = escala / diversificação ----------
   Olha as campanhas ATIVAS e mede o quanto:
   - a receita está espalhada entre lojas Shopify
   - o gasto está espalhado entre contas de anúncio
   Usa o "número efetivo" (1 / soma dos quadrados das fatias): 3 lojas com
   vendas iguais = 3; uma loja com 90% das vendas e duas pequenas ≈ 1,2.
   Cada lado vale 50 pontos e bate 100% quando atinge a meta configurada. */
function concentracao(mapa){
  const valores = Object.values(mapa).filter(v=>v>0);
  const total = valores.reduce((s,v)=>s+v,0);
  if(!total) return { qtd:0, efetivo:0, maiorPct:0 };
  const fatias = valores.map(v=>v/total);
  const hhi = fatias.reduce((s,f)=>s+f*f,0);
  return { qtd: valores.length, efetivo: 1/hhi, maiorPct: Math.max(...fatias)*100 };
}

function escalaInfo(){
  const cfg = state.saude || {};
  const metaLojas = Math.max(1, Number(cfg.metaLojas) || 5);
  const metaContas = Math.max(1, Number(cfg.metaContas) || 8);
  const ativas = state.campanhas.filter(c=>c.status==='ativa');
  const porLoja = {}, porConta = {};
  ativas.forEach(c=>{
    const loja = (c.loja||'').trim().toLowerCase();
    if(loja) porLoja[loja] = (porLoja[loja]||0) + (Number(c.receita)||0);
    if(c.contaId) porConta[c.contaId] = (porConta[c.contaId]||0) + (Number(c.gasto)||0);
  });
  const lojas = concentracao(porLoja);
  const contas = concentracao(porConta);
  const notaLojas = Math.min(1, lojas.efetivo / metaLojas);
  const notaContas = Math.min(1, contas.efetivo / metaContas);
  const score = Math.round(50*notaLojas + 50*notaContas);
  return { score, lojas, contas, metaLojas, metaContas };
}

function renderSaudeCard(){
  const e = escalaInfo();
  const score = e.score;
  const status = score >= 70 ? { txt:'ESCALADA', cor:'var(--green)' }
               : score >= 40 ? { txt:'EM EXPANSÃO', cor:'var(--gold)' }
               : { txt:'CONCENTRADA', cor:'#f0508c' };

  const cx = 115, cy = 100, r = 86;
  const pt = (deg, rad) => { const a = deg*Math.PI/180; return [cx + rad*Math.cos(a), cy - rad*Math.sin(a)]; };
  const [sx, sy] = pt(200, r), [ex, ey] = pt(-20, r);
  const arco = `M${sx.toFixed(2)},${sy.toFixed(2)} A${r},${r} 0 1 1 ${ex.toFixed(2)},${ey.toFixed(2)}`;
  const ticks = [196, 143, 90, 37, -16].map(deg=>{
    const [x1,y1] = pt(deg, 60), [x2,y2] = pt(deg, 67);
    return `<line x1="${x1.toFixed(1)}" y1="${y1.toFixed(1)}" x2="${x2.toFixed(1)}" y2="${y2.toFixed(1)}" class="g-tick" stroke-width="1.6" stroke-linecap="round"/>`;
  }).join('');

  // o maior risco aparece numa frase só
  let nota = '';
  if(e.lojas.qtd === 0 && e.contas.qtd === 0) nota = 'Cadastre a loja e a conta nas campanhas ativas pra medir a escala.';
  else if(e.lojas.maiorPct >= 60 && e.lojas.qtd > 0) nota = `${Math.round(e.lojas.maiorPct)}% das vendas vêm de uma loja só.`;
  else if(e.contas.maiorPct >= 60 && e.contas.qtd > 0) nota = `${Math.round(e.contas.maiorPct)}% do gasto está numa conta só.`;
  else if(score >= 70) nota = 'Vendas e gasto bem distribuídos.';
  else nota = 'Abra mais lojas e contas pra distribuir o risco.';

  return `
  <div class="g-card side-card health-card o-health" data-action="edit" data-type="saude" role="button" tabindex="0"
       title="Mede o quanto as vendas estão divididas entre lojas e o gasto entre contas de anúncio. Clique pra ajustar as metas.">
    <div class="card-head">
      <h3 class="card-title">Saúde da conta</h3>
      <span class="card-meta">escala</span>
    </div>
    <svg class="health-gauge" viewBox="0 0 230 152" role="img" aria-label="Escala: ${score} de 100, ${status.txt.toLowerCase()}">
      <defs>
        <linearGradient id="healthGrad" gradientUnits="userSpaceOnUse" x1="${cx-r}" y1="0" x2="${cx+r}" y2="0">
          <stop offset="0%" stop-color="#f0508c"/>
          <stop offset="30%" stop-color="#ff8a4a"/>
          <stop offset="52%" stop-color="#f7b23b"/>
          <stop offset="72%" stop-color="#b9cf45"/>
          <stop offset="100%" stop-color="#22c58b"/>
        </linearGradient>
      </defs>
      <path d="${arco}" fill="none" class="g-track" stroke-width="14" stroke-linecap="round"/>
      ${score > 0 ? `<path d="${arco}" fill="none" stroke="url(#healthGrad)" stroke-width="14" stroke-linecap="round" pathLength="100" stroke-dasharray="${score} 100"/>` : ''}
      ${ticks}
      <text x="${cx}" y="${cy+8}" text-anchor="middle" font-family="Sora, sans-serif" font-size="34" font-weight="600" class="g-score">${score}</text>
      <text x="${cx}" y="${cy+27}" text-anchor="middle" font-family="Sora, sans-serif" font-size="9" font-weight="600" letter-spacing="1.3" style="fill:${status.cor}">${status.txt}</text>
      <text x="${(sx+6).toFixed(1)}" y="148" text-anchor="middle" font-family="Sora, sans-serif" font-size="8" letter-spacing="1.1" class="g-end">CONCENTRADA</text>
      <text x="${(ex-6).toFixed(1)}" y="148" text-anchor="middle" font-family="Sora, sans-serif" font-size="8" letter-spacing="1.1" class="g-end">ESCALADA</text>
    </svg>
    <div class="health-legend">
      <span class="hl-lbl"><span class="hl-dot" style="background:#34d399;"></span>Lojas vendendo</span>
      <span class="hl-val">${e.lojas.qtd}<small>/ ${e.metaLojas}</small></span>
      <span class="hl-lbl"><span class="hl-dot" style="background:#4b84ff;"></span>Contas ativas</span>
      <span class="hl-val">${e.contas.qtd}<small>/ ${e.metaContas}</small></span>
    </div>
    <div class="health-note">${nota}</div>
  </div>`;
}

function evoDias(){
  const P = periodoInfo();
  return P.unico ? { dias: listaDias(addDays(P.ate, -13), 14), nome: 'últimos 14 dias' } : { dias: P.dias, nome: P.nome.toLowerCase() };
}

function renderEvolutionCard(){
  const { nome } = evoDias();
  return `
  <div class="g-card evo-card o-evo">
    <div class="card-head">
      <h3 class="card-title">Receita x gasto por dia <span class="card-meta">· ${nome}</span></h3>
      <div class="legend">
        <span><i style="background:#2f6bed;"></i>Receita</span>
        <span><i style="background:var(--chart-muted);"></i>Gasto</span>
      </div>
    </div>
    <div class="evo-chart-wrap" id="evo-chart"></div>
  </div>`;
}

function drawEvoChart(){
  const wrap = document.getElementById('evo-chart');
  if(!wrap) return;
  const mapa = mapaDiario();
  const { dias } = evoDias();
  const deltas = dias.map(k=>({ date:k, ...resumoDia(k, 23, mapa) }));
  if(deltas.length < 2 || !deltas.some(d=>d.receita>0 || d.gasto>0)){
    wrap.innerHTML = `<div class="chart-empty">Sem movimento registrado nesse período.</div>`;
    return;
  }
  const W = Math.max(260, wrap.clientWidth);
  const H = wrap.clientHeight || 200;
  const pad = { l:58, r:12, t:10, b:26 };
  const n = deltas.length;
  const maxVal = Math.max(1, ...deltas.map(d=>Math.max(d.gasto, d.receita)));
  const { y, svg:grid } = chartFrame(W, H, maxVal, pad);
  const x = i => pad.l + i*(W-pad.l-pad.r)/(n-1);
  const base = y(0);
  const pts = k => deltas.map((d,i)=>[x(i), y(d[k])]);
  const rec = pts('receita'), gas = pts('gasto');
  const lineR = monotonePath(rec);
  const areaR = `${lineR} L${rec[n-1][0].toFixed(1)},${base.toFixed(1)} L${rec[0][0].toFixed(1)},${base.toFixed(1)} Z`;
  const passo = Math.max(1, Math.ceil(n / (W < 520 ? 4 : 7)));
  const idx = [];
  for(let i=0; i<n; i+=passo) idx.push(i);
  if(n-1 - idx[idx.length-1] < passo/2 && idx.length > 1) idx.pop();
  if(idx[idx.length-1] !== n-1) idx.push(n-1);
  const xLabels = idx.map(i=>
    `<text x="${x(i).toFixed(1)}" y="${H-5}" text-anchor="${i===0?'start':i===n-1?'end':'middle'}" font-size="11" class="ch-axis" font-family="Sora, sans-serif">${fmtDate(deltas[i].date).slice(0,5)}</text>`).join('');
  evoChart = { x, y, pad, W, H, n, base, deltas };
  wrap.innerHTML = `
    <div class="fat-tip" id="evo-tip"></div>
    <svg viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" role="img" aria-label="Receita e gasto por dia">
      <defs>
        <linearGradient id="evoArea" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="#2f6bed" stop-opacity="0.22"/>
          <stop offset="100%" stop-color="#2f6bed" stop-opacity="0"/>
        </linearGradient>
      </defs>
      ${grid}
      <path d="${areaR}" fill="url(#evoArea)"/>
      <path d="${monotonePath(gas)}" fill="none" class="ch-muted" stroke-width="2" stroke-linecap="round"/>
      <path d="${lineR}" fill="none" stroke="#2f6bed" stroke-width="2.5" stroke-linecap="round"/>
      <line id="evo-guide" x1="0" x2="0" y1="${pad.t}" y2="${base.toFixed(1)}" class="ch-guide" stroke-width="1" opacity="0"/>
      <circle id="evo-dot-g" r="4" cx="0" cy="0" class="ch-dot" stroke="var(--chart-muted)" stroke-width="2.5" opacity="0"/>
      <circle id="evo-dot-r" r="4.5" cx="0" cy="0" class="ch-dot" stroke="#2f6bed" stroke-width="2.5" opacity="0"/>
      ${xLabels}
    </svg>`;
}

let evoChart = null;
function onEvoMove(e){
  const wrap = document.getElementById('evo-chart');
  if(!wrap || !evoChart) return;
  const { x, y, pad, W, n, deltas } = evoChart;
  const rect = wrap.getBoundingClientRect();
  const passo = (W - pad.l - pad.r) / Math.max(1, n-1);
  const i = Math.max(0, Math.min(n-1, Math.round((e.clientX - rect.left - pad.l) / passo)));
  const d = deltas[i];
  const cx = x(i);
  const set = (id, attrs) => { const el = wrap.querySelector('#'+id); if(el) for(const k in attrs) el.setAttribute(k, attrs[k]); };
  set('evo-guide', { x1:cx, x2:cx, opacity:1 });
  set('evo-dot-r', { cx, cy:y(d.receita), opacity:1 });
  set('evo-dot-g', { cx, cy:y(d.gasto), opacity:1 });
  const lucro = d.receita - d.gasto;
  const roas = d.gasto > 0 ? d.receita / d.gasto : 0;
  const SEM = ['dom','seg','ter','qua','qui','sex','sáb'];
  const tip = wrap.querySelector('#evo-tip');
  tip.innerHTML = `
    <div class="t-h">${fmtDate(d.date)} · ${SEM[new Date(d.date+'T00:00:00').getDay()]}</div>
    <div class="t-r"><span><i class="t-sw" style="background:#2f6bed;"></i>Receita</span><b>${money(d.receita)}</b></div>
    <div class="t-r"><span><i class="t-sw" style="background:var(--chart-muted);"></i>Gasto</span><b>${money(d.gasto)}</b></div>
    <div class="t-r"><span>Lucro</span><b style="color:${lucro>=0?'var(--ok)':'var(--bad)'};">${money(lucro)}</b></div>
    <div class="t-r"><span>ROAS</span><b>${roas.toLocaleString('pt-BR',{minimumFractionDigits:2,maximumFractionDigits:2})}x</b></div>
    <div class="t-r"><span>Pedidos</span><b>${fmtNum(d.pedidos)}</b></div>`;
  const tw = tip.offsetWidth || 170;
  let left = cx + 14;
  if(left + tw > rect.width) left = cx - tw - 14;
  tip.style.left = Math.max(0, left) + 'px';
  tip.style.top = Math.max(0, Math.min(y(d.receita) - 40, rect.height - (tip.offsetHeight||120))) + 'px';
  tip.classList.add('show');
}
function onEvoLeave(){
  const wrap = document.getElementById('evo-chart');
  if(!wrap) return;
  ['evo-guide','evo-dot-r','evo-dot-g'].forEach(id=> wrap.querySelector('#'+id)?.setAttribute('opacity', 0));
  wrap.querySelector('#evo-tip')?.classList.remove('show');
}

/* mini linhas dos cards: mostra o valor do ponto sob o mouse */
let kpiSparks = [];
function onSparkMove(e){
  const box = e.target.closest('.kpi-viz[data-spark]');
  if(!box) return;
  const dados = kpiSparks[Number(box.dataset.spark)];
  if(!dados) return;
  const r = box.getBoundingClientRect();
  const n = dados.vals.length;
  const i = Math.max(0, Math.min(n-1, Math.round((e.clientX - r.left) / r.width * (n-1))));
  const px = i / Math.max(1, n-1) * r.width;
  const min = Math.min(...dados.vals), max = Math.max(...dados.vals), rng = max - min || 1;
  const py = 3 + (1 - (dados.vals[i]-min)/rng) * (r.height - 6);
  const hov = box.querySelector('.spark-hover');
  hov.style.opacity = 1;
  hov.querySelector('.sh-line').style.left = px + 'px';
  const dot = hov.querySelector('.sh-dot');
  dot.style.left = px + 'px'; dot.style.top = py + 'px';
  const tip = hov.querySelector('.sh-tip');
  tip.textContent = `${dados.labels[i]} · ${money(dados.vals[i])}`;
  const tw = tip.offsetWidth;
  tip.style.left = Math.max(0, Math.min(r.width - tw, px - tw/2)) + 'px';
}
function onSparkLeave(){
  document.querySelectorAll('.spark-hover').forEach(h=> h.style.opacity = 0);
}

/* =========================================================
   RENDER: VISÃO GERAL
   ========================================================= */
function renderOverview(){
  const P = periodoInfo();
  const mapa = mapaDiario();
  const atualP = somaDias(P.dias, P.ateHora, mapa);
  const antP = somaDias(P.antDias, P.ateHora, mapa);
  const totalGasto = atualP.gasto;
  const totalReceita = atualP.receita;
  const roasGeral = totalGasto>0 ? totalReceita/totalGasto : 0;
  const roiGeral = totalGasto>0 ? ((totalReceita-totalGasto)/totalGasto)*100 : 0;
  const roasAnt = antP.gasto>0 ? antP.receita/antP.gasto : 0;
  const ativas = state.campanhas.filter(c=>c.status==='ativa');
  const campsTotal = state.campanhas.filter(c=>(c.orcamentoTipo||'Total') === 'Total');
  const gastoCampanhas = campsTotal.reduce((s,c)=>s+(Number(c.gasto)||0),0);

  // série do período: por hora (1 dia) ou por dia
  const pontos = P.unico
    ? Array.from({length:P.ateHora+1}, (_,h)=>{
        const b = (state.hourly||{})[P.de];
        const g = k => Number(b && b[h] && b[h][k]) || 0;
        return { gasto:g('gasto'), receita:g('receita') };
      })
    : P.dias.map(k=>resumoDia(k, 23, mapa));
  const serie = fn => pontos.map(fn);
  const temBaseP = cobertura(P.antDias, mapa) >= 0.5;
  const varP = (atual, antes) => (temBaseP && antes > 0) ? (atual-antes)/antes*100 : null;
  kpiSparks = [];
  const rotulos = P.unico
    ? pontos.map((_,h)=> String(h).padStart(2,'0') + 'h')
    : P.dias.map(k=> fmtDate(k).slice(0,5));
  const spark = (vals, cor) => {
    if(vals.length < 3 || !vals.some(v=>v!==0)) return `<span class="kpi-sub" style="margin:0;">poucos dados no período</span>`;
    const idx = kpiSparks.push({ vals, labels: rotulos }) - 1;
    const W = 100, H = 30, pad = 3;
    const min = Math.min(...vals), max = Math.max(...vals);
    const rng = max - min || 1;
    const pts = vals.map((v,i)=>[i*(W/(vals.length-1)), pad + (1-(v-min)/rng)*(H-pad*2)]);
    const linha = pts.map((p,i)=>`${i?'L':'M'}${p[0].toFixed(2)},${p[1].toFixed(2)}`).join(' ');
    const id = 'kg' + Math.random().toString(36).slice(2,7);
    return `<!--spark:${idx}--><svg class="kpi-spark" viewBox="0 0 ${W} ${H}" preserveAspectRatio="none" aria-hidden="true">
      <defs><linearGradient id="${id}" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="${cor}" stop-opacity=".28"/><stop offset="100%" stop-color="${cor}" stop-opacity="0"/></linearGradient></defs>
      <path d="${linha} L${W},${H} L0,${H} Z" fill="url(#${id})"/>
      <path d="${linha}" fill="none" stroke="${cor}" stroke-width="1.8" stroke-linejoin="round" stroke-linecap="round" vector-effect="non-scaling-stroke"/>
    </svg>
    <div class="spark-hover" style="--sc:${cor};"><i class="sh-line"></i><i class="sh-dot"></i><span class="sh-tip"></span></div>`;
  };
  const deltaTag = (p, neutro) => {
    if(p === null || !isFinite(p)) return '';
    const cls = (neutro || Math.abs(p) < 0.05) ? 'flat' : p > 0 ? 'up' : 'down';
    const arred = Math.round(p*10)/10;
    const txt = arred === 0 ? '0%' : `${arred>0?'+':'−'}${Math.abs(arred).toLocaleString('pt-BR',{maximumFractionDigits:1})}%`;
    return `<span class="kpi-delta ${arred===0?'flat':cls}" title="${P.vsLabel}">${txt}</span>`;
  };
  // ---------- Resultado do período: lucro e ROI em destaque ----------
  const liq = liquidoDe(totalReceita);
  const liqAnt = liquidoDe(antP.receita);
  const lucro = liq - totalGasto;
  const lucroAnt = liqAnt - antP.gasto;
  const roi = totalGasto > 0 ? lucro / totalGasto * 100 : null;
  const roiAnt = antP.gasto > 0 ? lucroAnt / antP.gasto * 100 : null;
  const roas = totalGasto > 0 ? liq / totalGasto : null;
  const margem = liq > 0 ? lucro / liq * 100 : null;
  const dedPct = Number((state.faturamento || {}).deducaoPct) || 0;

  const pct1 = v => (v > 0 ? '+' : v < 0 ? '−' : '') + Math.abs(v).toLocaleString('pt-BR', { minimumFractionDigits:1, maximumFractionDigits:1 }) + '%';
  const selo = (txt, cls, tip) => `<span class="delta-badge ${cls}" ${tip ? `title="${escapeHtml(tip)}"` : ''} style="height:24px;font-size:12px;padding:0 9px;">${HERO_ICONS[cls]}${txt}</span>`;
  // variação do lucro: compara com o módulo do período anterior (funciona mesmo saindo de prejuízo)
  let dLucroHtml = '';
  if(temBaseP && lucroAnt !== 0){
    const d = (lucro - lucroAnt) / Math.abs(lucroAnt) * 100;
    const cls = Math.abs(d) < 0.05 ? 'flat' : d > 0 ? 'up' : 'down';
    dLucroHtml = selo(pct1(d), cls, P.vsLabel);
  }
  let dRoiHtml = '';
  if(temBaseP && roi !== null && roiAnt !== null){
    const pp = roi - roiAnt;
    const cls = Math.abs(pp) < 0.05 ? 'flat' : pp > 0 ? 'up' : 'down';
    dRoiHtml = selo(`${pp > 0 ? '+' : pp < 0 ? '−' : ''}${Math.abs(pp).toLocaleString('pt-BR',{maximumFractionDigits:1})} p.p.`, cls, `${P.vsLabel} (pontos percentuais)`);
  }
  const corLucro = lucro > 0 ? 'pos' : lucro < 0 ? 'neg' : 'neu';
  const nivelRoi = roi === null ? null : roi >= 50 ? ['bom','saudável'] : roi >= 0 ? ['ok','apertado'] : ['ruim','no prejuízo'];
  const vsTxt = temBaseP ? P.vsLabel : '';

  // barra: faturamento líquido dividido entre gastos e lucro (ou gastos cobertos + prejuízo)
  let barra, eqFim;
  if(liq <= 0 && totalGasto <= 0){
    barra = '';
  } else if(lucro >= 0){
    const g = totalGasto / liq * 100;
    barra = `<span style="width:${g.toFixed(2)}%;background:var(--chart-muted);" title="Gastos: ${g.toLocaleString('pt-BR',{maximumFractionDigits:1})}% do faturamento"></span>
             <span style="width:${(100-g).toFixed(2)}%;background:var(--ok);" title="Lucro: ${(100-g).toLocaleString('pt-BR',{maximumFractionDigits:1})}% do faturamento"></span>`;
  } else {
    const f = liq / totalGasto * 100;
    barra = `<span style="width:${f.toFixed(2)}%;background:var(--chart-muted);" title="Gastos cobertos pelo faturamento: ${f.toLocaleString('pt-BR',{maximumFractionDigits:1})}%"></span>
             <span style="width:${(100-f).toFixed(2)}%;background:var(--bad);" title="Prejuízo: ${(100-f).toLocaleString('pt-BR',{maximumFractionDigits:1})}% dos gastos"></span>`;
  }
  eqFim = lucro >= 0 ? 'Lucro' : 'Prejuízo';
  const partGasto = liq > 0 ? `${(totalGasto/liq*100).toLocaleString('pt-BR',{maximumFractionDigits:1})}% do faturamento` : '';
  const serieLucro = serie(d=>liquidoDe(d.receita) - d.gasto);

  const kpis = `
  <div class="g-card res-card o-kpi">
    <div class="res-head">
      <h3 class="card-title">Resultado <span class="card-meta">· ${P.nome}</span></h3>
      <span class="g2-i" style="width:15px;height:15px;" title="Lucro = faturamento líquido − gastos com anúncios. ROI = lucro ÷ gastos. Faturamento líquido = bruto − ${dedPct.toLocaleString('pt-BR')}% (taxas, descontos e reembolsos — ajuste no card de Faturamento).">${G2_ICONS.info}</span>
    </div>
    <div class="res-top">
      <div class="res-bloco">
        <div class="res-lbl"><span class="res-dot" style="background:${lucro >= 0 ? 'var(--ok)' : 'var(--bad)'};"></span>${lucro >= 0 ? 'Lucro' : 'Prejuízo'}</div>
        <div class="res-val ${corLucro}">${money(lucro)}</div>
        <div class="res-linha">${dLucroHtml}${dLucroHtml ? `<span>${vsTxt}</span>` : '<span>sem período anterior pra comparar</span>'}</div>
        <div class="res-spark kpi-viz" ${(()=>{ const h = spark(serieLucro, lucro >= 0 ? '#34d399' : '#ff6b7a'); const m = /<!--spark:(\d+)-->/.exec(h); return (m ? `data-spark="${m[1]}"` : '') + '>' + h; })()}</div>
      </div>
      <div class="res-bloco">
        <div class="res-lbl"><span class="res-dot" style="background:var(--accent-2);"></span>ROI</div>
        <div class="res-val ${roi === null ? 'neu' : roi >= 0 ? 'pos' : 'neg'}">${roi === null ? '—' : pct1(roi)}</div>
        <div class="res-linha">${dRoiHtml}${nivelRoi ? `<span class="res-selo ${nivelRoi[0]}">${nivelRoi[1]}</span>` : ''}</div>
        <div class="res-frase">${roas === null
          ? 'Sem gastos com anúncios no período.'
          : `Cada <b>${simboloMoeda()} 1,00</b> investido voltou <b>${ocultarValores ? `${simboloMoeda()} •••` : `${simboloMoeda()} ` + roas.toLocaleString('pt-BR',{minimumFractionDigits:2,maximumFractionDigits:2})}</b> em faturamento líquido${margem !== null ? ` · margem de <b>${margem.toLocaleString('pt-BR',{maximumFractionDigits:1})}%</b>` : ''}.`}</div>
      </div>
    </div>
    <div class="res-conta">
      ${barra ? `<div class="res-barra" role="img" aria-label="Divisão do faturamento entre gastos e lucro">${barra}</div>` : ''}
      <div class="res-eq">
        <div class="res-eq-item"><div class="t"><i style="background:var(--accent-2);"></i>Faturamento líquido</div><div class="v">${money(liq)}</div><div class="s">bruto ${money(totalReceita)}</div></div>
        <div class="res-eq-op">−</div>
        <div class="res-eq-item"><div class="t"><i style="background:var(--chart-muted);"></i>Gastos com anúncios</div><div class="v">${money(totalGasto)}</div><div class="s">${partGasto}${roas !== null ? ` · ROAS ${roas.toLocaleString('pt-BR',{minimumFractionDigits:2,maximumFractionDigits:2})}` : ''}</div></div>
        <div class="res-eq-op eq-igual">=</div>
        <div class="res-eq-item"><div class="t"><i style="background:${lucro >= 0 ? 'var(--ok)' : 'var(--bad)'};"></i>${eqFim}</div><div class="v" style="color:${lucro >= 0 ? 'var(--ok)' : 'var(--bad)'};">${money(lucro)}</div><div class="s">${margem !== null ? `margem ${margem.toLocaleString('pt-BR',{maximumFractionDigits:1})}%` : ''}</div></div>
      </div>
    </div>
  </div>`;

  // Fases das campanhas ativas (idade + ROI)
  const hoje = new Date();
  const fases = { aprendizado:0, otimizando:0, escalando:0, estavel:0 };
  ativas.forEach(c=>{
    const m = campaignMetrics(c);
    const dias = c.dataInicio ? Math.floor((hoje - new Date(c.dataInicio+'T00:00:00'))/86400000) : 999;
    if(dias < 4) fases.aprendizado++;
    else if(m.roiPct < 0) fases.otimizando++;
    else if(m.roiPct < 80) fases.escalando++;
    else fases.estavel++;
  });
  const faseItem = (lbl, dica, n, cor) => `
    <li title="${dica}"><span class="dot" style="background:${cor};"></span>
      <span class="lbl">${lbl}</span><span class="n ${n?'':'zero'}">${n}</span></li>`;
  const fasesCard = `
  <div class="g-card side-card o-phases">
    <div class="card-head"><h3 class="card-title">Fases das campanhas</h3><span class="card-meta">${ativas.length} ativa(s)</span></div>
    <ul class="side-list">
      ${faseItem('Pra escalar', 'ROI entre 0% e 80%', fases.escalando, '#4b84ff')}
      ${faseItem('Estáveis', 'ROI acima de 80%', fases.estavel, '#34d399')}
      ${faseItem('Precisam de ajuste', 'ROI negativo', fases.otimizando, '#ff6b7a')}
      ${faseItem('Em aprendizado', 'Menos de 4 dias rodando', fases.aprendizado, '#6d6e7c')}
    </ul>
  </div>`;

  // Alertas (os mais graves primeiro, no máximo 4)
  const alerts = [];
  const add = (nivel, texto, sub) => alerts.push({ nivel, texto, sub });
  state.bms.filter(b=>b.status==='banido').forEach(b=> add(2, `BM ${escapeHtml(b.nome)} banida`, escapeHtml(b.plataforma)));
  state.contas.filter(c=>c.status==='desativada').forEach(c=> add(2, `Conta ${escapeHtml(c.nome)} desativada`, escapeHtml(c.cliente||'')));
  state.rejeitados.filter(r=>r.status==='em recurso').forEach(r=> add(2, `Anúncio em recurso`, escapeHtml(r.nomeAnuncio)));
  state.campanhas.filter(c=>c.status==='ativa').forEach(c=>{
    const m = campaignMetrics(c);
    const dias = c.dataInicio ? Math.floor((new Date() - new Date(c.dataInicio+'T00:00:00'))/86400000) : 999;
    if(dias >= 4 && m.roiPct <= -20 && (Number(c.gasto)||0) >= 100)
      add(2, `ROI ${m.roiPct.toLocaleString('pt-BR',{maximumFractionDigits:0})}% em campanha`, escapeHtml(c.nome));
  });
  state.contas.forEach(c=>{
    const ritmo = contaRitmoGasto(c);
    if(!ritmo) return;
    if(ritmo.pctLimite >= 100) add(2, `Conta acima do limite diário`, escapeHtml(c.nome));
    else if(ritmo.pctLimite >= 85) add(1, `Conta perto do limite diário`, escapeHtml(c.nome));
  });
  state.contas.filter(c=>c.status==='em revisão').forEach(c=> add(1, `Conta em revisão`, escapeHtml(c.nome)));
  state.bms.filter(b=>b.status==='em revisão').forEach(b=> add(1, `BM em revisão`, escapeHtml(b.nome)));
  alerts.sort((a,b)=>b.nivel-a.nivel);
  const extra = alerts.length > 4 ? alerts.length - 4 : 0;
  const alertsCard = `
  <div class="g-card side-card o-alerts">
    <div class="card-head"><h3 class="card-title">Alertas</h3><span class="card-meta">${alerts.length ? alerts.length : ''}</span></div>
    ${alerts.length ? `<ul class="side-list">${alerts.slice(0,4).map(a=>`
      <li><span class="dot" style="background:${a.nivel===2?'#ff6b7a':'#f2b24a'};"></span>
        <span class="lbl">${a.texto}<small>${a.sub}</small></span></li>`).join('')}</ul>
      ${extra ? `<div class="side-empty">+ ${extra} outro(s) nas abas BMs, Contas e Rejeitados</div>` : ''}`
    : `<div class="side-empty">Nenhum alerta. Tudo sob controle.</div>`}
  </div>`;

  return `
  <div class="ov-grid">
    <div class="ov-col">
      ${renderFaturamentoCard()}
      ${kpis}
      ${renderEvolutionCard()}
    </div>
    <div class="ov-col">
      ${renderPeriodBar()}
      ${renderSaudeCard()}
      ${alertsCard}
      ${fasesCard}
    </div>
  </div>`;
}

/* =========================================================
   RENDER: BMS
   ========================================================= */
function renderBms(){
  const reais = (state.bms || []).filter((b) => b.facebookId);
  const pg = paginate(reais, listPage.bms, LIST_PAGE_SIZE);
  listPage.bms = pg.page;
  return renderBmsView({
    bms: reais,
    contas: state.contas,
    page: listPage.bms,
  });
}

/* =========================================================
   RENDER: CONTAS
   ========================================================= */
function renderContas(){
  const filter = listFilter.contas || 'todas';
  const list = state.contas.filter(c=> filter==='todas' || c.status===filter);
  const pg = paginate(list, listPage.contas, LIST_PAGE_SIZE);
  listPage.contas = pg.page;
  const toolbar = `
    <div class="table-toolbar">
      <select class="filter" id="contas-filter">
        <option value="todas" ${filter==='todas'?'selected':''}>Todos os status</option>
        <option value="ativa" ${filter==='ativa'?'selected':''}>Ativas</option>
        <option value="em revisão" ${filter==='em revisão'?'selected':''}>Em revisão</option>
        <option value="desativada" ${filter==='desativada'?'selected':''}>Desativadas</option>
      </select>
      <span class="cell-sub">${pg.total} conta(s)</span>
    </div>`;
  const rows = pg.total ? pg.items.map(c=>{
    const ritmo = contaRitmoGasto(c);
    let ritmoCell = `<span class="cell-sub">sem dado suficiente</span>`;
    if(ritmo){
      const cor = ritmo.pctLimite>=100 ? 'var(--red)' : ritmo.pctLimite>=85 ? 'var(--gold)' : 'var(--green)';
      ritmoCell = `<span class="mono" style="color:${cor};font-size:12.5px;">${moneyConta(ritmo.mediaDiaria, moedaConta(c))}/dia</span><div class="cell-sub">${ritmo.pctLimite.toLocaleString('pt-BR',{maximumFractionDigits:0})}% do limite</div>`;
    }
    return `<tr>
      <td><div class="cell-title">${escapeHtml(c.nome)}</div><div class="cell-sub">${escapeHtml(c.cliente||'—')}</div></td>
      <td>${escapeHtml(bmNome(c.bmId))}</td>
      <td>${escapeHtml(c.plataforma)}</td>
      <td class="num">${moneyConta(c.limiteGasto, moedaConta(c))}/dia</td>
      <td>${ritmoCell}</td>
      <td><span class="pill ${statusPillClass(c.status)}">${c.status}</span></td>
      <td>
        <div class="row-actions">
          <button class="icon-btn" data-action="edit" data-type="contas" data-id="${c.id}">editar</button>
          <button class="icon-btn danger" data-action="delete" data-type="contas" data-id="${c.id}">excluir</button>
        </div>
      </td>
    </tr>`;
  }).join('') : `<tr><td colspan="7" class="empty-state">Nenhuma conta encontrada.</td></tr>`;

  return `${toolbar}<div class="panel-box"><div class="table-scroll"><table>
    <thead><tr><th>Conta</th><th>BM</th><th>Plataforma</th><th>Limite diário</th><th>Ritmo de gasto</th><th>Status</th><th></th></tr></thead>
    <tbody>${rows}</tbody>
  </table></div></div>
  ${pg.total ? renderPager({ id:'contas', page:pg.page, totalPages:pg.totalPages, from:pg.from, to:pg.to, total:pg.total, label:'conta(s)', hrefFor: (n) => buildHref({ section:'contas', page:n }) }) : ''}`;
}

/* =========================================================
   RENDER: CAMPANHAS
   ========================================================= */
/* =========================================================
   HISTÓRICO POR ITEM (campanhas, conjuntos, anúncios)
   Uma "foto" por dia dos acumulados de cada item. Os valores de um
   período = acumulado no fim − acumulado antes do início.
   ========================================================= */
const HIST_KEYS = { g:'gasto', r:'receita', v:'conversoes', k:'cliques', i:'impressoes', c:'checkouts', p:'visualizacoes' };
const ZERO_M = { gasto:0, receita:0, conversoes:0, cliques:0, impressoes:0, checkouts:0, visualizacoes:0 };
const NIVEIS_DADOS = ['campanhas','conjuntos','anuncios'];

function snapshotItens(){
  const hoje = localDateKey();
  NIVEIS_DADOS.forEach(tipo=>{
    (state[tipo]||[]).forEach(it=>{
      if(!Array.isArray(it.hist)) it.hist = [];
      const snap = { d: hoje };
      for(const k in HIST_KEYS) snap[k] = Number(it[HIST_KEYS[k]]) || 0;
      const last = it.hist[it.hist.length-1];
      if(last && last.d === hoje) Object.assign(last, snap); else it.hist.push(snap);
      if(it.hist.length > 100) it.hist = it.hist.slice(-100);
    });
  });
}

// Histórico fictício (só pros dados de exemplo): espalha o acumulado pelos dias
function gerarHistItem(it, dias){
  const hoje = localDateKey();
  const limite = addDays(hoje, -(dias||95));
  let ini = it.dataInicio || limite;
  if(ini < limite) ini = limite;
  if(ini > hoje) ini = hoje;
  const n = diasEntre(ini, hoje) + 1;
  const pesos = Array.from({length:n}, (_,i)=> (0.6 + Math.random()*0.8) * (i === n-1 ? 0.55 : 1));
  const tot = pesos.reduce((a,b)=>a+b, 0);
  let acc = 0;
  it.hist = pesos.map((w,i)=>{
    acc += w;
    const f = acc / tot;
    const s = { d: addDays(ini, i) };
    for(const k in HIST_KEYS){
      const v = Number(it[HIST_KEYS[k]]) || 0;
      s[k] = (k === 'g' || k === 'r') ? Math.round(v*f*100)/100 : Math.round(v*f);
    }
    return s;
  }).slice(-100);
}

function valoresAtuais(it){ const o = {}; for(const k in ZERO_M) o[k] = Number(it[k]) || 0; return o; }
function valoresSnap(s){ const o = {}; for(const k in HIST_KEYS) o[HIST_KEYS[k]] = Number(s[k]) || 0; return o; }

function metricasPeriodo(it, per){
  if(!per || per.tipo === 'max') return valoresAtuais(it);
  const P = periodoInfo(per);
  const hoje = localDateKey();
  const h = Array.isArray(it.hist) ? it.hist : [];
  let fim;
  if(P.ate >= hoje) fim = valoresAtuais(it);
  else {
    let s = null;
    for(const x of h){ if(x.d <= P.ate) s = x; }
    fim = s ? valoresSnap(s) : { ...ZERO_M };
  }
  let b = null;
  for(const x of h){ if(x.d < P.de) b = x; }
  const base = b ? valoresSnap(b) : { ...ZERO_M };
  const o = {};
  for(const k in ZERO_M) o[k] = Math.max(0, fim[k] - base[k]);
  return o;
}

// Guarda a "última atualização" recebida de um item (planilha, API ou edição)
function marcarAtualizado(it){
  it.ult = {
    em: new Date().toISOString(),
    gasto: Number(it.gasto) || 0,
    receita: Number(it.receita) || 0,
    vendas: Number(it.conversoes) || 0
  };
  state.sincronizadoEm = it.ult.em;
}

/* =========================================================
   RENDER: CAMPANHAS (gerenciador em tabela)
   ========================================================= */

let g2 = { nivel:'campanhas', busca:'', status:'', periodo:{ tipo:'hoje' }, conta:'', produto:'', pagina:1, soSugestao:false, soNaoTrack:false, sel:new Set() };
let g2Ocultas = new Set();
try{
  const s = JSON.parse(localStorage.getItem('torre:g2') || 'null');
  if(s){
    if(s.periodo && ['hoje','ontem','7d','30d','90d','max'].includes(s.periodo.tipo)) g2.periodo = s.periodo;
    if(['contas','campanhas','conjuntos','anuncios'].includes(s.nivel)) g2.nivel = s.nivel;
  }
  g2Ocultas = new Set(JSON.parse(localStorage.getItem('torre:g2cols') || '[]'));
}catch(e){}
function salvarG2(){
  try{
    localStorage.setItem('torre:g2', JSON.stringify({ periodo:g2.periodo, nivel:g2.nivel }));
    localStorage.setItem('torre:g2cols', JSON.stringify([...g2Ocultas]));
  }catch(e){}
}

const G2_PERIODOS = [['hoje','Hoje'],['ontem','Ontem'],['7d','Últimos 7 dias'],['30d','Últimos 30 dias'],['90d','Últimos 90 dias'],['max','Máximo']];
const G2_NIVEIS = [['contas','Contas'],['campanhas','Campanhas'],['conjuntos','Conjuntos'],['anuncios','Anúncios']];
const G2_SING = { contas:'Conta', campanhas:'Campanha', conjuntos:'Conjunto', anuncios:'Anúncio' };
const G2_NOME = { contas:'Nome da Conta', campanhas:'Nome da Campanha', conjuntos:'Nome do Conjunto', anuncios:'Nome do Anúncio' };
const G2_STATUS = { contas:'Status da Conta', campanhas:'Status da Campanha', conjuntos:'Status do Conjunto', anuncios:'Status do Anúncio' };
const G2_PAG = 5;
const PAGE_PAD = 21.5;

const numBR = (v, casas=2) => (Number(v)||0).toLocaleString('pt-BR', { minimumFractionDigits:casas, maximumFractionDigits:casas });
const sinalCor = (v) => v > 0 ? 'g2-pos' : v < 0 ? 'g2-neg' : '';

// Colunas de métricas (as fixas — seleção, status, nome, orçamento, sugestão,
// últ. atualização — ficam fora desta lista)
const liqL = l => liquidoDe(l.m.receita);
const G2_COLS = [
  { id:'vendas', label:'VENDAS', w:98,  v:l=>fmtNum(l.m.conversoes), csv:l=>l.m.conversoes },
  { id:'cpa', label:'CPA', w:98, info:'Custo por venda: gastos ÷ vendas',
    v:l=> l.m.conversoes ? fmtValor(l.m.gasto/l.m.conversoes) : 'N/A', csv:l=> l.m.conversoes ? numBR(l.m.gasto/l.m.conversoes) : '' },
  { id:'gastos', label:'GASTOS', w:112, v:l=>fmtValor(l.m.gasto), csv:l=>numBR(l.m.gasto) },
  { id:'faturamento', label:'FATURAMENTO', w:150, info:'Faturamento líquido: vendas − taxas, descontos e reembolsos (% ajustável no card de Faturamento)',
    v:l=>fmtValor(liqL(l)), csv:l=>numBR(liqL(l)) },
  { id:'lucro', label:'LUCRO', w:124, info:'Faturamento líquido − gastos',
    v:l=>fmtValor(liqL(l)-l.m.gasto), cls:l=>sinalCor(liqL(l)-l.m.gasto), csv:l=>numBR(liqL(l)-l.m.gasto) },
  { id:'roas', label:'ROAS', w:104, info:'Faturamento líquido ÷ gastos',
    v:l=> l.m.gasto ? numBR(liqL(l)/l.m.gasto) : 'N/A', cls:l=> l.m.gasto ? (liqL(l)/l.m.gasto >= 1 ? 'g2-pos' : 'g2-neg') : '',
    csv:l=> l.m.gasto ? numBR(liqL(l)/l.m.gasto) : '' },
  { id:'margem', label:'MARGEM', w:122, info:'Lucro ÷ faturamento',
    v:l=> liqL(l) ? numBR((liqL(l)-l.m.gasto)/liqL(l)*100)+'%' : 'N/A', cls:l=> liqL(l) ? sinalCor(liqL(l)-l.m.gasto) : '',
    csv:l=> liqL(l) ? numBR((liqL(l)-l.m.gasto)/liqL(l)*100) : '' },
  { id:'roi', label:'ROI', w:108, info:'Lucro ÷ gastos',
    v:l=> l.m.gasto ? numBR((liqL(l)-l.m.gasto)/l.m.gasto*100)+'%' : 'N/A', cls:l=> l.m.gasto ? sinalCor(liqL(l)-l.m.gasto) : '',
    csv:l=> l.m.gasto ? numBR((liqL(l)-l.m.gasto)/l.m.gasto*100) : '' },
  { id:'ic', label:'IC', w:89, info:'Inícios de checkout', v:l=>fmtNum(l.m.checkouts), csv:l=>l.m.checkouts },
  { id:'cpi', label:'CPI', w:95, info:'Custo por início de checkout',
    v:l=> l.m.checkouts ? fmtValor(l.m.gasto/l.m.checkouts) : 'N/A', csv:l=> l.m.checkouts ? numBR(l.m.gasto/l.m.checkouts) : '' },
  { id:'cpc', label:'CPC', w:95, info:'Custo por clique no link',
    v:l=> l.m.cliques ? fmtValor(l.m.gasto/l.m.cliques) : 'N/A', csv:l=> l.m.cliques ? numBR(l.m.gasto/l.m.cliques) : '' },
  { id:'ctr', label:'CTR', w:92, info:'Cliques no link ÷ impressões',
    v:l=> l.m.impressoes ? numBR(l.m.cliques/l.m.impressoes*100)+'%' : 'N/A', csv:l=> l.m.impressoes ? numBR(l.m.cliques/l.m.impressoes*100) : '' },
  { id:'cpm', label:'CPM', w:100, info:'Custo por mil impressões',
    v:l=> l.m.impressoes ? fmtValor(l.m.gasto/l.m.impressoes*1000) : 'N/A', csv:l=> l.m.impressoes ? numBR(l.m.gasto/l.m.impressoes*1000) : '' },
  { id:'cliques', label:'CLIQUES', w:98, v:l=>fmtNum(l.m.cliques), csv:l=>l.m.cliques },
  { id:'impressoes', label:'IMPRESSÕES', w:120, v:l=>fmtNum(l.m.impressoes), csv:l=>l.m.impressoes }
];
const G2_FIXAS_OPC = [['orcamento','ORÇAMENTO'],['sugestao','SUGESTÃO']];
const g2Vis = id => !g2Ocultas.has(id);

// Sugestão de orçamento (subir 30% / revisar) — mesma regra de antes
function contextoSugestao(){
  const ativas = state.campanhas.filter(c=>c.status==='ativa' && (Number(c.conversoes)||0)>0);
  const avgCpa = ativas.length ? ativas.reduce((s,c)=>s+campaignMetrics(c).cpa,0)/ativas.length : 0;
  return { avgCpa };
}
function sugestaoCampanha(c, ctx){
  if(c.status !== 'ativa') return null;
  const m = campaignMetrics(c);
  const cpaBarato = ctx.avgCpa>0 && m.cpa>0 && m.cpa < ctx.avgCpa*0.85;
  if(m.roiPct >= 80 && cpaBarato){
    const base = Number(c.orcamento)||Number(c.gasto)||0;
    const novo = base*1.3;
    return { tipo:'subir', txt:`↑ Subir 30% · ${moneyConta(novo, moedaCampanha(c))}`,
      tip:`ROI de ${m.roiPct.toLocaleString('pt-BR',{maximumFractionDigits:1})}% e CPA de ${moneyConta(m.cpa, moedaCampanha(c))} — ${((1-(m.cpa/ctx.avgCpa))*100).toLocaleString('pt-BR',{maximumFractionDigits:0})}% mais barato que a média das ativas (${moneyConta(ctx.avgCpa, moedaCampanha(c))}). Sugestão: subir o orçamento de ${moneyConta(base, moedaCampanha(c))} para ${moneyConta(novo, moedaCampanha(c))}.` };
  }
  const dias = c.dataInicio ? Math.floor((new Date() - new Date(c.dataInicio+'T00:00:00'))/86400000) : 999;
  if(dias>=4 && m.roiPct<=-20 && (Number(c.gasto)||0)>=100){
    return { tipo:'pausar', txt:'⏸ Revisar/pausar',
      tip:`ROI de ${m.roiPct.toLocaleString('pt-BR',{maximumFractionDigits:1})}% depois de ${dias} dia(s) rodando, já com ${moneyConta(c.gasto, moedaCampanha(c))} gastos. Considere pausar, trocar criativo ou revisar o público.` };
  }
  return null;
}

// Venda sem campanha de anúncio ligada (ex.: UTM da Shopify que não bateu com nenhuma conta)
const naoTrackeada = c => c && !c.contaId && (Number(c.receita)||0) > 0 && !((Number(c.gasto)||0) > 0);

function campanhaDoItem(it){
  return state.campanhas.find(c=>c.id===it.campanhaId)
      || state.campanhas.find(c=>(c.nome||'').trim().toLowerCase() === String(it.campanha||'').trim().toLowerCase());
}

function g2Linhas(){
  const per = g2.periodo;
  const ctx = contextoSugestao();
  const passaCamp = c => (!g2.conta || c.contaId === g2.conta) && (!g2.produto || (c.produto||'') === g2.produto);
  let linhas = [];
  if(g2.nivel === 'campanhas'){
    linhas = state.campanhas.filter(passaCamp).map(c=>{
      const origem = moedaCampanha(c);
      const bruto = metricasPeriodo(c, per);
      return {
      tipo:'campanhas', id:c.id, nome:c.nome, item:c,
      ativo: c.status === 'ativa', podeLigar: true,
      orc: paraBase(Number(c.orcamento)||0, origem), orcTipo: c.orcamentoTipo || 'Total',
      m: { ...bruto, gasto: paraBase(bruto.gasto, origem), receita: paraBase(bruto.receita, origem) }, ult: c.ult || null,
      sug: sugestaoCampanha(c, ctx), naoTrack: naoTrackeada(c)
    };});
  } else if(g2.nivel === 'contas'){
    linhas = state.contas.filter(ct=>!g2.conta || ct.id === g2.conta).map(ct=>{
      const camps = state.campanhas.filter(c=>c.contaId === ct.id && (!g2.produto || (c.produto||'') === g2.produto));
      const m = { ...ZERO_M };
      camps.forEach(c=>{
        const mm = metricasPeriodo(c, per);
        const origem = moedaCampanha(c);
        for(const k in m) m[k] += (k === 'gasto' || k === 'receita') ? paraBase(mm[k], origem) : mm[k];
      });
      const ults = camps.map(c=>c.ult).filter(Boolean);
      const ult = ults.length ? {
        em: ults.map(u=>u.em).sort().pop(),
        gasto: ults.reduce((s,u)=>s+u.gasto,0), receita: ults.reduce((s,u)=>s+u.receita,0), vendas: ults.reduce((s,u)=>s+u.vendas,0)
      } : null;
      return { tipo:'contas', id:ct.id, nome:ct.nome, item:ct, ativo: ct.status === 'ativa', podeLigar: true,
        orc: paraBase(Number(ct.limiteGasto)||0, moedaConta(ct)), orcTipo:'Diário', m, ult, sug:null, naoTrack:false };
    });
  } else {
    linhas = (state[g2.nivel]||[]).filter(it=>{
      if(!g2.conta && !g2.produto) return true;
      const c = campanhaDoItem(it);
      return c && passaCamp(c);
    }).map(it=>{
      const origem = moedaCampanha(campanhaDoItem(it) || {});
      const bruto = metricasPeriodo(it, per);
      return {
      tipo:g2.nivel, id:it.id, nome:it.nome, item:it,
      ativo: it.status === 'ativa' || it.status === 'ativo', podeLigar: true,
      orc: paraBase(Number(it.orcamento)||0, origem), orcTipo: it.orcamentoTipo || 'Diário',
      m: { ...bruto, gasto: paraBase(bruto.gasto, origem), receita: paraBase(bruto.receita, origem) }, ult: it.ult || null, sug:null, naoTrack:false
    };});
  }
  if(g2.busca){
    const q = g2.busca.trim().toLowerCase();
    linhas = linhas.filter(l=>(l.nome||'').toLowerCase().includes(q));
  }
  if(g2.status === 'on') linhas = linhas.filter(l=>l.ativo);
  if(g2.status === 'off') linhas = linhas.filter(l=>!l.ativo);
  if(g2.soSugestao) linhas = linhas.filter(l=>l.sug);
  if(g2.soNaoTrack) linhas = linhas.filter(l=>l.naoTrack);
  return linhas;
}

function g2Th(label, info, extra){
  return `<th${extra||''}><span class="g2-th">${label}${info ? `<span class="g2-i" title="${escapeHtml(info)}">${G2_ICONS.info}</span>` : ''}</span></th>`;
}
const contaNomeDe = id => { const c = state.contas.find(x=>x.id===id); return c ? c.nome : ''; };
const metaLigado = () => { const i = state.integracao || {}; return !!(i.ativo && i.url); };
const facebookLigado = () => (state.bms || []).some((b) => b.facebookId);

function renderCampanhas(){
  const nivel = g2.nivel;
  const todas = g2Linhas();
  const totalPag = Math.max(1, Math.ceil(todas.length / G2_PAG));
  if(g2.pagina > totalPag) g2.pagina = totalPag;
  if(g2.pagina < 1) g2.pagina = 1;
  const ini = todas.length ? (g2.pagina-1)*G2_PAG + 1 : 0;
  const fim = Math.min(todas.length, g2.pagina*G2_PAG);
  const linhas = todas.slice(ini ? ini-1 : 0, fim);
  // limpa seleção de itens que sumiram
  const idsNivel = new Set(todas.map(l=>l.id));
  [...g2.sel].forEach(id=>{ if(!idsNivel.has(id)) g2.sel.delete(id); });

  // aviso de vendas não trackeadas (só faz sentido em campanhas)
  const naoTrack = nivel === 'campanhas' ? state.campanhas.filter(naoTrackeada) : [];
  const vendasNT = naoTrack.reduce((s,c)=>s+(Number(c.conversoes)||0),0);
  const receitaNT = naoTrack.reduce((s,c)=>s+(Number(c.receita)||0),0);
  const badge = naoTrack.length ? `
    <button type="button" class="g2-badge ${g2.soNaoTrack?'on':''}" data-g2="naotrack"
      title="Vendas da Shopify que não bateram com nenhuma campanha de anúncio (${money(receitaNT)}). Clique pra ${g2.soNaoTrack?'mostrar todas':'ver só essas'}.">
      ${G2_ICONS.warn}${vendasNT > 0 ? `${fmtNum(vendasNT)} vendas não trackeadas` : `${money(receitaNT)} em vendas não trackeadas`}
    </button>` : '';
  const nSug = nivel === 'campanhas' ? todas.filter(l=>l.sug).length : 0;

  const tabs = `
  <div class="g2-tabs" role="tablist">
    ${G2_NIVEIS.map(([id,label])=>`
      <a href="${buildHref({ section:'campanhas', g2Level:id, page:1 })}" role="tab" aria-selected="${nivel===id}" class="g2-tab ${nivel===id?'on':''}" data-g2="nivel" data-v="${id}">
        ${G2_ICONS[id]}<span>${label}</span>
      </a>`).join('')}
  </div>`;

  const toolbar = `
  <div class="g2-toolbar">
    <div class="g2-left">
      <div class="g2-btns">
        <button type="button" class="g2-btn" data-g2="cols" title="Escolher colunas" aria-label="Escolher colunas">${G2_ICONS.gear}</button>
        <button type="button" class="g2-btn" data-g2="export" title="Exportar tabela (CSV)" aria-label="Exportar tabela">${G2_ICONS.up}</button>
        <button type="button" class="g2-btn ${g2.soSugestao?'on':''}" data-g2="sug" aria-pressed="${g2.soSugestao}"
          title="${nivel==='campanhas' ? (g2.soSugestao ? 'Mostrar todas as campanhas' : `Mostrar só campanhas com sugestão (${nSug})`) : 'Sugestões aparecem na aba Campanhas'}" aria-label="Filtrar por sugestão">
          ${G2_ICONS.pulse}${nSug && !g2.soSugestao ? '<i class="g2-dot"></i>' : ''}
        </button>
        <button type="button" class="g2-btn g2-btn-dark" data-g2="mais" title="Mais ações" aria-label="Mais ações">${G2_ICONS.down}</button>
      </div>
      ${badge}
      ${g2.sel.size ? `<span class="g2-selinfo">${g2.sel.size} selecionado(s)</span>` : ''}
    </div>
    <button type="button" class="g2-conn ${(metaLigado() || facebookLigado())?'on':''}" data-g2="integracao" title="${metaLigado() ? 'Alterações de status e orçamento vão pro Meta Ads' : facebookLigado() ? 'Facebook conectado. Pausar, ativar e mudar orçamento da campanha também muda no Facebook.' : 'Alterações ficam só no painel.'}">
      <i></i>${metaLigado() ? 'Meta Ads conectado' : facebookLigado() ? 'Facebook conectado' : 'Meta Ads desconectado'}
    </button>
    <div class="g2-right">
      <span class="g2-pag">${ini}-${fim} de ${todas.length}</span>
      <div class="g2-pgbtns">
        ${g2.pagina<=1
          ? `<span class="g2-pg" aria-disabled="true" aria-label="Página anterior">${G2_ICONS.left}</span>`
          : `<a href="${buildHref({ section:'campanhas', g2Level:nivel, page:g2.pagina-1 })}" class="g2-pg" data-g2="prev" aria-label="Página anterior">${G2_ICONS.left}</a>`}
        ${pageNumbers(g2.pagina, totalPag).map(n => n === '…'
          ? `<span class="g2-pg-ellipsis">…</span>`
          : `<a href="${buildHref({ section:'campanhas', g2Level:nivel, page:n })}" class="g2-pg g2-pg-num ${n===g2.pagina?'on':''}" data-g2="page" data-v="${n}" aria-label="Página ${n}" aria-current="${n===g2.pagina?'page':'false'}">${n}</a>`
        ).join('')}
        ${g2.pagina>=totalPag
          ? `<span class="g2-pg" aria-disabled="true" aria-label="Próxima página">${G2_ICONS.right}</span>`
          : `<a href="${buildHref({ section:'campanhas', g2Level:nivel, page:g2.pagina+1 })}" class="g2-pg g2-pg-next" data-g2="next" aria-label="Próxima página">${G2_ICONS.right}</a>`}
      </div>
      <input type="text" class="g2-pgbox" id="g2-pagina" inputmode="numeric" aria-label="Ir para a página" title="Ir para a página (1 a ${totalPag})">
      <span class="g2-i g2-i-lg" title="Os números seguem o período de visualização. Status e orçamento alterados aqui são enviados ao Meta Ads quando a integração está ligada.">${G2_ICONS.info}</span>
      <span class="g2-upd" id="g2-upd">Atualizado ${tempoRelativo(state.sincronizadoEm)}</span>
      <button type="button" class="g2-atualizar" data-g2="atualizar">Atualizar</button>
    </div>
    <div class="g2-menu" id="g2-menu" hidden></div>
  </div>`;

  const contasOpts = state.contas.map(c=>`<option value="${escapeHtml(c.id)}" ${g2.conta===c.id?'selected':''}>${escapeHtml(c.nome)}</option>`).join('');
  const produtos = [...new Set(state.campanhas.map(c=>(c.produto||'').trim()).filter(Boolean))].sort((a,b)=>a.localeCompare(b,'pt-BR'));
  const produtosOpts = produtos.map(p=>`<option value="${escapeHtml(p)}" ${g2.produto===p?'selected':''}>${escapeHtml(p)}</option>`).join('');
  const seta = `<span class="g2-chev">${G2_ICONS.down}</span>`;
  const filtros = `
  <div class="g2-filtros">
    <label class="g2-f"><span class="g2-lbl">${G2_NOME[nivel]}</span>
      <input type="text" class="g2-in" id="g2-busca" placeholder="Filtrar por nome" value="${escapeHtml(g2.busca)}" autocomplete="off"></label>
    <label class="g2-f"><span class="g2-lbl">${G2_STATUS[nivel]}</span>
      <span class="g2-selwrap"><select class="g2-in" id="g2-status">
        <option value="" ${!g2.status?'selected':''}>Qualquer</option>
        <option value="on" ${g2.status==='on'?'selected':''}>Ativo</option>
        <option value="off" ${g2.status==='off'?'selected':''}>Pausado</option>
      </select>${seta}</span></label>
    <label class="g2-f"><span class="g2-lbl">Período de Visualização<span class="g2-i" title="Soma dos números dentro do período. 'Máximo' mostra o total desde o início.">${G2_ICONS.info}</span></span>
      <span class="g2-selwrap"><select class="g2-in" id="g2-periodo">
        ${G2_PERIODOS.map(([v,l])=>`<option value="${v}" ${g2.periodo.tipo===v?'selected':''}>${l}</option>`).join('')}
      </select>${seta}</span></label>
    <label class="g2-f"><span class="g2-lbl">Conta de Anúncio</span>
      <span class="g2-selwrap"><select class="g2-in" id="g2-conta"><option value="">Qualquer</option>${contasOpts}</select>${seta}</span></label>
    <label class="g2-f"><span class="g2-lbl">Produto</span>
      <span class="g2-selwrap"><select class="g2-in" id="g2-produto"><option value="">Qualquer</option>${produtosOpts}</select>${seta}</span></label>
  </div>`;

  const cols = G2_COLS.filter(c=>g2Vis(c.id));
  const temSug = nivel === 'campanhas' && g2Vis('sugestao');
  const colgroup = `<colgroup>
    <col style="width:50px"><col style="width:92px"><col style="width:240px">
    ${g2Vis('orcamento')?'<col style="width:140px">':''}
    ${temSug?'<col style="width:210px">':''}
    ${cols.map(c=>`<col style="width:${c.w}px">`).join('')}
    <col style="width:86px">
  </colgroup>`;
  const todosSel = linhas.length > 0 && linhas.every(l=>g2.sel.has(l.id));
  const thead = `<thead><tr>
    <th class="g2-c-chk"><input type="checkbox" class="g2-chk g2-chk-head" data-g2="selall" ${todosSel?'checked':''} aria-label="Selecionar todos"></th>
    ${g2Th('STATUS')}
    ${g2Th(G2_SING[nivel].toUpperCase())}
    ${g2Vis('orcamento') ? g2Th('ORÇAMENTO', 'Clique no valor pra alterar', ' class="g2-r"') : ''}
    ${temSug ? g2Th('SUGESTÃO', 'Subir orçamento quando o ROI é forte e o CPA está abaixo da média; revisar quando o ROI está muito negativo. Clique na sugestão pra aplicar.') : ''}
    ${cols.map(c=>g2Th(c.label, c.info, ' class="g2-r"')).join('')}
    ${g2Th('', null, ' class="g2-c-acoes"')}
  </tr></thead>`;

  const nCols = 4 + (g2Vis('orcamento')?1:0) + (temSug?1:0) + cols.length;
  const vazio = {
    contas: 'Nenhuma conta de anúncio cadastrada.',
    campanhas: (g2.busca || g2.status || g2.conta || g2.produto || g2.soSugestao || g2.soNaoTrack) ? 'Nenhuma campanha com esses filtros.' : (facebookLigado() ? 'Nenhuma campanha ainda. Ao abrir esta página, a Torre baixa as campanhas das contas do Facebook.' : 'Nenhuma campanha cadastrada. Clique em "+ Novo" ou importe uma planilha.'),
    conjuntos: 'Nenhum conjunto ainda. Eles aparecem aqui quando a integração com o Meta Ads enviar os dados.',
    anuncios: 'Nenhum anúncio ainda. Eles aparecem aqui quando a integração com o Meta Ads enviar os dados.'
  }[nivel];

  const corpo = linhas.length ? linhas.map(l=>{
    const podeEditar = l.tipo === 'campanhas' || l.tipo === 'contas';
    const temOrc = l.tipo !== 'anuncios';
    const sub = l.tipo === 'campanhas' ? [l.item.produto, contaNomeDe(l.item.contaId)].filter(Boolean).join(' · ')
              : l.tipo === 'contas' ? (l.item.cliente || '')
              : ((campanhaDoItem(l.item) || {}).nome || l.item.campanha || '');
    const sy = l.item.sync;
    const syncIco = !sy ? '' : sy.estado === 'enviando'
      ? `<span class="g2-sync enviando" title="Enviando ao Meta Ads…">${G2_ICONS.spin}</span>`
      : sy.estado === 'ok'
        ? `<span class="g2-sync ok" title="Sincronizado com o Meta Ads em ${dataHora(sy.em)}">${G2_ICONS.ok}</span>`
        : sy.estado === 'erro'
          ? `<span class="g2-sync erro" title="Falhou no Meta Ads (${escapeHtml(sy.erro||'erro')}). A alteração foi desfeita.">${G2_ICONS.erro}</span>` : '';
    const sugHtml = !l.sug ? `<span class="cell-sub">—</span>`
      : `<button type="button" class="pill ${l.sug.tipo==='subir'?'pill-suggest':'pill-warn'}" data-g2="${l.sug.tipo==='subir'?'sug-aplicar':'sug-pausar'}" data-id="${l.id}" title="${escapeHtml(l.sug.tip)} — clique pra aplicar.">${l.sug.txt}</button>`;
    return `<tr class="${g2.sel.has(l.id)?'sel':''}">
      <td class="g2-c-chk"><input type="checkbox" class="g2-chk" data-g2="sel" data-id="${l.id}" ${g2.sel.has(l.id)?'checked':''} aria-label="Selecionar ${escapeHtml(l.nome)}"></td>
      <td data-label="Status"><div class="g2-st">
        <button type="button" class="g2-toggle ${l.ativo?'on':''}" data-g2="toggle" data-id="${l.id}" role="switch" aria-checked="${l.ativo}"
          ${sy && sy.estado==='enviando' ? 'disabled' : ''} title="${l.ativo?'Ativo — clique pra pausar':'Pausado — clique pra ativar'}"><i></i></button>
        ${syncIco}
      </div></td>
      <td class="g2-nome" data-label="${escapeHtml(G2_SING[nivel])}">
        ${podeEditar ? `<button type="button" class="g2-nome-btn" data-g2="editar" data-id="${l.id}" title="Editar">${escapeHtml(l.nome||'—')}</button>` : `<div class="g2-nome-t">${escapeHtml(l.nome||'—')}</div>`}
        ${sub ? `<div class="g2-nome-sub" title="${escapeHtml(sub)}">${escapeHtml(sub)}</div>` : ''}
      </td>
      ${g2Vis('orcamento') ? `<td class="g2-orc" data-label="Orçamento">${temOrc
        ? `<button type="button" class="g2-orc-btn" data-g2="orcamento" data-id="${l.id}" title="Alterar orçamento">${l.orc ? fmtValor(l.orc) : 'Definir'}${G2_ICONS.edit}</button>
           <div class="g2-orc-t">${l.orc ? l.orcTipo : ''}</div>`
        : `<span class="g2-faint">—</span>`}</td>` : ''}
      ${temSug ? `<td class="g2-sug" data-label="Sugestão">${sugHtml}</td>` : ''}
      ${cols.map(c=>`<td class="g2-num ${c.cls ? c.cls(l) : ''}" data-label="${escapeHtml(c.label)}">${c.v(l)}</td>`).join('')}
      <td class="g2-c-acoes"><div class="g2-acoes">
        ${podeEditar ? `<button type="button" class="g2-ico" data-g2="editar" data-id="${l.id}" title="Editar" aria-label="Editar">${G2_ICONS.edit}</button>` : ''}
        <button type="button" class="g2-ico g2-ico-del" data-g2="excluir" data-id="${l.id}" title="Excluir" aria-label="Excluir">${G2_ICONS.trash}</button>
      </div></td>
    </tr>`;
  }).join('') : `<tr><td colspan="${nCols}" class="g2-vazio">${vazio}</td></tr>`;

  return `
  <div class="g2">
    ${tabs}
    <div class="g2-panel">
      ${toolbar}
      <hr class="g2-sep">
      ${filtros}
      <div class="g2-scroll"><table class="g2-table">${colgroup}${thead}<tbody>${corpo}</tbody></table></div>
    </div>
  </div>`;
}

/* ---------- ações da tabela ---------- */
function g2Item(id){
  const arr = g2.nivel === 'contas' ? state.contas : (state[g2.nivel] || []);
  return arr.find(x=>x.id===id);
}
function g2DefinirStatus(it, ligar){
  if(g2.nivel === 'contas') it.status = ligar ? 'ativa' : 'desativada';
  else it.status = ligar ? 'ativa' : 'pausada';
}
async function g2Salvar(msg){
  await saveState();
  renderNav(); renderContent(); renderMetaMini();
  if(msg) showToast(msg);
}
function g2Rerender(focarId){
  const pos = focarId ? (document.getElementById(focarId)||{}).selectionStart : null;
  renderContent();
  if(focarId){
    const el = document.getElementById(focarId);
    if(el){ el.focus({ preventScroll:true }); try{ el.setSelectionRange(pos, pos); }catch(e){} }
  }
}
function g2FecharMenu(){
  const m = document.getElementById('g2-menu');
  if(m) m.hidden = true;
}
function g2AbrirMenu(btn, html, dono){
  const m = document.getElementById('g2-menu');
  if(!m) return;
  dono = dono || btn.dataset.g2;
  if(!m.hidden && m.dataset.dono === dono && dono !== 'orcamento'){ g2FecharMenu(); return; }
  m.innerHTML = html;
  m.dataset.dono = dono;
  m.hidden = false;
  const box = m.parentElement.getBoundingClientRect();
  const r = btn.getBoundingClientRect();
  m.style.left = Math.max(0, Math.min(r.left - box.left, box.width - m.offsetWidth)) + 'px';
  m.style.top = (r.bottom - box.top + 6) + 'px';
}
function g2MenuColunas(){
  const itens = [...G2_FIXAS_OPC.filter(([id])=> id !== 'sugestao' || g2.nivel === 'campanhas'), ...G2_COLS.map(c=>[c.id, c.label])];
  return `<div class="g2-menu-t">Colunas visíveis</div>
    <div class="g2-menu-cols">${itens.map(([id,label])=>`
      <label class="g2-menu-chk"><input type="checkbox" data-g2="col" data-col="${id}" ${g2Vis(id)?'checked':''}> ${label}</label>`).join('')}</div>
    <button type="button" class="g2-menu-i" data-g2="cols-reset">Mostrar todas</button>`;
}
function g2MenuMais(){
  const n = g2.sel.size;
  const dis = n ? '' : 'disabled';
  return `
    ${g2.nivel === 'campanhas' ? `<button type="button" class="g2-menu-i" data-g2="nova">+ Nova campanha</button>` : ''}
    ${g2.nivel === 'contas' ? `<button type="button" class="g2-menu-i" data-g2="nova">+ Nova conta</button>` : ''}
    <button type="button" class="g2-menu-i" data-g2="lote-on" ${dis}>Ativar selecionados${n?` (${n})`:''}</button>
    <button type="button" class="g2-menu-i" data-g2="lote-off" ${dis}>Pausar selecionados${n?` (${n})`:''}</button>
    <button type="button" class="g2-menu-i g2-menu-del" data-g2="lote-del" ${dis}>Excluir selecionados${n?` (${n})`:''}</button>
    <div class="g2-menu-sep"></div>
    <button type="button" class="g2-menu-i" data-g2="integracao">Integração com Meta Ads</button>
    <button type="button" class="g2-menu-i" data-g2="historico">Histórico de alterações</button>
    <button type="button" class="g2-menu-i" data-g2="importar">Importar gasto do Facebook</button>`;
}
function g2ExportarCSV(){
  const linhas = g2Linhas();
  const cols = G2_COLS.filter(c=>g2Vis(c.id));
  const cab = ['Status', G2_SING[g2.nivel], 'Orçamento', 'Tipo de orçamento', ...cols.map(c=>c.label.charAt(0)+c.label.slice(1).toLowerCase())];
  const esc = v => { const s = String(v ?? ''); return /[;"\n]/.test(s) ? `"${s.replace(/"/g,'""')}"` : s; };
  const corpo = linhas.map(l=>[l.ativo?'Ativo':'Pausado', l.nome, l.orc ? numBR(l.orc) : '', l.orc ? l.orcTipo : '', ...cols.map(c=>c.csv(l))].map(esc).join(';'));
  const csv = '﻿' + [cab.map(esc).join(';'), ...corpo].join('\n');
  try{
    const url = URL.createObjectURL(new Blob([csv], { type:'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    const per = (G2_PERIODOS.find(p=>p[0]===g2.periodo.tipo)||['',''])[1].toLowerCase().replace(/\s+/g,'-');
    a.href = url; a.download = `torre-${g2.nivel}-${per}-${localDateKey()}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(()=>URL.revokeObjectURL(url), 2000);
    showToast(`${linhas.length} linha(s) exportada(s).`);
  }catch(e){ showToast('Não foi possível exportar aqui.'); }
}
let campanhasBaixadasEm = 0;
let campanhasBaixando = null;
let painelGeracao = 0;

async function baixarCampanhasFacebook(force) {
  if (!facebookLigado()) return;
  if (!force && Date.now() - campanhasBaixadasEm < 60000) return;
  if (campanhasBaixando) return campanhasBaixando;
  campanhasBaixadasEm = Date.now();
  const geracao = painelGeracao;
  campanhasBaixando = (async () => {
    try {
      const result = await syncFacebookCampaigns();
      if (geracao !== painelGeracao) return;
      await loadState();
      if (geracao !== painelGeracao) return;
      if (currentSection === 'campanhas') renderContent();
      const n = result?.data?.imported?.campanhas || 0;
      if (n) showToast(`${n} campanha(s) baixada(s) do Facebook.`);
      else if (force) showToast('Nenhuma campanha nova no Facebook.');
    } catch (err) {
      campanhasBaixadasEm = 0;
      showToast(err?.message || 'Não foi possível baixar as campanhas do Facebook.');
    } finally {
      campanhasBaixando = null;
    }
  })();
  return campanhasBaixando;
}

async function g2Atualizar(btn){
  btn.disabled = true; btn.textContent = 'Atualizando…';
  try{
    if (facebookLigado()) await baixarCampanhasFacebook(true);
    if(typeof window.TorreAPI.aoAtualizar === 'function') await window.TorreAPI.aoAtualizar();
    state.sincronizadoEm = new Date().toISOString();
    await g2Salvar('Dados atualizados.');
  }catch(e){
    console.error(e);
    btn.disabled = false; btn.textContent = 'Atualizar';
    showToast('Não foi possível atualizar agora.');
  }
}

/* ---------- envio de alterações ao Meta Ads (via webhook do n8n) ---------- */
const NIVEL_META = { campanhas:'campaign', conjuntos:'adset', anuncios:'ad' };
async function enviarAoFacebook(it, acao, dados, alt) {
  const payload = {};
  if (acao === 'status') payload.status = dados.para;
  if (acao === 'orcamento') {
    payload.orcamento = dados.para;
    payload.orcamentoTipo = dados.tipo;
  }
  it.sync = { estado: 'enviando', em: alt.em };
  if (currentSection === 'campanhas') renderContent();
  try {
    await updateFacebookCampaign(it.id, payload);
    alt.estado = 'ok';
    it.sync = { estado: 'ok', em: new Date().toISOString() };
    painelGeracao += 1;
  } catch (err) {
    alt.estado = 'erro';
    alt.erro = err?.message || String(err);
    it.sync = { estado: 'erro', em: new Date().toISOString(), erro: alt.erro };
  }
  return alt;
}

async function enviarAoMeta(nivel, it, acao, dados){
  const alt = { id: uid(), em: new Date().toISOString(), nivel, itemId: it.id, nome: it.nome || '', idMeta: it.idMeta || '', acao, ...dados, estado:'pendente' };
  state.alteracoes = [alt, ...(state.alteracoes || [])].slice(0, 200);
  const integ = state.integracao || {};
  if(!NIVEL_META[nivel]){ alt.estado = 'local'; alt.motivo = 'contas não são alteradas no Meta por aqui'; return alt; }
  if (nivel === 'campanhas' && facebookLigado() && String(it.idMeta || '').trim()) {
    return enviarAoFacebook(it, acao, dados, alt);
  }
  if(!integ.ativo || !integ.url){ alt.estado = 'local'; alt.motivo = 'integração com o Meta desligada'; return alt; }
  if(!String(it.idMeta || '').trim()){ alt.estado = 'local'; alt.motivo = 'sem ID do Meta cadastrado'; return alt; }
  it.sync = { estado:'enviando', em: alt.em };
  if(currentSection === 'campanhas') renderContent();
  const payload = { origem:'torre-de-controle', chave: integ.chave || '', alteracao_id: alt.id,
                    nivel: NIVEL_META[nivel], id: String(it.idMeta).trim(), acao };
  if(acao === 'status') payload.status = dados.para === 'ativa' ? 'ACTIVE' : 'PAUSED';
  if(acao === 'orcamento'){
    payload.tipo_orcamento = dados.tipo === 'Total' ? 'lifetime_budget' : 'daily_budget';
    payload.valor = dados.para;
    payload.valor_centavos = Math.round(dados.para * 100);
  }
  try{
    const ctrl = new AbortController();
    const timer = setTimeout(()=>ctrl.abort(), 20000);
    const res = await fetch(integ.url, { method:'POST', headers:{ 'Content-Type':'application/json' }, body: JSON.stringify(payload), signal: ctrl.signal });
    clearTimeout(timer);
    let corpo = null;
    try{ corpo = await res.json(); }catch(e){}
    if(!res.ok || !corpo || corpo.ok !== true) throw new Error((corpo && corpo.erro) || `resposta ${res.status}`);
    alt.estado = 'ok';
    it.sync = { estado:'ok', em: new Date().toISOString() };
  }catch(err){
    alt.estado = 'erro';
    alt.erro = err.name === 'AbortError' ? 'tempo esgotado' : String(err.message || err);
    it.sync = { estado:'erro', em: new Date().toISOString(), erro: alt.erro };
  }
  return alt;
}
function avisoAlteracao(alt, okTxt){
  if(alt.estado === 'ok') showToast(`${okTxt} no painel e no Meta Ads.`);
  else if(alt.estado === 'local') showToast(`${okTxt} só no painel (${alt.motivo}).`);
  else showToast(`Não foi possível alterar no Meta Ads: ${alt.erro}. Nada foi mudado.`);
}
async function alterarStatus(nivel, it, ligar){
  const antes = it.status;
  g2DefinirStatusNivel(nivel, it, ligar);
  const alt = await enviarAoMeta(nivel, it, 'status', { de: antes, para: it.status });
  if(alt.estado === 'erro') it.status = antes;
  return alt;
}
async function alterarOrcamento(nivel, it, valor, tipo){
  const campo = nivel === 'contas' ? 'limiteGasto' : 'orcamento';
  const antes = { v: Number(it[campo]) || 0, t: it.orcamentoTipo };
  it[campo] = valor;
  if(nivel !== 'contas') it.orcamentoTipo = tipo;
  const alt = await enviarAoMeta(nivel, it, 'orcamento', { de: antes.v, para: valor, tipo: nivel === 'contas' ? 'Diário' : tipo });
  if(alt.estado === 'erro'){ it[campo] = antes.v; if(nivel !== 'contas') it.orcamentoTipo = antes.t; }
  return alt;
}
function g2DefinirStatusNivel(nivel, it, ligar){
  if(nivel === 'contas') it.status = ligar ? 'ativa' : 'desativada';
  else it.status = ligar ? 'ativa' : 'pausada';
}

let orcEd = null; // { id, nivel, base, tipo }
function g2EditorOrcamento(btn, id, sugerido){
  const it = g2Item(id); if(!it) return;
  const nivel = g2.nivel;
  const origem = nivel === 'contas' ? moedaConta(it) : moedaCampanha(campanhaDoItem(it) || it);
  const base = Number(nivel === 'contas' ? it.limiteGasto : it.orcamento) || 0;
  const baseVisivel = paraBase(base, origem);
  const tipo = nivel === 'contas' ? 'Diário' : (it.orcamentoTipo || (nivel === 'campanhas' ? 'Total' : 'Diário'));
  orcEd = { id, nivel, base, tipo, origem };
  const valor = sugerido != null ? paraBase(sugerido, origem) : baseVisivel;
  const vaiProFacebook = nivel === 'campanhas' && facebookLigado() && String(it.idMeta || '').trim();
  const vaiProMeta = vaiProFacebook || (NIVEL_META[nivel] && metaLigado() && String(it.idMeta||'').trim());
  const nota = nivel === 'contas' ? 'O limite da conta fica só no painel.'
    : vaiProFacebook ? `Será alterado no Facebook (${tipo === 'Total' ? 'orçamento total' : 'orçamento diário'}).`
    : vaiProMeta ? `Será enviado ao Meta Ads (${tipo === 'Total' ? 'orçamento total' : 'orçamento diário'}).`
    : !metaLigado() ? 'Fica só no painel — a integração com o Meta está desligada.'
    : 'Fica só no painel — cadastre o ID do Meta nesta campanha pra enviar.';
  const html = `
    <div class="g2-menu-t">${nivel === 'contas' ? 'Limite diário' : 'Orçamento'} · ${escapeHtml(it.nome || '')}</div>
    <div class="orc-ed">
      ${nivel === 'contas' ? '' : `<div class="orc-tipo" role="group" aria-label="Tipo de orçamento">
        <button type="button" class="${tipo==='Diário'?'on':''}" data-g2="orc-tipo" data-v="Diário">Diário</button>
        <button type="button" class="${tipo==='Total'?'on':''}" data-g2="orc-tipo" data-v="Total">Total</button>
      </div>`}
      <label class="orc-in"><span>${simboloMoeda()}</span><input id="orc-valor" inputmode="decimal" autocomplete="off" value="${numBR(valor)}" aria-label="Novo valor"></label>
      <div class="orc-chips">
        ${[-20,-10,10,20,30].map(p=>`<button type="button" data-g2="orc-pct" data-v="${p}">${p>0?'+':'−'}${Math.abs(p)}%</button>`).join('')}
      </div>
      <div class="orc-de">Atual: ${baseVisivel ? fmtValor(baseVisivel) : 'sem orçamento'}${sugerido != null ? ' · valor sugerido já preenchido' : ''} · ${escapeHtml(cotacaoLabel() || origem)}</div>
      <div class="orc-nota ${vaiProMeta?'meta':''}"><i></i><span>${nota}</span></div>
      <div class="orc-acoes">
        <button type="button" class="btn btn-ghost" data-g2="orc-cancelar">Cancelar</button>
        <button type="button" class="btn btn-primary" data-g2="orc-salvar">Aplicar</button>
      </div>
    </div>`;
  const m = document.getElementById('g2-menu');
  m.hidden = true;
  g2AbrirMenu(btn, html, 'orcamento');
  // alinha pela direita do botão
  const box = m.parentElement.getBoundingClientRect(), r = btn.getBoundingClientRect();
  m.style.left = Math.max(0, Math.min(r.right - box.left - m.offsetWidth, box.width - m.offsetWidth)) + 'px';
  const inp = document.getElementById('orc-valor');
  inp.focus({ preventScroll:true }); inp.select();
}
async function g2SalvarOrcamento(){
  if(!orcEd) return;
  const inp = document.getElementById('orc-valor');
  const digitado = Math.round(parseMoney(inp.value) * 100) / 100;
  if(!(digitado > 0)){ showToast('Digite um valor maior que zero.'); inp.focus(); return; }
  const it = g2Item(orcEd.id); if(!it) return;
  const { nivel, tipo, base, origem } = orcEd;
  const valor = paraOrigem(digitado, origem || 'BRL');
  if(Math.abs(valor - base) < 0.009 && (nivel === 'contas' || tipo === (it.orcamentoTipo || tipo))){ g2FecharMenu(); return; }
  const btn = document.querySelector('[data-g2="orc-salvar"]');
  if(btn){ btn.disabled = true; btn.textContent = 'Aplicando…'; }
  g2FecharMenu();
  const alt = await alterarOrcamento(nivel, it, valor, tipo);
  await saveState();
  renderNav(); renderContent(); renderMetaMini();
  avisoAlteracao(alt, `Orçamento alterado para ${fmtValor(digitado)}`);
  orcEd = null;
}

async function g2Click(e){
  const btn = e.target.closest('[data-g2]');
  if(!btn){ if(!e.target.closest('#g2-menu')) g2FecharMenu(); return; }
  if (btn.tagName === 'A' && !isPlainClick(e)) return;
  if (btn.tagName === 'A') e.preventDefault();
  const acao = btn.dataset.g2;
  const id = btn.dataset.id;
  if(!['cols','mais','col','orcamento','sug-aplicar'].includes(acao) && !btn.closest('#g2-menu')) g2FecharMenu();
  switch(acao){
    case 'nivel':
      if(g2.nivel !== btn.dataset.v){ g2.nivel = btn.dataset.v; g2.pagina = 1; g2.sel.clear(); g2.soSugestao = false; g2.soNaoTrack = false; salvarG2(); renderContent(); syncUrl(); }
      break;
    case 'toggle': {
      const it = g2Item(id); if(!it) break;
      const ligar = !btn.classList.contains('on');
      const alt = await alterarStatus(g2.nivel, it, ligar);
      await g2Salvar();
      const fem = g2.nivel === 'campanhas' || g2.nivel === 'contas';
      avisoAlteracao(alt, `${G2_SING[g2.nivel]} ${ligar ? (fem?'ativada':'ativado') : (fem?'pausada':'pausado')}`);
      break;
    }
    case 'orcamento': g2EditorOrcamento(btn, id); break;
    case 'sug-aplicar': {
      const it = g2Item(id); if(!it) break;
      const base = Number(it.orcamento) || Number(it.gasto) || 0;
      const cel = btn.closest('tr').querySelector('[data-g2="orcamento"]') || btn;
      g2EditorOrcamento(cel, id, Math.round(base * 1.3 * 100) / 100);
      break;
    }
    case 'sug-pausar': {
      const it = g2Item(id); if(!it) break;
      if(!confirm(`Pausar "${it.nome}"?`)) break;
      const alt = await alterarStatus(g2.nivel, it, false);
      await g2Salvar();
      avisoAlteracao(alt, 'Campanha pausada');
      break;
    }
    case 'orc-pct': {
      const inp = document.getElementById('orc-valor');
      const visivel = orcEd ? paraBase(orcEd.base, orcEd.origem || 'BRL') : parseMoney(inp.value);
      inp.value = numBR(Math.max(0, visivel * (1 + Number(btn.dataset.v)/100)));
      inp.focus();
      break;
    }
    case 'orc-tipo':
      if(orcEd){ orcEd.tipo = btn.dataset.v; btn.parentElement.querySelectorAll('button').forEach(b=>b.classList.toggle('on', b === btn)); }
      break;
    case 'orc-cancelar': g2FecharMenu(); orcEd = null; break;
    case 'orc-salvar': g2SalvarOrcamento(); break;
    case 'integracao': g2FecharMenu(); openIntegracao(); break;
    case 'historico': g2FecharMenu(); openHistorico(); break;
    case 'sel':
      btn.checked ? g2.sel.add(id) : g2.sel.delete(id);
      renderContent();
      break;
    case 'selall': {
      const pagina = g2Linhas().slice((g2.pagina-1)*G2_PAG, g2.pagina*G2_PAG);
      if(btn.checked) pagina.forEach(l=>g2.sel.add(l.id)); else pagina.forEach(l=>g2.sel.delete(l.id));
      renderContent();
      break;
    }
    case 'prev': g2.pagina--; renderContent(); syncUrl(); break;
    case 'next': g2.pagina++; renderContent(); syncUrl(); break;
    case 'page': g2.pagina = Number(btn.dataset.v) || 1; renderContent(); syncUrl(); break;
    case 'sug':
      if(g2.nivel !== 'campanhas'){ showToast('Sugestões aparecem na aba Campanhas.'); break; }
      g2.soSugestao = !g2.soSugestao; g2.pagina = 1; renderContent(); syncUrl({ replace: true });
      break;
    case 'naotrack': g2.soNaoTrack = !g2.soNaoTrack; g2.pagina = 1; renderContent(); syncUrl({ replace: true }); break;
    case 'export': g2ExportarCSV(); break;
    case 'cols': g2AbrirMenu(btn, g2MenuColunas()); break;
    case 'mais': g2AbrirMenu(btn, g2MenuMais()); break;
    case 'col':
      btn.checked ? g2Ocultas.delete(btn.dataset.col) : g2Ocultas.add(btn.dataset.col);
      salvarG2();
      { const scroll = document.querySelector('.g2-scroll'); const sx = scroll ? scroll.scrollLeft : 0;
        renderContent();
        const s2 = document.querySelector('.g2-scroll'); if(s2) s2.scrollLeft = sx;
        g2AbrirMenu(document.querySelector('[data-g2="cols"]'), g2MenuColunas()); }
      break;
    case 'cols-reset': g2Ocultas.clear(); salvarG2(); renderContent(); break;
    case 'nova': g2FecharMenu(); openDrawer(g2.nivel === 'contas' ? 'contas' : 'campanhas', null); break;
    case 'importar': goSection('importar'); break;
    case 'lote-on': case 'lote-off': {
      const ligar = acao === 'lote-on';
      const ids = [...g2.sel];
      g2.sel.clear();
      g2FecharMenu();
      const res = { ok:0, local:0, erro:0 };
      for(const sid of ids){
        const it = g2Item(sid); if(!it) continue;
        const alt = await alterarStatus(g2.nivel, it, ligar);
        res[alt.estado === 'ok' ? 'ok' : alt.estado === 'erro' ? 'erro' : 'local']++;
      }
      await g2Salvar();
      const partes = [];
      if(res.ok) partes.push(`${res.ok} no Meta Ads`);
      if(res.local) partes.push(`${res.local} só no painel`);
      if(res.erro) partes.push(`${res.erro} com erro (desfeito)`);
      showToast(`${ids.length} item(ns) ${ligar?'ativado(s)':'pausado(s)'}: ${partes.join(', ')}.`);
      break;
    }
    case 'lote-del': {
      const n = g2.sel.size;
      if(!n || !await confirmarApagar(`Excluir ${n} item(ns) selecionado(s)?`)) break;
      const chave = g2.nivel === 'contas' ? 'contas' : g2.nivel;
      state[chave] = state[chave].filter(x=>!g2.sel.has(x.id));
      g2.sel.clear();
      await g2Salvar(`${n} item(ns) excluído(s).`);
      break;
    }
    case 'editar': openDrawer(g2.nivel === 'contas' ? 'contas' : 'campanhas', id); break;
    case 'excluir': {
      if(!await confirmarApagar('Excluir este registro?')) break;
      const chave = g2.nivel === 'contas' ? 'contas' : g2.nivel;
      state[chave] = state[chave].filter(x=>x.id!==id);
      g2.sel.delete(id);
      await g2Salvar('Registro excluído.');
      break;
    }
    case 'atualizar': g2Atualizar(btn); break;
  }
}

let g2BuscaTimer = null;
function g2Input(e){
  if(e.target.id === 'g2-busca'){
    g2.busca = e.target.value;
    clearTimeout(g2BuscaTimer);
    g2BuscaTimer = setTimeout(()=>{ g2.pagina = 1; g2Rerender('g2-busca'); syncUrl({ replace: true }); }, 160);
  }
}
function g2Change(e){
  const t = e.target;
  if(t.id === 'g2-status'){ g2.status = t.value; g2.pagina = 1; renderContent(); syncUrl({ replace: true }); }
  else if(t.id === 'g2-periodo'){ g2.periodo = { tipo: t.value }; salvarG2(); renderContent(); }
  else if(t.id === 'g2-conta'){ g2.conta = t.value; g2.pagina = 1; renderContent(); syncUrl({ replace: true }); }
  else if(t.id === 'g2-produto'){ g2.produto = t.value; g2.pagina = 1; renderContent(); syncUrl({ replace: true }); }
}
function g2Keydown(e){
  if(e.target.id === 'orc-valor'){
    if(e.key === 'Enter'){ e.preventDefault(); g2SalvarOrcamento(); }
    if(e.key === 'Escape'){ g2FecharMenu(); orcEd = null; }
    return;
  }
  if(e.target.id === 'g2-pagina' && e.key === 'Enter'){
    const n = parseInt(e.target.value, 10);
    if(n > 0){ g2.pagina = n; renderContent(); syncUrl(); }
  }
}

/* =========================================================
   RENDER: REJEITADOS
   ========================================================= */
function renderRejeitados(){
  const filter = listFilter.rejeitados || 'todas';
  const list = state.rejeitados.filter(r=> filter==='todas' || r.status===filter);
  const pg = paginate(list, listPage.rejeitados, LIST_PAGE_SIZE);
  listPage.rejeitados = pg.page;
  const toolbar = `
    <div class="table-toolbar">
      <select class="filter" id="rejeitados-filter">
        <option value="todas" ${filter==='todas'?'selected':''}>Todos os status</option>
        <option value="em recurso" ${filter==='em recurso'?'selected':''}>Em recurso</option>
        <option value="perdido" ${filter==='perdido'?'selected':''}>Perdido</option>
        <option value="reaprovado" ${filter==='reaprovado'?'selected':''}>Reaprovado</option>
      </select>
      <span class="cell-sub">${pg.total} registro(s)</span>
    </div>`;
  const rows = pg.total ? pg.items.map(r=>`
    <tr>
      <td><div class="cell-title">${escapeHtml(r.nomeAnuncio)}</div><div class="cell-sub">${escapeHtml(contaNome(r.contaId))}</div></td>
      <td style="max-width:280px;">${escapeHtml(r.motivo||'—')}</td>
      <td class="num">${fmtDate(r.data)}</td>
      <td><span class="pill ${statusPillClass(r.status)}">${r.status}</span></td>
      <td>
        <div class="row-actions">
          <button class="icon-btn" data-action="edit" data-type="rejeitados" data-id="${r.id}">editar</button>
          <button class="icon-btn danger" data-action="delete" data-type="rejeitados" data-id="${r.id}">excluir</button>
        </div>
      </td>
    </tr>`).join('') : `<tr><td colspan="5" class="empty-state">Nenhum anúncio rejeitado registrado.</td></tr>`;

  return `${toolbar}<div class="panel-box"><div class="table-scroll"><table>
    <thead><tr><th>Anúncio</th><th>Motivo</th><th>Data</th><th>Status</th><th></th></tr></thead>
    <tbody>${rows}</tbody>
  </table></div></div>
  ${pg.total ? renderPager({ id:'rejeitados', page:pg.page, totalPages:pg.totalPages, from:pg.from, to:pg.to, total:pg.total, label:'registro(s)', hrefFor: (n) => buildHref({ section:'rejeitados', page:n }) }) : ''}`;
}

/* =========================================================
   RENDER: RELATÓRIOS (por cliente)
   ========================================================= */
function renderRelatorios(){
  const map = {};
  state.campanhas.forEach(c=>{
    const key = c.loja && c.loja.trim() ? c.loja.trim() : 'Sem loja definida';
    if(!map[key]) map[key] = [];
    map[key].push(c);
  });
  const lojas = Object.keys(map).sort((a,b)=>{
    const totalA = map[a].reduce((s,c)=>s+(Number(c.gasto)||0),0);
    const totalB = map[b].reduce((s,c)=>s+(Number(c.gasto)||0),0);
    return totalB - totalA;
  });
  if(lojas.length===0){
    return `<div class="panel-box"><div class="empty-state">Cadastre a "Loja Shopify" em cada campanha (na edição da campanha) para ver o relatório por loja.</div></div>`;
  }

  const pg = paginate(lojas, listPage.relatorios, REPORT_PAGE_SIZE);
  listPage.relatorios = pg.page;

  const blocos = pg.items.map(loja=>{
    const camps = [...map[loja]].sort((a,b)=>(Number(b.gasto)||0)-(Number(a.gasto)||0));
    const totalGasto = camps.reduce((s,c)=>s+paraBase(c.gasto, moedaCampanha(c)),0);
    const totalReceita = camps.reduce((s,c)=>s+paraBase(c.receita, moedaCampanha(c)),0);
    const roasLoja = totalGasto>0 ? totalReceita/totalGasto : 0;
    const roiLoja = totalGasto>0 ? ((totalReceita-totalGasto)/totalGasto)*100 : 0;

    const rows = camps.map(c=>{
      const m = campaignMetrics(c);
      return `<tr>
        <td><div class="cell-title">${escapeHtml(c.nome)}</div>${c.cliente?`<div class="cell-sub">${escapeHtml(c.cliente)}</div>`:''}</td>
        <td class="num">${moneyConta(c.gasto, moedaCampanha(c))}</td>
        <td class="num">${moneyConta(c.receita, moedaCampanha(c))}</td>
        <td class="num">${m.roas.toLocaleString('pt-BR',{maximumFractionDigits:2})}x</td>
        <td class="num" style="color:${m.roiPct>=0?'var(--green)':'var(--red)'}">${m.roiPct.toLocaleString('pt-BR',{maximumFractionDigits:1})}%</td>
        <td class="num">${moneyConta(m.cpa, moedaCampanha(c))}</td>
        <td><span class="pill ${statusPillClass(c.status)}">${c.status}</span></td>
      </tr>`;
    }).join('');

    return `
    <div class="panel-box" style="margin-bottom:16px;">
      <div class="panel-box-head">
        <h3>${escapeHtml(loja)}</h3>
        <span class="cell-sub mono">${fmtValor(totalGasto)} gasto · ${fmtValor(totalReceita)} receita · ROAS ${roasLoja.toLocaleString('pt-BR',{maximumFractionDigits:2})}x · ROI <span style="color:${roiLoja>=0?'var(--green)':'var(--red)'}">${roiLoja.toLocaleString('pt-BR',{maximumFractionDigits:1})}%</span></span>
      </div>
      <div class="table-scroll"><table>
        <thead><tr><th>Campanha</th><th>Gasto</th><th>Receita</th><th>ROAS</th><th>ROI</th><th>CPA</th><th>Status</th></tr></thead>
        <tbody>${rows}</tbody>
      </table></div>
    </div>`;
  }).join('');

  return `${blocos}${renderPager({ id:'relatorios', page:pg.page, totalPages:pg.totalPages, from:pg.from, to:pg.to, total:pg.total, label:'loja(s)', hrefFor: (n) => buildHref({ section:'relatorios', page:n }) })}`;
}

/* =========================================================
   RENDER: IMPORTAR DADOS (Facebook / Shopify)
   ========================================================= */
function guessColumn(headers, keywords){
  const lower = headers.map(h=>h.toLowerCase());
  for(const kw of keywords){
    const idx = lower.findIndex(h=>h.includes(kw));
    if(idx>-1) return headers[idx];
  }
  return headers[0] || '';
}

function renderImportar(){
  const fbLog = state.importLog.find(l=>l.tipo==='facebook');

  const cards = `
    <div class="import-grid">
      <div class="panel-box">
        <div class="panel-box-head"><h3>Gasto do Facebook Ads</h3></div>
        <div style="padding:16px;">
          <p class="cell-sub" style="margin:0 0 12px 0;">No Gerenciador de Anúncios, veja o desempenho por campanha e exporte como CSV ou Excel (.xlsx). Inclua nome da campanha, valor usado e, se quiser, as colunas do funil. Vendas da Shopify entram pela aba Lojas, não por planilha.</p>
          <input type="file" accept=".csv,.xlsx,.xls" data-import-type="facebook">
          ${fbLog ? `<div class="cell-sub" style="margin-top:10px;">Última importação: ${fmtDate(fbLog.data)} · ${escapeHtml(fbLog.arquivo)} — ${fbLog.atualizadas} atualizada(s), ${fbLog.criadas} nova(s)</div>` : `<div class="cell-sub" style="margin-top:10px;">Nenhuma importação ainda.</div>`}
        </div>
      </div>
    </div>`;

  let mapping = '';
  if(pendingImport){
    const { headers, rows, fileName } = pendingImport;
    const campGuess = guessColumn(headers, ['nome da campanha','campaign name','campanha','campaign']);
    const valGuess = guessColumn(headers, ['valor usado','amount spent','gasto','spend']);
    const convGuess = guessColumn(headers, ['compras','purchases','conversõ','conversions','resultados']);
    const guessOpt = (kws)=>{ const lower = headers.map(h=>h.toLowerCase()); for(const kw of kws){ const i = lower.findIndex(h=>h.includes(kw)); if(i>-1) return headers[i]; } return ''; };
    const clkGuess = guessOpt(['cliques no link','link clicks']);
    const lpvGuess = guessOpt(['visualizações da página de destino','visualizacoes da pagina de destino','landing page views']);
    const chkGuess = guessOpt(['finalizações de compra iniciadas','finalizacoes de compra iniciadas','checkouts initiated','checkout']);
    const optHtml = (sel)=> headers.map(h=>`<option value="${escapeHtml(h)}" ${h===sel?'selected':''}>${escapeHtml(h)}</option>`).join('');

    mapping = `
    <div class="panel-box" style="margin-top:18px;">
      <div class="panel-box-head"><h3>Confirmar colunas — Facebook Ads · ${escapeHtml(fileName)}</h3></div>
      <form id="import-mapping-form" style="padding:16px;display:flex;flex-direction:column;gap:14px;">
        <div class="field-row">
          <div class="field">
            <label>Coluna com o nome da campanha</label>
            <select name="campCol">${optHtml(campGuess)}</select>
          </div>
          <div class="field">
            <label>Coluna com o valor gasto</label>
            <select name="valCol">${optHtml(valGuess)}</select>
          </div>
        </div>
        <div class="field">
          <label>Coluna de compras/conversões (opcional)</label>
          <select name="convCol"><option value="">Nenhuma</option>${optHtml(convGuess)}</select>
        </div>
        <div class="cell-sub">Colunas do funil (opcionais)</div>
        <div class="field-row">
          <div class="field"><label>Cliques no link</label><select name="clkCol"><option value="">Nenhuma</option>${optHtml(clkGuess)}</select></div>
          <div class="field"><label>Visualizações da página de destino</label><select name="lpvCol"><option value="">Nenhuma</option>${optHtml(lpvGuess)}</select></div>
        </div>
        <div class="field"><label>Finalizações de compra iniciadas</label><select name="chkCol"><option value="">Nenhuma</option>${optHtml(chkGuess)}</select></div>
        <div class="cell-sub">${rows.length} linha(s) detectada(s) no arquivo. Linhas com o mesmo nome de campanha serão somadas; o valor final substitui o gasto atual da campanha correspondente. Campanhas sem correspondência serão criadas automaticamente.</div>
        <div style="display:flex;gap:10px;">
          <button type="button" class="btn btn-ghost" data-action="cancel-import">Cancelar</button>
          <button type="submit" class="btn btn-primary">Confirmar importação</button>
        </div>
      </form>
    </div>`;
  }

  return cards + mapping;
}

async function handleFileSelect(tipo, file){
  if(tipo !== 'facebook'){
    showToast('Vendas da Shopify entram pela aba Lojas, não por planilha.');
    return;
  }
  const ext = (file.name.split('.').pop() || '').toLowerCase();
  let XLSX = null;
  if(ext === 'xlsx' || ext === 'xls'){
    try{
      showToast('Preparando leitor de Excel…');
      XLSX = await loadXLSX();
    }catch(err){
      showToast('Sem conexão pra abrir Excel agora. Tente exportar como .csv.');
      return;
    }
  }
  const reader = new FileReader();

  reader.onerror = ()=> showToast('Erro ao ler o arquivo.');

  if(ext === 'xlsx' || ext === 'xls'){
    reader.onload = (e)=>{
      try{
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type:'array' });
        const { headers, rows } = parseWorkbookToTable(XLSX, workbook);
        if(headers.length===0 || rows.length===0){
          showToast('Não consegui ler linhas nessa planilha. Confira o arquivo.');
          return;
        }
        pendingImport = { tipo, headers, rows, fileName: file.name };
        renderContent();
      }catch(err){
        console.error(err);
        showToast('Não consegui abrir esse arquivo Excel. Tente exportar como .csv.');
      }
    };
    reader.readAsArrayBuffer(file);
  } else {
    reader.onload = (e)=>{
      const { headers, rows } = parseCSV(e.target.result);
      if(headers.length===0 || rows.length===0){
        showToast('Não consegui ler linhas nesse arquivo. Confira o CSV.');
        return;
      }
      pendingImport = { tipo, headers, rows, fileName: file.name };
      renderContent();
    };
    reader.readAsText(file, 'UTF-8');
  }
}

function processImport(form){
  if(!pendingImport) return;
  const { headers, rows } = pendingImport;
  const campCol = form.campCol.value;
  const valCol = form.valCol.value;
  const convCol = form.convCol ? form.convCol.value : '';
  const campIdx = headers.indexOf(campCol);
  const valIdx = headers.indexOf(valCol);
  const convIdx = convCol ? headers.indexOf(convCol) : -1;
  const colIdx = (name)=> (form[name] && form[name].value) ? headers.indexOf(form[name].value) : -1;
  const funilCols = { cliques: colIdx('clkCol'), visualizacoes: colIdx('lpvCol'), checkouts: colIdx('chkCol') };
  const toInt = v => parseInt(String(v||'0').replace(/[^\d-]/g,''),10) || 0;

  const agg = {};
  rows.forEach(r=>{
    const campRaw = (r[campIdx]||'').trim();
    if(!campRaw) return;
    const key = campRaw.toLowerCase();
    if(!agg[key]) agg[key] = { label: campRaw, valor:0, conv:0, cliques:0, visualizacoes:0, checkouts:0 };
    for(const k in funilCols){ if(funilCols[k]>-1) agg[key][k] += toInt(r[funilCols[k]]); }
    agg[key].valor += parseMoney(r[valIdx]);
    if(convIdx>-1) agg[key].conv += (parseInt(String(r[convIdx]||'0').replace(/[^\d-]/g,''),10) || 0);
  });

  let atualizadas = 0, criadas = 0;
  Object.values(agg).forEach(item=>{
    let campanha = state.campanhas.find(c => (c.nome||'').trim().toLowerCase() === item.label.toLowerCase());
    if(!campanha){
      campanha = state.campanhas.find(c => {
        const nome = (c.nome||'').toLowerCase();
        return nome.includes(item.label.toLowerCase()) || item.label.toLowerCase().includes(nome);
      });
    }
    if(!campanha){
      campanha = { id: uid(), nome: item.label, contaId:'', cliente:'', objetivo:'', status:'ativa', dataInicio:'', orcamento:0, gasto:0, receita:0, impressoes:0, cliques:0, visualizacoes:0, checkouts:0, conversoes:0 };
      state.campanhas.push(campanha);
      criadas++;
    } else {
      atualizadas++;
    }
    campanha.gasto = item.valor;
    if(convIdx>-1) campanha.conversoes = item.conv;
    for(const k in funilCols){ if(funilCols[k]>-1) campanha[k] = item[k]; }
    marcarAtualizado(campanha);
  });

  state.importLog = state.importLog.filter(l=>l.tipo!=='facebook');
  state.importLog.unshift({ id: uid(), tipo: 'facebook', data: localDateKey(), arquivo: pendingImport.fileName, atualizadas, criadas });
  pendingImport = null;
  saveState();
  renderNav();
  renderContent();
  renderMetaMini();
  showToast(`Importação concluída: ${atualizadas} campanha(s) atualizada(s), ${criadas} nova(s).`);
}

/* =========================================================
   ROTEAMENTO DE CONTEÚDO
   ========================================================= */
function resumoParaApp() {
  const P = periodoInfo();
  const atualP = somaDias(P.dias, P.ateHora, mapaDiario());
  const vendasPeriodo = resumoVendasPeriodo(P.de, P.ate);
    if (vendasPeriodo && !vendasPeriodo.erro) {
    return {
      periodo: P.nome,
      rotulo: 'Vendas pagas',
      lucro: ocultarValores ? `${simboloMoeda()} •••` : fmtValor(vendasPeriodo.vendido),
      receita: ocultarValores ? '•••' : String(vendasPeriodo.pedidos),
      detalhe: 'Pedidos',
      gasto: money(atualP.gasto),
      positivo: vendasPeriodo.vendido >= 0,
      offline: Boolean(state._offline),
    };
  }
  const liq = liquidoDe(atualP.receita);
  const lucro = liq - atualP.gasto;
  return {
    periodo: P.nome,
    lucro: money(lucro),
    receita: money(liq),
    gasto: money(atualP.gasto),
    positivo: lucro >= 0,
    offline: Boolean(state._offline) || Boolean(vendasPeriodo?.erro),
  };
}

function renderContent(){
  const el = document.getElementById('content');
  if (modoApp() && appPerfil) {
    el.innerHTML = renderAppPerfil();
    marcarAbaApp('perfil');
    return;
  }
  if (modoApp() && currentSection === 'overview') {
    fatChart = null;
    el.innerHTML = renderAppHome(resumoParaApp());
    marcarAbaApp('overview');
    const periodo = periodoInfo();
    if (!resumoVendasPeriodo(periodo.de, periodo.ate)) {
      refreshVendas().then(() => {
        if (modoApp() && currentSection === 'overview' && !appPerfil) renderContent();
      });
    }
    return;
  }
  const map = {
    overview: renderOverview,
    funil: () => renderFunil({
      onNeedRefresh: () => { if (currentSection === 'funil') renderContent(); },
    }),
    bms: renderBms,
    contas: renderContas,
    campanhas: renderCampanhas,
    rejeitados: renderRejeitados,
    relatorios: renderRelatorios,
    importar: renderImportar,
    admin: () => renderAdmins({
      page: listPage.admin,
      onNeedRefresh: () => { if (currentSection === 'admin') renderContent(); },
    }),
    lojas: () => renderIntegracoes({
      page: listPage.lojas,
      onNeedRefresh: () => { if (currentSection === 'lojas') renderContent(); },
    }),
    vendas: () => renderVendas({
      page: listPage.vendas,
      onNeedRefresh: () => { if (currentSection === 'vendas') renderContent(); },
    }),
    tracker: () => renderTracker(),
    notificacoes: () => renderNotificacoes(),
    produtos: () => renderProdutos({
      page: listPage.produtos,
      onNeedRefresh: () => { if (currentSection === 'produtos') renderContent(); },
    }),
  };
  if(currentSection !== 'overview') fatChart = null;
  const corpo = map[currentSection] ? map[currentSection]() : '';
  const titulo = SECTIONS.find((item) => item.id === currentSection)?.label || '';
  el.innerHTML = (modoApp() ? cabecalhoApp(titulo) : '') + corpo;
  if (modoApp()) marcarAbaApp(currentSection);
  if(currentSection === 'overview'){ drawFatChart(); drawEvoChart(); }
  else labelTables(el);
}

// Copia o nome da coluna pra cada célula — no celular a tabela vira cartões
// e cada valor aparece com o rótulo ao lado.
function labelTables(root){
  root.querySelectorAll('table').forEach(t=>{
    const heads = [...t.querySelectorAll('thead th')].map(th=>th.textContent.trim());
    t.querySelectorAll('tbody tr').forEach(tr=>{
      [...tr.children].forEach((td,i)=>{ if(heads[i]) td.dataset.label = heads[i]; });
    });
  });
}

/* =========================================================
   FORMULÁRIOS (DRAWER)
   ========================================================= */

function dynamicOptions(kind){
  if(kind==='bms') return state.bms.map(b=>({value:b.id,label:b.nome}));
  if(kind==='contas') return state.contas.map(c=>({value:c.id,label:c.nome}));
  return [];
}

function origemDoRegistro(type, data){
  if(type === 'contas') return moedaConta(data || {});
  if(type === 'campanhas') return moedaCampanha(data || {});
  return 'BRL';
}
function camposDinheiro(type){
  if(type === 'contas') return ['limiteGasto'];
  if(type === 'campanhas') return ['orcamento', 'gasto', 'receita'];
  return [];
}
function gravarDinheiro(type, record, prev){
  const campos = camposDinheiro(type);
  if(!campos.length) return;
  const origem = origemDoRegistro(type, { ...(prev || {}), contaId: record.contaId || prev?.contaId, currency: prev?.currency });
  for(const k of campos){
    if(record[k] == null || record[k] === '') continue;
    record[k] = paraOrigem(parseFloat(record[k]) || 0, origem);
  }
}
function moedaAlteracao(a){
  if(a.nivel === 'contas') return moedaConta(state.contas.find((c)=> c.id === a.itemId) || {});
  const campanha = state.campanhas.find((c)=> c.id === a.itemId);
  if(campanha) return moedaCampanha(campanha);
  const filho = (state.conjuntos || []).find((c)=> c.id === a.itemId) || (state.anuncios || []).find((c)=> c.id === a.itemId);
  return moedaCampanha(campanhaDoItem(filho || {}) || {});
}
function buildFormHTML(type, data){
  const fields = FIELD_SCHEMAS[type];
  data = data || {};
  const origem = origemDoRegistro(type, data);
  let html = '';
  let i = 0;
  while(i < fields.length){
    const f = fields[i];
    if(f.group){
      // agrupa campos consecutivos do mesmo grupo em pares
      const groupFields = [];
      while(i<fields.length && fields[i].group===f.group){ groupFields.push(fields[i]); i++; }
      for(let j=0;j<groupFields.length;j+=2){
        const pair = groupFields.slice(j,j+2);
        html += `<div class="field-row">${pair.map(pf=>renderField(pf,data,origem)).join('')}</div>`;
      }
      continue;
    }
    html += renderField(f, data, origem);
    i++;
  }
  return html;
}

function renderField(f, data, origem){
  const dinheiro = f.group === 'money' || f.name === 'limiteGasto';
  let val = data[f.name] != null ? data[f.name] : '';
  if(dinheiro && val !== '' && val != null) val = paraBase(Number(val) || 0, origem || 'BRL');
  if(f.type==='select'){
    const opts = f.dynamic ? dynamicOptions(f.dynamic) : f.options.map(o=>({value:o,label:o}));
    const optionsHtml = `<option value="">Selecionar…</option>` + opts.map(o=>
      `<option value="${escapeHtml(o.value)}" ${String(o.value)===String(val)?'selected':''}>${escapeHtml(o.label)}</option>`
    ).join('');
    return `<div class="field"><label>${f.label}</label><select name="${f.name}">${optionsHtml}</select></div>`;
  }
  if(f.type==='textarea'){
    return `<div class="field"><label>${f.label}</label><textarea name="${f.name}">${escapeHtml(val)}</textarea></div>`;
  }
  const label = dinheiro ? `${f.label.replace(/\s*\([^)]*\)\s*$/, '')} (${moedaBase()})` : f.label;
  const hint = f.hint ? `<div class="cell-sub" style="margin-top:6px;">${f.hint}</div>` : '';
  const autocomplete = f.type === 'password' ? 'autocomplete="new-password"' : '';
  const shown = f.type === 'password' ? '' : val;
  return `<div class="field"><label>${label}</label><input type="${f.type}" name="${f.name}" ${f.step?`step="${f.step}"`:''} ${f.type==='number'?`min="0" inputmode="${f.step==='1'?'numeric':'decimal'}"`:''} value="${escapeHtml(shown)}" ${autocomplete} ${f.required?'required':''}>${hint}</div>`;
}

function abrirGaveta(titulo, html, comSalvar){
  document.getElementById('drawer-title').textContent = titulo;
  document.getElementById('drawer-form').innerHTML = html;
  document.getElementById('drawer-save').classList.toggle('hidden', !comSalvar);
  document.getElementById('drawer-cancel').textContent = comSalvar ? 'Cancelar' : 'Fechar';
  document.getElementById('overlay').classList.add('show');
  document.getElementById('drawer').classList.add('show');
}

function openIntegracao(){
  const i = state.integracao || {};
  editing = { type:'integracao', id:null };
  abrirGaveta('Integração com Meta Ads', `
    <div class="field"><label>Enviar alterações ao Meta Ads</label>
      <select name="ativo"><option value="sim" ${i.ativo?'selected':''}>Ligado</option><option value="nao" ${!i.ativo?'selected':''}>Desligado</option></select>
      <div class="cell-sub" style="margin-top:6px;">Ligado: pausar, ativar e mudar orçamento aqui também muda no Facebook. Se o Meta recusar, a alteração é desfeita no painel.</div></div>
    <div class="field"><label>URL do webhook (n8n)</label>
      <input type="url" name="url" placeholder="https://seu-n8n.com/webhook/torre-meta" value="${escapeHtml(i.url||'')}" autocomplete="off"></div>
    <div class="field"><label>Chave secreta</label>
      <input type="text" name="chave" placeholder="a mesma chave do fluxo no n8n" value="${escapeHtml(i.chave||'')}" autocomplete="off">
      <div class="cell-sub" style="margin-top:6px;">O token do Meta fica guardado só no n8n — nunca no navegador.</div></div>
    <div class="integ-acoes">
      <button type="button" class="btn" data-integ="testar">Testar conexão</button>
      <button type="button" class="btn" data-integ="baixar">Baixar fluxo pronto do n8n</button>
    </div>
    <div class="integ-res" id="integ-res"></div>
    <div class="integ-box">
      <h4>Como ligar (uma vez só)</h4>
      <ol>
        <li>No n8n, importe o fluxo baixado acima.</li>
        <li>No nó <b>Meta Ads API</b>, crie uma credencial <b>Header Auth</b>: nome <code>Authorization</code>, valor <code>Bearer SEU_TOKEN</code> (token de usuário do sistema do Business com permissão <code>ads_management</code>).</li>
        <li>No nó <b>Validar e montar</b>, troque <code>TROQUE-ESTA-CHAVE</code> por uma chave sua e cole a mesma chave aqui.</li>
        <li>Ative o fluxo e cole aqui a URL de <b>produção</b> do webhook.</li>
        <li>Em cada campanha, preencha o <b>ID da campanha no Meta Ads</b> (botão editar).</li>
      </ol>
    </div>`, true);
}

function openHistorico(){
  editing = null;
  const lista = state.alteracoes || [];
  const ESTADO = { ok:['pill-green','no Meta'], local:['pill-neutral','só no painel'], erro:['pill-red','falhou'], pendente:['pill-amber','enviando'] };
  const desc = a => a.acao === 'status'
    ? `Status: ${a.de || '—'} → ${a.para}`
    : `Orçamento${a.tipo ? ' ' + a.tipo.toLowerCase() : ''}: ${fmtValor(paraBase(a.de, moedaAlteracao(a)))} → ${fmtValor(paraBase(a.para, moedaAlteracao(a)))}`;
  abrirGaveta('Histórico de alterações', lista.length ? `<ul class="hist-list">${lista.map(a=>{
    const [cls, txt] = ESTADO[a.estado] || ESTADO.local;
    return `<li>
      <div class="hist-top"><span class="hist-nome">${escapeHtml(a.nome || '—')}</span><span class="pill ${cls}">${txt}</span></div>
      <div class="hist-desc">${desc(a)}</div>
      <div class="hist-meta">${dataHora(a.em)} · ${G2_SING[a.nivel] || a.nivel}${a.erro ? ' · ' + escapeHtml(a.erro) : a.motivo ? ' · ' + escapeHtml(a.motivo) : ''}</div>
    </li>`;
  }).join('')}</ul>` : `<div class="empty-state">Nenhuma alteração feita ainda.</div>`, false);
}

document.getElementById('drawer-form').addEventListener('click', async (e)=>{
  const b = e.target.closest('[data-integ]');
  if(!b) return;
  e.preventDefault();
  const f = document.getElementById('drawer-form');
  const out = document.getElementById('integ-res');
  if(b.dataset.integ === 'baixar'){
    try{
      const url = URL.createObjectURL(new Blob([JSON.stringify(N8N_FLUXO, null, 2)], { type:'application/json' }));
      const a = document.createElement('a'); a.href = url; a.download = 'n8n-torre-meta.json';
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(()=>URL.revokeObjectURL(url), 2000);
    }catch(err){ out.textContent = 'Não foi possível baixar aqui.'; }
    return;
  }
  const url = f.url.value.trim();
  if(!/^https?:\/\//.test(url)){ out.style.color = 'var(--bad)'; out.textContent = 'Informe a URL do webhook.'; return; }
  b.disabled = true; out.style.color = 'var(--label-soft)'; out.textContent = 'Testando…';
  try{
    const res = await fetch(url, { method:'POST', headers:{ 'Content-Type':'application/json' },
      body: JSON.stringify({ origem:'torre-de-controle', acao:'teste', chave: f.chave.value.trim() }) });
    let corpo = null; try{ corpo = await res.json(); }catch(err){}
    if(res.ok && corpo && corpo.ok === true){ out.style.color = 'var(--ok)'; out.textContent = 'Conexão funcionando.'; }
    else { out.style.color = 'var(--bad)'; out.textContent = `O n8n respondeu com erro: ${(corpo && corpo.erro) || 'resposta ' + res.status}.`; }
  }catch(err){
    out.style.color = 'var(--bad)';
    out.textContent = 'Não consegui falar com o webhook (confira a URL, se o fluxo está ativo e se o CORS permite este site).';
  }finally{ b.disabled = false; }
});

function openInfo(kind){
  const page = INFO_PAGES[kind];
  if(!page) return;
  editing = null;
  document.getElementById('drawer-title').textContent = page.titulo;
  document.getElementById('drawer-form').innerHTML = `<div class="info-text">${page.html}</div>`;
  document.getElementById('drawer-save').classList.add('hidden');
  document.getElementById('drawer-cancel').textContent = 'Fechar';
  document.getElementById('overlay').classList.add('show');
  document.getElementById('drawer').classList.add('show');
}

function openDrawer(type, id){
  if(type === 'lojasShopify' && !id){
    openLojaWizard();
    return;
  }
  if(type === 'bms' && !id){
    openBmWizard();
    return;
  }
  document.getElementById('drawer-save').classList.remove('hidden');
  document.getElementById('drawer-cancel').textContent = 'Cancelar';
  editing = { type, id: id || null };
  const schemaType = type === 'admins' && id ? 'adminsEdit' : type;
  const item = type === 'meta' ? (state.meta || {})
             : type === 'saude' ? (state.saude || {})
             : type === 'faturamento' ? (state.faturamento || {})
             : type === 'admins' ? (id ? getCachedAdmin(id) : null)
             : type === 'lojasShopify' ? (id ? getCachedLoja(id) : null)
             : (id ? state[type].find(x=>x.id===id) : null);
  document.getElementById('drawer-title').textContent = type === 'meta' ? 'Meta do mês'
    : type === 'saude' ? 'Metas de escala'
    : type === 'faturamento' ? 'Cálculo do líquido'
    : type === 'admins' ? (id ? 'Editar administrador' : 'Novo administrador')
    : type === 'lojasShopify' ? (id ? 'Editar loja Shopify' : 'Nova loja Shopify')
    : (id ? 'Editar ' : 'Novo(a) ') + TYPE_LABELS[type];
  document.getElementById('drawer-save').textContent = type === 'admins' && !id ? 'Enviar convite' : 'Salvar';
  document.getElementById('drawer-form').innerHTML = buildFormHTML(schemaType, item);
  document.getElementById('overlay').classList.add('show');
  document.getElementById('drawer').classList.add('show');
}

function closeDrawer(){
  if(isLojaWizardOpen()){
    closeLojaWizard();
    return;
  }
  if(isBmWizardOpen()){
    closeBmWizard();
    return;
  }
  document.getElementById('overlay').classList.remove('show');
  document.getElementById('drawer').classList.remove('show');
  editing = null;
}

document.getElementById('drawer-form').addEventListener('submit', async (e)=>{
  e.preventDefault();
  if(!editing) return;
  const { type, id } = editing;
  const formData = new FormData(e.target);
  const record = {};
  for(const [k,v] of formData.entries()) record[k] = v;
  if(type === 'admins'){
    try{
      if(id){
        await updateAdmin(id, { name: record.name, role: record.role || 'admin', status: record.status || 'ACTIVE' });
        showToast('Administrador atualizado.');
      } else {
        if(!String(record.name||'').trim() || !String(record.email||'').includes('@')){
          showToast('Informe nome e e-mail válidos.');
          return;
        }
        await inviteAdmin({ name: record.name, email: record.email, role: record.role || 'admin' });
        showToast('Convite enviado. O admin define a senha pelo e-mail.');
      }
      await refreshAdmins();
      closeDrawer();
      renderNav();
      renderContent();
    }catch(err){
      showToast(err.message || 'Não foi possível salvar o administrador.');
    }
    return;
  }
  if(type === 'lojasShopify'){
    try{
      const shop_domain = normalizeShopDomain(record.shop_domain);
      const nome = String(record.nome||shop_domain).trim();
      if(!shop_domain || !shop_domain.endsWith('.myshopify.com')){
        showToast('Informe o domínio admin da loja (minhaloja.myshopify.com).');
        return;
      }
      if(!id && !String(record.client_id||record.access_token||'').trim()){
        showToast('Informe o Client ID do app no Dev Dashboard.');
        return;
      }
      if(!id && !String(record.webhook_secret||'').trim()){
        showToast('Informe o Client secret do app no Dev Dashboard.');
        return;
      }
      const payload = {
        nome,
        shop_domain,
        status: record.status || 'ativa',
      };
      if (String(record.client_id || '').trim()) payload.client_id = String(record.client_id).trim();
      if (String(record.access_token || '').trim()) payload.access_token = String(record.access_token).trim();
      if (String(record.webhook_secret || '').trim()) payload.webhook_secret = String(record.webhook_secret).trim();
      await saveShopifyLoja(payload, id);
      showToast(id ? 'Loja salva. Importando de novo produtos e pedidos, inclusive os não pagos.' : 'Loja conectada. Teste ok — importando produtos e vendas no backend.');
      await refreshIntegracoes();
      closeDrawer();
      renderNav();
      renderContent();
    }catch(err){
      showToast(err.message || 'Não foi possível salvar a loja.');
    }
    return;
  }
  if(type === 'meta'){
    state.meta = { valorObjetivo: parseFloat(record.valorObjetivo) || 0 };
    showToast('Meta salva.');
  } else if(type === 'saude'){
    state.saude = {
      metaLojas: Math.max(1, Math.round(parseFloat(record.metaLojas) || 5)),
      metaContas: Math.max(1, Math.round(parseFloat(record.metaContas) || 8))
    };
    showToast('Metas de escala salvas.');
  } else if(type === 'integracao'){
    state.integracao = { ativo: record.ativo === 'sim', url: String(record.url||'').trim(), chave: String(record.chave||'').trim() };
    showToast(state.integracao.ativo && state.integracao.url ? 'Integração com o Meta ligada.' : 'Integração salva (desligada).');
  } else if(type === 'faturamento'){
    state.faturamento = { deducaoPct: Math.min(100, Math.max(0, parseFloat(record.deducaoPct) || 0)) };
    showToast('Cálculo do líquido atualizado.');
  } else if(id){
    const idx = state[type].findIndex(x=>x.id===id);
    gravarDinheiro(type, record, state[type][idx]);
    state[type][idx] = { ...state[type][idx], ...record };
    if(type === 'campanhas') marcarAtualizado(state[type][idx]);
    showToast('Alterações salvas.');
  } else {
    record.id = uid();
    gravarDinheiro(type, record, record);
    if(type === 'campanhas') marcarAtualizado(record);
    state[type].push(record);
    showToast('Item adicionado.');
  }
  await saveState();
  closeDrawer();
  renderNav();
  renderContent();
  renderMetaMini();
});

document.getElementById('drawer-close').addEventListener('click', closeDrawer);
document.getElementById('drawer-cancel').addEventListener('click', closeDrawer);
document.getElementById('overlay').addEventListener('click', closeDrawer);

document.getElementById('btn-add').addEventListener('click', ()=>{
  const typeMap = { lojas:'lojasShopify', bms:'bms', contas:'contas', campanhas:'campanhas', rejeitados:'rejeitados', admin:'admins' };
  const type = typeMap[currentSection];
  if(!type){ return; }
  openDrawer(type, null);
});

/* Delegação de eventos para ações em tabelas e filtros */
document.getElementById('content').addEventListener('click', async (e)=>{
  const pageBtn = e.target.closest('[data-pager]');
  if(pageBtn){
    if(pageBtn.getAttribute('aria-disabled') === 'true') return;
    if(pageBtn.tagName === 'A' && !isPlainClick(e)) return;
    if(pageBtn.tagName === 'A') e.preventDefault();
    const key = pageBtn.dataset.pager;
    const n = Number(pageBtn.dataset.page);
    if(key && n > 0 && Object.prototype.hasOwnProperty.call(listPage, key)){
      listPage[key] = n;
      renderContent();
      syncUrl();
    }
    return;
  }
  const btn = e.target.closest('[data-action]');
  if(!btn) return;
  const { action, type, id } = btn.dataset;
  if(action==='loja-wizard'){
    openLojaWizard();
    return;
  }
  if(action==='atualizar-app-shopify'){
    const shop = btn.dataset.shop || '';
    btn.disabled = true;
    try {
      const data = await shopifyInstallStart(shop, { atualizar: true });
      if (!data?.installUrl) throw new Error('Não foi possível abrir a atualização.');
      const aba = window.open(data.installUrl, '_blank');
      if (!aba) window.location.assign(data.installUrl);
      else btn.disabled = false;
    } catch (err) {
      btn.disabled = false;
      showToast(err.message || 'Não foi possível atualizar o app na Shopify.');
    }
    return;
  }
  if(action==='bm-wizard'){
    openBmWizard();
    return;
  }
  if(action==='cancel-import'){ pendingImport = null; renderContent(); return; }
  if(action==='fat-view'){
    faturamentoView = btn.dataset.view === 'bruto' ? 'bruto' : 'liquido';
    renderContent();
    return;
  }
  if(action==='toggle-eye'){
    ocultarValores = !ocultarValores;
    try{ localStorage.setItem('torre:ocultar', ocultarValores ? '1' : '0'); }catch(err){}
    renderContent();
    renderMetaMini();
    return;
  }
  if(action==='periodo-cal'){ abrirPopPeriodo(document.getElementById('periodo-pop').hidden); return; }
  if(action==='pp-cancel'){ fecharPopPeriodo(true); return; }
  if(action==='pp-quick'){ periodo = { tipo: btn.dataset.v }; salvarPeriodo(); renderContent(); return; }
  if(action==='pp-apply'){
    let de = document.getElementById('pp-de').value, ate = document.getElementById('pp-ate').value;
    if(!de && !ate){ showToast('Escolha as datas do período.'); return; }
    de = de || ate; ate = ate || de;
    const hoje = localDateKey();
    if(de > ate) [de, ate] = [ate, de];
    if(ate > hoje) ate = hoje;
    if(de > hoje) de = hoje;
    if(de === ate && ate === hoje) periodo = { tipo:'hoje' };
    else if(de === ate && ate === addDays(hoje,-1)) periodo = { tipo:'ontem' };
    else periodo = { tipo:'custom', de, ate };
    salvarPeriodo();
    renderContent();
    return;
  }
  if(action==='send-password'){
    if(!confirm('Enviar e-mail para este administrador gerar ou redefinir a senha?')) return;
    sendAdminPassword(id).then(()=>{
      showToast('E-mail de senha enviado.');
    }).catch((err)=>{
      showToast(err.message || 'Não foi possível enviar o e-mail.');
    });
    return;
  }
  if(action==='sync'){
    if(type === 'lojasShopify'){
      const loja = getCachedLoja(id);
      if(isLojaSyncBusy(loja)){
        showToast('Esta loja já está sincronizando.');
        return;
      }
      markLojaSyncPending(id);
      renderContent();
      syncShopifyLoja(id).then(async ()=>{
        invalidateVendas();
        invalidateProdutos();
        invalidateFunil();
        await refreshIntegracoes();
        renderNav();
        renderContent();
        showToast('Sincronização iniciada no backend.');
      }).catch((err)=>{
        clearLojaSyncPending(id);
        renderContent();
        showToast(err.message || 'Não foi possível sincronizar.');
      });
      return;
    }
    if(type === 'bms'){
      const bm = state.bms.find((item) => String(item.id) === String(id));
      if(isBmSyncBusy(bm)){
        showToast('Esta BM já está sincronizando.');
        return;
      }
      markBmSyncPending(id);
      renderContent();
      syncFacebookBm(id).then(async ()=>{
        clearBmSyncPending(id);
        await loadState();
        renderNav();
        renderContent();
        renderMetaMini();
        showToast('BM sincronizada. Contas de anúncio atualizadas.');
      }).catch((err)=>{
        clearBmSyncPending(id);
        renderContent();
        showToast(err.message || 'Não foi possível sincronizar a BM.');
      });
    }
    return;
  }
  if(action==='edit') openDrawer(type, id);
  if(action==='delete'){
    const mensagem = type === 'lojasShopify'
      ? 'Remover esta loja Shopify? Produtos e vendas desta loja também serão apagados.'
      : type === 'admins'
        ? 'Remover este administrador?'
        : type === 'bms'
        ? 'Remover esta BM? Contas, campanhas e anúncios ligados a ela também serão apagados.'
        : 'Excluir este registro?';
    confirmarApagar(mensagem).then((ok)=>{
      if(!ok) return;
      if(type === 'lojasShopify'){
        deleteShopifyLoja(id).then(async ()=>{
          await refreshIntegracoes();
          renderNav();
          renderContent();
          showToast('Loja removida.');
        }).catch((err)=>{
          showToast(err.message || 'Não foi possível remover.');
        });
        return;
      }
      if(type === 'admins'){
        deleteAdmin(id).then(async ()=>{
          await refreshAdmins();
          renderNav();
          renderContent();
          showToast('Administrador removido.');
        }).catch((err)=>{
          showToast(err.message || 'Não foi possível remover.');
        });
        return;
      }
      if(type === 'bms') removerBm(id);
      else state[type] = state[type].filter(x=>x.id!==id);
      saveState();
      renderNav();
      renderContent();
      renderMetaMini();
      showToast('Registro excluído.');
    });
    return;
  }
});
document.getElementById('content').addEventListener('change', (e)=>{
  if(e.target.matches('.filter')){
    if(e.target.id === 'contas-filter'){ listFilter.contas = e.target.value || 'todas'; listPage.contas = 1; }
    if(e.target.id === 'rejeitados-filter'){ listFilter.rejeitados = e.target.value || 'todas'; listPage.rejeitados = 1; }
    if(e.target.id === 'vendas-loja'){ setVendasFilter('loja', e.target.value); listPage.vendas = 1; }
    if(e.target.id === 'vendas-status'){ setVendasFilter('status', e.target.value); listPage.vendas = 1; }
    if(e.target.id === 'produtos-loja'){ setProdutosFilter('loja', e.target.value); listPage.produtos = 1; }
    if(e.target.id === 'produtos-status'){ setProdutosFilter('status', e.target.value); listPage.produtos = 1; }
    if(e.target.id === 'funil-loja'){ setFunilFilter('loja', e.target.value); }
    if(e.target.id === 'funil-periodo'){ setFunilFilter('periodo', { tipo: e.target.value }); }
    if(e.target.id === 'funil-de'){ setFunilFilter('de', e.target.value); }
    if(e.target.id === 'funil-ate'){ setFunilFilter('ate', e.target.value); }
    renderContent();
    if(e.target.id === 'contas-filter' || e.target.id === 'rejeitados-filter') syncUrl({ replace: true });
  }
  if(e.target.id === 'periodo-select'){
    if(e.target.value === 'custom'){ abrirPopPeriodo(true); }
    else { periodo = { tipo: e.target.value }; salvarPeriodo(); renderContent(); }
  }
  if(e.target.matches('input[type="file"]') && e.target.dataset.importType){
    const file = e.target.files[0];
    if(file) handleFileSelect(e.target.dataset.importType, file);
  }
});

document.getElementById('content').addEventListener('submit', (e)=>{
  if(e.target.id === 'import-mapping-form'){
    e.preventDefault();
    processImport(e.target);
  }
});

/* aba Campanhas */
document.getElementById('content').addEventListener('click', (e)=>{ if(currentSection === 'campanhas') g2Click(e); });
document.getElementById('content').addEventListener('input', (e)=>{ if(currentSection === 'campanhas') g2Input(e); });
document.getElementById('content').addEventListener('change', (e)=>{ if(currentSection === 'campanhas') g2Change(e); });
document.getElementById('content').addEventListener('keydown', (e)=>{ if(currentSection === 'campanhas') g2Keydown(e); });
document.addEventListener('click', (e)=>{
  if(!e.target.closest('#g2-menu') && !e.target.closest('[data-g2="cols"],[data-g2="mais"],[data-g2="orcamento"],[data-g2="sug-aplicar"]')) g2FecharMenu();
});
setInterval(()=>{
  const el = document.getElementById('g2-upd');
  if(el) el.textContent = 'Atualizado ' + tempoRelativo(state.sincronizadoEm);
}, 30000);

/* popover do período personalizado */
function abrirPopPeriodo(abrir){
  const pop = document.getElementById('periodo-pop');
  if(!pop) return;
  pop.hidden = !abrir;
  document.querySelector('[data-action="periodo-cal"]')?.setAttribute('aria-expanded', abrir ? 'true' : 'false');
  if(abrir) document.getElementById('pp-de')?.focus({ preventScroll:true });
}
function fecharPopPeriodo(restaurar){
  const pop = document.getElementById('periodo-pop');
  if(!pop || pop.hidden) return;
  abrirPopPeriodo(false);
  // se o select ficou em "Personalizado" sem aplicar, volta pro período atual
  const sel = document.getElementById('periodo-select');
  if(restaurar !== false && sel && sel.value !== periodo.tipo) sel.value = periodo.tipo;
}
document.addEventListener('click', (e)=>{
  if(!e.target.closest('#period-box')) fecharPopPeriodo(true);
});
document.addEventListener('keydown', (e)=>{ if(e.key === 'Escape') fecharPopPeriodo(true); });

const contentEl = document.getElementById('content');
function hoverGraficos(e, toque){
  if(e.target.closest('#fat-chart')) onFatChartMove(e); else if(toque || e.pointerType === 'mouse') onFatChartLeave();
  if(e.target.closest('#evo-chart')) onEvoMove(e); else if(toque || e.pointerType === 'mouse') onEvoLeave();
  if(e.target.closest('.kpi-viz[data-spark]')) onSparkMove(e); else if(toque || e.pointerType === 'mouse') onSparkLeave();
}
function esconderHover(){ onFatChartLeave(); onEvoLeave(); onSparkLeave(); }
contentEl.addEventListener('pointermove', (e)=> hoverGraficos(e, false), { passive:true });
// no celular: tocar num gráfico mostra o valor; tocar fora esconde
document.addEventListener('pointerdown', (e)=> hoverGraficos(e, true), { passive:true });
contentEl.addEventListener('pointerleave', (e)=>{ if(e.pointerType === 'mouse') esconderHover(); });
window.addEventListener('scroll', esconderHover, { passive:true });
contentEl.addEventListener('keydown', (e)=>{
  const card = e.target.closest('.health-card');
  if(card && (e.key === 'Enter' || e.key === ' ')){ e.preventDefault(); openDrawer('saude', null); }
});
// No celular a barra do navegador some/aparece e dispara "resize" sem mudar a
// largura — só redesenha os gráficos quando a largura realmente muda.
let resizeTimer = null, lastWidth = window.innerWidth;
window.addEventListener('resize', ()=>{
  if(window.innerWidth === lastWidth) return;
  lastWidth = window.innerWidth;
  clearTimeout(resizeTimer);
  resizeTimer = setTimeout(()=>{ if(currentSection === 'overview'){ drawFatChart(); drawEvoChart(); } }, 150);
}, { passive:true });
// Mantém "Hoje" atualizado (troca de hora / meia-noite), só com a aba visível
setInterval(()=>{ if(currentSection === 'overview' && !editing && !document.hidden) renderContent(); }, 5*60*1000);
document.addEventListener('visibilitychange', ()=>{
  if(!document.hidden && currentSection === 'overview' && !editing) renderContent();
});

function onNavClick(e){
  if(!isPlainClick(e)) return;
  const el = e.target && e.target.nodeType === 1 ? e.target : e.target && e.target.parentElement;
  const path = typeof e.composedPath === 'function' ? e.composedPath() : [];
  const btn = (el && typeof el.closest === 'function' && el.closest('[data-nav]'))
    || path.find((n)=> n && n.getAttribute && n.getAttribute('data-nav'));
  if(!btn) return;
  e.preventDefault();
  e.stopPropagation();
  goSection(btn.getAttribute('data-nav'));
}

document.getElementById('nav').addEventListener('click', onNavClick);
document.querySelector('.sidebar')?.addEventListener('click', onNavClick);

/* =========================================================
   INICIALIZAÇÃO
   ========================================================= */
document.querySelector('.topbar')?.addEventListener('click', (e)=>{
  const btn = e.target.closest('[data-moeda]');
  if(!btn) return;
  trocarMoeda(btn.dataset.moeda);
});
document.getElementById('meta-mini').addEventListener('click', (e)=>{
  const btn = e.target.closest('[data-action="edit"]');
  if(btn) openDrawer('meta', null);
});

/* ---------- API do funil (pra integrações: n8n, API do Meta, etc.) ----------
   TorreAPI.importarFunil([
     { campanha:'Nome da campanha', cliques:312, visualizacoes:211, checkouts:120, compras:21, loja:'minhaloja.myshopify.com' }
   ], { somar:false })
   - casa pelo nome da campanha (igual à importação por planilha); cria a campanha se não existir
   - somar:true soma aos números atuais em vez de substituir */
window.TorreAPI = {
  async importarFunil(linhas, opcoes){
    const somar = !!(opcoes && opcoes.somar);
    const num = v => Math.max(0, Math.round(Number(String(v ?? 0).replace(/[^\d.-]/g,'')) || 0));
    let atualizadas = 0, criadas = 0;
    (Array.isArray(linhas) ? linhas : []).forEach(l=>{
      const nome = String(l.campanha || l.nome || '').trim();
      if(!nome) return;
      const alvo = nome.toLowerCase();
      let c = state.campanhas.find(x=>(x.nome||'').trim().toLowerCase() === alvo);
      if(!c){
        c = { id: uid(), nome, contaId:'', cliente:'', loja:'', objetivo:'', status:'ativa', dataInicio:'', orcamento:0, gasto:0, receita:0, impressoes:0, cliques:0, visualizacoes:0, checkouts:0, conversoes:0 };
        state.campanhas.push(c); criadas++;
      } else atualizadas++;
      if(l.loja && !c.loja) c.loja = String(l.loja).trim();
      const campos = { cliques: l.cliques, visualizacoes: l.visualizacoes, checkouts: l.checkouts, conversoes: l.compras ?? l.conversoes };
      for(const k in campos){
        if(campos[k] == null) continue;
        c[k] = somar ? (Number(c[k])||0) + num(campos[k]) : num(campos[k]);
      }
      marcarAtualizado(c);
    });
    await saveState();
    renderNav(); renderContent(); renderMetaMini();
    return { atualizadas, criadas };
  },
  /* TorreAPI.importarNivel('conjuntos' | 'anuncios', [
       { nome:'Conjunto 01', campanha:'Nome da campanha', status:'ativa', orcamento:100, orcamentoTipo:'Diário',
         gasto:120.5, faturamento:340, vendas:3, cliques:210, impressoes:9800, checkouts:8, visualizacoes:150 }
     ]) — casa pelo nome dentro da mesma campanha; cria se não existir */
  async importarNivel(nivel, linhas){
    if(!['conjuntos','anuncios'].includes(nivel)) throw new Error('nivel deve ser "conjuntos" ou "anuncios"');
    if(!Array.isArray(state[nivel])) state[nivel] = [];
    const dec = v => Math.max(0, Number(String(v ?? 0).replace(/[^\d.-]/g,'')) || 0);
    let atualizados = 0, criados = 0;
    (Array.isArray(linhas) ? linhas : []).forEach(l=>{
      const nome = String(l.nome || '').trim();
      if(!nome) return;
      const camp = state.campanhas.find(c=>(c.nome||'').trim().toLowerCase() === String(l.campanha||'').trim().toLowerCase());
      let it = state[nivel].find(x=>(x.nome||'').trim().toLowerCase() === nome.toLowerCase() && (x.campanhaId||'') === (camp ? camp.id : (x.campanhaId||'')));
      if(!it){ it = { id: uid(), nome, status:'ativa' }; state[nivel].push(it); criados++; } else atualizados++;
      if(camp) it.campanhaId = camp.id; else if(l.campanha) it.campanha = String(l.campanha);
      if(l.status) it.status = String(l.status).toLowerCase().startsWith('pau') ? 'pausada' : 'ativa';
      if(l.orcamento != null) it.orcamento = dec(l.orcamento);
      if(l.orcamentoTipo) it.orcamentoTipo = l.orcamentoTipo;
      const campos = { gasto: l.gasto, receita: l.faturamento ?? l.receita, conversoes: l.vendas ?? l.compras, cliques: l.cliques,
                       impressoes: l.impressoes, checkouts: l.checkouts, visualizacoes: l.visualizacoes };
      for(const k in campos){ if(campos[k] != null) it[k] = dec(campos[k]); }
      marcarAtualizado(it);
    });
    await saveState();
    renderNav(); renderContent(); renderMetaMini();
    return { atualizados, criados };
  },
  lerFunil(){
    const d = lerFunil();
    return Object.fromEntries(d.etapas.map((e) => [e.key, e.valor]));
  }
};

document.getElementById('ano-atual').textContent = new Date().getFullYear();

bindLojaWizard();

window.addEventListener('torre:shopify-connected', async () => {
  invalidateVendas();
  invalidateProdutos();
  invalidateFunil();
  await refreshIntegracoes();
  if (appReady) renderContent();
});

bindBmWizard({
  onSaved: async () => {
    await loadState();
    renderNav();
    renderContent();
    renderMetaMini();
  },
});

bindConfirm();
bindTracker(() => {
  if (currentSection !== 'tracker') return;
  renderContent();
  syncUrl();
});
bindNotificacoes(() => { if (currentSection === 'notificacoes') renderContent(); });

bindTheme({
  onInfo: (kind) => openInfo(kind),
  onEscape: () => {
    if (isConfirmOpen()) { cancelarConfirmacao(); return; }
    if (isLojaWizardOpen() || isBmWizardOpen() || document.getElementById('drawer').classList.contains('show')) closeDrawer();
  }
});

document.addEventListener('click', (e) => {
  if (e.target.closest('[data-logout]')) {
    e.preventDefault();
    logout();
  }
});

bindAppMode((id) => {
  if (id === 'perfil') {
    appPerfil = true;
    renderContent();
    return;
  }
  appPerfil = false;
  goSection(id);
});

bindLogin({
  onSuccess: async () => {
    showBootScreen('Carregando dados', 'Preparando o painel');
    const next = consumeLoginNext();
    history.replaceState({}, '', next || buildHref({ section: 'overview' }));
    await prepararApp();
    await handleRoute();
    showToast('Bem-vindo à torre.');
  }
});

export async function initApp(){
  window.addEventListener('popstate', () => { handleRoute(); });
  await handleRoute();
}
